/*
 * Returns & Exchanges portal.
 * Host: sections/returns-portal.liquid — read the {% doc %} header there first.
 *
 * Classic IIFE, no modules, matching find-your-fit.js / size-finder.js / bundle-builder.js.
 *
 * ---------------------------------------------------------------------------------------------
 * THE TWO PHASE-2 SEAMS
 *
 *   identifyOrder()  — turns whatever the shopper gave us into `state.order`.
 *   submitRequest()  — sends the finished request.
 *
 * Today those are "trust what they typed" and "populate a contact form". When an app with an
 * app proxy exists, they become a real Admin API order lookup and a real returnCreate. Keep them
 * thin; nothing else should need to change.
 * ---------------------------------------------------------------------------------------------
 *
 * THE SIZE RULES (the part worth getting right)
 *
 * 1. A size letter is only comparable WITHIN a build. Poseidon M and Apollo M are different
 *    bodies. Never compare across builds by letter alone.
 * 2. Builds do not share a ladder. Poseidon/Apollo tops run S-XL; Hercules/Zeus run L-XXL and
 *    have no S or M at all.
 * 3. So "one size up" walks the shopper's OWN product's `sizeValues` array, never a global
 *    ladder. That single choice makes Hercules/Zeus starting at L, and waist-sized bottoms
 *    (29"-30" → 31" → 32"), work with no special cases.
 * 4. BUILD_ORDER runs leanest to broadest and is `Poseidon, Apollo, Zeus, Hercules`. Zeus sits
 *    BEFORE Hercules: the client's matrix sends "too small" from Zeus to Hercules for more room
 *    through the midsection, and "too big" from Hercules to Zeus for more definition. An earlier
 *    build had these two the other way round, which pointed every cross-build recommendation
 *    involving them in the wrong direction.
 * 5. Dead ends are real in today's catalogue, not hypothetical. Hercules XXL and Zeus XXL are the
 *    top of the entire range, and the matrix gives neither of them a cross-build answer in either
 *    direction. `build_step_up_map` records them with an EMPTY right-hand side, which means
 *    "nothing else exists at this build and size, whichever way the shopper is going" — we then
 *    show the no-recommendations panel rather than an empty box.
 *    Poseidon XL and Apollo XL are NOT dead ends and must not be listed there: the matrix answers
 *    them with Apollo XL and Zeus XL respectively, which the adjacent-build rule derives on its
 *    own. Listing them mapped to Hercules L, as an earlier build did, produced a card labelled
 *    "one size up" pointing at a different build entirely.
 * 6. Linen Shirts run one letter LARGER than tees/polos for the same body, so a card that
 *    crosses that boundary shifts one rung. This mirrors snippets/cart-upsell.liquid and
 *    snippets/linen-size-nudge.liquid — if the brand fact changes, change all three.
 * 7. Availability is a hard filter everywhere. Recommending an out-of-stock size is worse than
 *    recommending nothing.
 *
 * `matchFitCards` is a port of the selection rule in snippets/cart-upsell.liquid.
 * >>> If you change the rule there, change it here too. <<<
 */

(function () {
  'use strict';

  var root = document.querySelector('[data-returns-portal]');
  if (!root) return;

  /* The section-settings heading, captured before the first step overwrites the masthead with its
     own title. Read once in paintMasthead(); null means "not captured yet". */
  var MASTHEAD_DEFAULT_TITLE = null;

  var Core = window.OrserioReturnsCore;
  if (!Core) {
    console.error('[rp] interaction core did not load');
    return;
  }

  var configEl = root.querySelector('[data-rp-config]');
  if (!configEl) return;

  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error('[rp] config did not parse', err);
    return;
  }

  /*
   * LIVE mode means a Returns API URL is configured: we look up the shopper's real order from
   * their email + postcode and create a real Return in Shopify. Without it the portal still runs
   * end to end but only emails the team, so the page never breaks because the API is down or not
   * deployed yet.
   */
  var API_URL = String(CONFIG.apiUrl || '').trim().replace(/\/+$/, '');
  var LIVE = !!API_URL && root.dataset.rpLive === 'true';
  var SHOW_ALL = root.hasAttribute('data-rp-show-all');
  var STORAGE_KEY = 'orserio-returns-v1';
  var STATE_VERSION = 1;

  var KNOWN_MODELS = splitList(CONFIG.knownModels);
  var SIZE_LADDER = splitList(CONFIG.sizeLadder).map(upper);
  var BUILD_ORDER = splitList(CONFIG.buildOrder);
  var STEP_UP_MAP = parseRuleMap(CONFIG.stepUpMap);
  var SIBLING_MAP = parseRuleMap(CONFIG.siblingMap);
  var BUILD_DESCRIPTIONS = parseRuleMap(CONFIG.buildDescriptions);

  /*
   * THE CLIENT'S SIZING MATRIX, AS AN EXPLICIT TABLE (2026-08-27).
   *
   * The authoritative spreadsheet turned out NOT to be derivable from `build_order` plus the two
   * rule maps. It is hand-curated and deliberately irregular: on the too-big path the leaner build
   * from Hercules L is Apollo XL, from Hercules XL it is Apollo XL again, but from Hercules XXL it
   * is Zeus XXL. No ordering of builds produces that, and tuning the derivation to chase it just
   * moves the mismatches around — measured, the derivation agreed with only 21 of 28 T-shirt
   * origins.
   *
   * So the table wins where it speaks, and the existing derivation stays as the fallback for
   * anything it does not list. That keeps every product the sheet has not covered behaving exactly
   * as before, which is what makes this safe to ship incrementally.
   *
   * It lives in a merchant SETTING rather than in this file, which is the same place the older
   * `build_step_up_map` / `fit_sibling_map` rules already live. The 2026-08-13 note that the
   * client "did not want it pasted into the theme as data" was about hardcoding sizing into source
   * a merchant cannot reach; a settings textarea is the opposite of that, and the client has since
   * asked for exactly this ("the more civilized way is we still have a table").
   *
   * Line format, one per origin:
   *
   *     <Product Type>: <Build> <Size> > <Build> <Size> | <Build> <Size> | ...
   *
   * Recommendations are in the client's own rank order. An EMPTY right-hand side is meaningful and
   * is not the same as an absent line: it is the sheet's "No larger option" / "No smaller option",
   * i.e. a genuine dead end, and it suppresses the fallback rather than inviting it.
   */
  function parseSizingMatrix(raw) {
    var map = {};

    String(raw || '').split('\n').forEach(function (line) {
      var trimmed = line.trim();
      if (!trimmed || trimmed.charAt(0) === '#') return;

      var colon = trimmed.indexOf(':');
      var arrow = trimmed.indexOf('>');
      if (colon === -1 || arrow === -1 || arrow < colon) return;

      var type = trimmed.slice(0, colon).trim();
      var origin = trimmed.slice(colon + 1, arrow).trim();
      var targets = trimmed.slice(arrow + 1).split('|')
        .map(function (s) { return s.trim(); })
        .filter(Boolean)
        .map(parseMatrixTarget)
        .filter(Boolean);

      if (!type || !origin) return;
      map[matrixKey(type, origin)] = targets;
    });

    return map;
  }

  /*
   * "Poseidon M" -> { build: 'Poseidon', token: 'M' }. The build is the first word and the size is
   * the rest, because a size can carry spaces or punctuation while a build never does.
   */
  function parseMatrixTarget(text) {
    var parts = String(text).trim().split(/\s+/);
    if (parts.length < 2) return null;
    return { build: parts[0], token: upper(parts.slice(1).join(' ')) };
  }

  /*
   * Normalised so the sheet's own spelling does not have to match the catalogue's exactly: the
   * sheet writes "T-Shirt" and "Cotton Trousers" where `product.type` is "T-shirt" and "Cotton
   * Trouser". Case and a trailing plural are the only differences, and both are absorbed here
   * rather than by hand-editing 208 rows into store spelling.
   */
  function normaliseType(type) {
    return String(type || '').trim().toLowerCase().replace(/s$/, '');
  }

  function matrixKey(type, origin) {
    return normaliseType(type) + '|' + upper(String(origin).replace(/\s+/g, ' ').trim());
  }

  var MATRIX = {
    smaller: parseSizingMatrix(CONFIG.matrixSmaller),
    larger: parseSizingMatrix(CONFIG.matrixLarger)
  };

  /*
   * The table's answer for one origin, or null when it does not list it at all. Null means "fall
   * back to the derivation"; an empty ARRAY means "the sheet says there is nothing here".
   *
   * Bottoms are matched leniently on the build because the sheet treats Poseidon and Apollo as one
   * combined build for trousers and shorts ("Poseidon / Apollo 29-30"), which the catalogue does
   * not — those products carry both tags.
   */
  function matrixFor(item, direction) {
    var table = MATRIX[direction === 'larger' ? 'larger' : 'smaller'];
    if (!table) return null;

    var product = resolveProduct(item);
    var type = product ? product.type : item.type;
    var size = upper(item.sizeToken || '');

    var exact = table[matrixKey(type, item.build + ' ' + size)];
    if (exact) return exact;

    for (var key in table) {
      if (!Object.prototype.hasOwnProperty.call(table, key)) continue;
      var parts = key.split('|');
      if (parts[0] !== normaliseType(type)) continue;

      var origin = parts[1];
      var slash = origin.indexOf('/');
      if (slash === -1) continue;

      // "POSEIDON / APOLLO 29-30": every word before the size is an accepted build.
      var originParts = origin.split(/\s+/);
      var originSize = originParts[originParts.length - 1];
      if (originSize !== size) continue;

      var builds = originParts.slice(0, -1).join(' ').split('/')
        .map(function (b) { return upper(b.trim()); })
        .filter(Boolean);
      if (builds.indexOf(upper(item.build)) !== -1) return table[key];
    }

    return null;
  }

  /* ------------------------------------------------------------------ small helpers */

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function upper(s) { return String(s || '').toUpperCase(); }

  /*
   * Size values carry descriptive suffixes: `L (6'1 & Under, Ready For Dispatch)`, and the waist
   * ones contain both quote characters, e.g. `29"-30" (In Stock...)`. Splitting on
   * whitespace-or-open-paren gets the leading token right in every case. Same rule as
   * `ov.value | split: ' ' | first` in Liquid and the regex in linen-size-nudge-assets.liquid.
   */
  function sizeToken(value) {
    return String(value || '').trim().split(/[\s(]/)[0].toUpperCase();
  }

  /*
   * Merchant rule maps are authored one per line as `Left > Right`, parsed like
   * parseLegacySwatchConfig in assets/size-finder.js. An EMPTY right-hand side is meaningful:
   * it declares "nothing exists past here", which is different from "no rule was written".
   */
  function parseRuleMap(raw) {
    var map = {};
    String(raw || '')
      .split('\n')
      .forEach(function (line) {
        var trimmed = line.trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var parts = trimmed.split('>');
        if (parts.length < 2) return;
        var key = parts[0].trim().toUpperCase();
        var value = parts.slice(1).join('>').trim();
        if (!key) return;
        map[key] = value;
      });
    return map;
  }

  function buildOf(tags) {
    var list = Array.isArray(tags) ? tags : splitList(tags);
    for (var i = 0; i < KNOWN_MODELS.length; i++) {
      if (list.indexOf(KNOWN_MODELS[i]) !== -1) return KNOWN_MODELS[i];
    }
    return '';
  }

  /* Bottoms carry several build tags (one product serves Poseidon and Apollo), so a card may
     need every build a product belongs to, not just the first. */
  function buildsOf(tags) {
    var list = Array.isArray(tags) ? tags : splitList(tags);
    return KNOWN_MODELS.filter(function (m) { return list.indexOf(m) !== -1; });
  }

  function isLinenShirt(type) { return String(type || '') === 'Linen Shirt'; }

  /*
   * `currency` is optional and defaults to the currency the shopper is browsing in, which is
   * right for anything they are about to BUY (replacements, Instant Dispatch extras — those go
   * through the real cart at checkout).
   *
   * It must be passed explicitly for anything they ALREADY PAID FOR. An order placed in USD is
   * still returnable while the shopper browses in GBP, and printing that order's dollars with a
   * pound sign — or worse, converting them — tells someone they are getting back less than they
   * spent (client, Kyle, 2026-09-02). The API sends each returnable line's own currency; see
   * lineToItem().
   */
  function money(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || CONFIG.currencyCode || 'GBP'
      }).format(amount);
    } catch (err) {
      return amount.toFixed(2);
    }
  }

  function spellSize(token) {
    switch (upper(token)) {
      case 'XS': return 'Extra Small';
      case 'S': return 'Small';
      case 'M': return 'Medium';
      case 'L': return 'Large';
      case 'XL': return 'Extra Large';
      case 'XXL': return 'XXL';
      default: return token;
    }
  }

  function fillTemplate(text, vars) {
    return String(text || '').replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
    });
  }

  function stripTags(html) {
    var div = document.createElement('div');
    div.innerHTML = String(html || '');
    return (div.textContent || '').trim();
  }

  /* ------------------------------------------------------------------ catalogue index */

  var PRODUCTS = Array.isArray(CONFIG.products) ? CONFIG.products : [];

  PRODUCTS.forEach(function (product) {
    product._builds = buildsOf(product.tags);
    product._build = product._builds[0] || '';
    product._sizeTokens = (product.sizeValues || []).map(sizeToken);
    product._isLinen = isLinenShirt(product.type);
  });

  function productByHandle(handle) {
    for (var i = 0; i < PRODUCTS.length; i++) {
      if (PRODUCTS[i].handle === handle) return PRODUCTS[i];
    }
    return null;
  }

  function productById(id) {
    for (var i = 0; i < PRODUCTS.length; i++) {
      if (String(PRODUCTS[i].id) === String(id)) return PRODUCTS[i];
    }
    return null;
  }

  /*
   * An item bought long ago may sit outside the merchant's configured catalogue collections, so
   * handle lookup can miss. Falling back to build + type keeps the engine working for it.
   */
  function resolveProduct(item) {
    if (item.productHandle) {
      var byHandle = productByHandle(item.productHandle);
      if (byHandle) return byHandle;
    }
    for (var i = 0; i < PRODUCTS.length; i++) {
      var p = PRODUCTS[i];
      if (p.type === item.type && p._builds.indexOf(item.build) !== -1) return p;
    }
    return null;
  }

  function variantSizeToken(product, variant) {
    return sizeToken((variant.options || [])[product.sizeIndex]);
  }

  function variantColour(product, variant) {
    if (product.colorIndex == null || product.colorIndex < 0) return '';
    return String((variant.options || [])[product.colorIndex] || '').trim();
  }

  /* Prefer the shopper's own colour at the target size; fall back to the first available one —
     the same fallback rule cart-upsell.liquid uses. */
  function findVariant(product, targetToken, preferredColour) {
    if (!product) return null;
    var fallback = null;
    var wanted = String(preferredColour || '').toLowerCase();

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (!v.available) continue;
      if (variantSizeToken(product, v) !== targetToken) continue;
      if (!fallback) fallback = v;
      if (wanted && variantColour(product, v).toLowerCase() === wanted) return v;
    }
    return fallback;
  }

  function productsForBuildAndType(build, type) {
    return PRODUCTS.filter(function (p) {
      return p.type === type && p._builds.indexOf(build) !== -1;
    });
  }

  /* ------------------------------------------------------------------ the fit engine */

  /*
   * Card A / B: step along the shopper's OWN product's size values. See rule 3 in the header —
   * this is what makes every build and both sizing systems work without branching.
   * Returns null when the product's ladder runs out, which the caller resolves via the
   * merchant's step-up map.
   */
  function stepWithinProduct(product, currentToken, steps) {
    if (!product) return null;
    var idx = product._sizeTokens.indexOf(upper(currentToken));
    if (idx === -1) return null;
    var targetIdx = idx + steps;
    if (targetIdx < 0 || targetIdx >= product._sizeTokens.length) return null;
    return product._sizeTokens[targetIdx];
  }

  /*
   * The ladder ran out. `Poseidon XL > Hercules L` means "there is nothing bigger in this build,
   * try that one instead". A rule present with an EMPTY right side means the catalogue genuinely
   * stops here — return the marker so the caller shows the no-recommendations panel rather than
   * silently trying something else.
   */
  function applyStepUpMap(build, currentToken) {
    var key = (build + ' ' + currentToken).toUpperCase();
    if (!Object.prototype.hasOwnProperty.call(STEP_UP_MAP, key)) return null;
    var target = STEP_UP_MAP[key];
    if (!target) return { exhausted: true };
    var parts = target.split(/\s+/);
    if (parts.length < 2) return null;
    return { build: parts[0], token: upper(parts[1]) };
  }

  /*
   * The shopper's own build has run out of sizes in the direction they need.
   *
   * The client's rule (meeting, section H) is NOT "jump to the same letter in the next build":
   *
   *   "step 2: if no smaller size exists in that fit, drop a fit and step up a size. Worked
   *    example: Zeus L too big goes to Apollo XL, then Apollo L."
   *
   * Changing build is itself worth roughly a size, so the leaner build at the SAME letter is a
   * double step and lands too far down. Moving the letter one rung the other way cancels half of
   * it, which is why Apollo XL is the first answer and Apollo L the second. The row below that
   * would have been Apollo M, and the client rejected Zeus L to Apollo M by name as "too
   * substantial" — that judgement is the calibration rule for every edge case here.
   *
   * Mirrored for "too small": the next build UP at one letter DOWN. That mirror is not a guess.
   * Inverting the client's own example gives it exactly: if Zeus L too big goes to Apollo XL, then
   * Apollo XL too small goes to Zeus L.
   *
   * Returns null for waist sizes (no position on the alpha ladder) and at the two ends of
   * BUILD_ORDER, which is what makes Poseidon S "too big" a genuine dead end rather than a
   * silently empty row.
   */
  function compensatedBuildStep(build, currentToken, direction) {
    var fromIdx = BUILD_ORDER.indexOf(build);
    if (fromIdx === -1) return null;

    var wantBroader = direction === 'smaller';
    var nextIdx = wantBroader ? fromIdx + 1 : fromIdx - 1;
    if (nextIdx < 0 || nextIdx >= BUILD_ORDER.length) return null;

    var ladderIdx = SIZE_LADDER.indexOf(upper(currentToken));
    if (ladderIdx === -1) return null;
    var compensated = wantBroader ? ladderIdx - 1 : ladderIdx + 1;
    if (compensated < 0 || compensated >= SIZE_LADDER.length) return null;

    return { build: BUILD_ORDER[nextIdx], token: SIZE_LADDER[compensated] };
  }

  function applySiblingMap(build, currentToken) {
    var key = (build + ' ' + currentToken).toUpperCase();
    var target = SIBLING_MAP[key];
    if (!target) return null;
    var parts = target.split(/\s+/);
    if (parts.length < 2) return null;
    return { build: parts[0], token: upper(parts[1]) };
  }

  /*
   * Linen Shirts run one letter larger than tees/polos for the same body, so crossing that
   * boundary shifts one rung on the alpha ladder. Bottoms have no ladder position, so they
   * correctly fall out. Mirrors the size_offset block in snippets/cart-upsell.liquid.
   */
  function applyLinenOffset(token, fromIsLinen, toIsLinen) {
    var offset = 0;
    if (!fromIsLinen && toIsLinen) offset = 1;
    else if (fromIsLinen && !toIsLinen) offset = -1;
    if (offset === 0) return token;

    var idx = SIZE_LADDER.indexOf(upper(token));
    if (idx === -1) return null;
    var target = idx + offset;
    if (target < 0 || target >= SIZE_LADDER.length) return null;
    return SIZE_LADDER[target];
  }

  /*
   * The raw merchant override for one fromLabel/toLabel pair, or null. Returns the whole entry
   * rather than just `.copy` so `makeCard()` can also reach `.short` — the customer-facing
   * one-line reason — from the exact same lookup, rather than scanning `CONFIG.fitNotes` twice.
   */
  function fitNoteEntryFor(fromLabel, toLabel) {
    var notes = CONFIG.fitNotes || [];
    for (var i = 0; i < notes.length; i++) {
      if (
        String(notes[i].from || '').trim().toUpperCase() === fromLabel.toUpperCase() &&
        String(notes[i].to || '').trim().toUpperCase() === toLabel.toUpperCase()
      ) {
        return notes[i];
      }
    }
    return null;
  }

  function fitNoteFor(fromLabel, toLabel) {
    var entry = fitNoteEntryFor(fromLabel, toLabel);
    return entry ? entry.copy : null;
  }

  /*
   * Waist-sized products describe a size change on the waist, not the chest — "1 size up,
   * moderate yet noticeable" is chest/shoulder language that reads as nonsense on a trouser
   * (client, 2026-08-28, against the sizing sheet's own "Medium Version (SHOWN TO CUSTOMER)"
   * column: bottoms use "One size up in the same fit", never mentioning the chest at all).
   * `normaliseType` already exists for exactly this matching (case + trailing-plural insensitive),
   * built for the sizing matrix.
   */
  function isBottomsType(type) {
    var normalised = normaliseType(type);
    return normalised === 'cotton trouser' || normalised === 'linen trouser' || normalised === 'linen short';
  }

  /*
   * DIRECTION NAMES WHAT WAS WRONG WITH THE GARMENT, NOT WHAT IS BEING RECOMMENDED. `'smaller'` is
   * the shopper pressing "Too Small", which recommends a BIGGER size; `'larger'` is "Too Big",
   * which recommends a smaller one. The names read backwards at first glance and that is the
   * existing convention throughout this file — `data-rp-fit="smaller"` is the Too Small button.
   */
  function recommendsDownward(direction) {
    return direction === 'larger';
  }

  /*
   * Labels and notes exist as merchant settings per direction. Only the derived 3+ case is built
   * from the magnitude, and it already respected direction — "3 sizes up" is plainly wrong for a
   * shopper who said the garment was too big, and so was "1 size up", which is what this fixes.
   *
   * The fallback when a merchant blanks a "down" setting is a DERIVED down string, never the "up"
   * setting: silently borrowing the opposite direction's wording is the exact bug being fixed here,
   * and it would come back invisibly the first time someone cleared a field.
   */
  function cardLabelFor(kind, magnitude, direction) {
    if (kind === 'different') return CONFIG.cardLabels.different;

    var down = recommendsDownward(direction);
    if (magnitude === 1) {
      return down ? (CONFIG.cardLabels.oneDown || '1 size down') : CONFIG.cardLabels.oneUp;
    }
    if (magnitude === 2) {
      return down ? (CONFIG.cardLabels.twoDown || '2 sizes down') : CONFIG.cardLabels.twoUp;
    }
    return magnitude + (down ? ' sizes down' : ' sizes up');
  }

  /* Which `cardNotes` entry a card takes, given its kind and the shopper's fit direction. */
  /*
   * `bottoms` only branches the plain same-fit size moves. A fit-CHANGE card never reaches the
   * generic 'different'/'differentDown' buckets for a bottoms product in practice — every bottoms
   * fit-change pair is covered by name via a Fit note override block instead (see
   * fitNoteEntryFor(), checked first in makeCard()), because unlike a plain size-up/down the
   * correct wording genuinely depends on which specific build it switches to ("Switch to a Zeus
   * for more definition" vs "...for a roomier cut" is not a single string either direction can
   * share). No "two sizes down" bottoms key either: the client's own matrix never recommends two
   * sizes down in any direction, for tops or bottoms.
   */
  function cardNoteKeyFor(kind, direction, bottoms) {
    var down = recommendsDownward(direction);
    if (kind === 'different') return down ? 'differentDown' : 'different';
    if (kind === 'two_up') return bottoms ? 'twoUpBottoms' : (down ? 'twoDown' : 'twoUp');
    return bottoms ? (down ? 'oneDownBottoms' : 'oneUpBottoms') : (down ? 'oneDown' : 'oneUp');
  }

  function makeCard(kind, product, variant, token, item, options) {
    if (!product || !variant) return null;

    options = options || {};
    var build = product._builds.indexOf(item.build) !== -1 ? item.build : product._build;
    var labelKey = kind === 'one_up' ? 'oneUp' : kind === 'two_up' ? 'twoUp' : 'different';
    var fromLabel = item.build + ' ' + item.sizeToken;
    var toLabel = build + ' ' + token;

    var vars = {
      build: build,
      size: token,
      product: product.title,
      from_build: item.build,
      from_size: item.sizeToken
    };

    var fitNoteEntry = fitNoteEntryFor(fromLabel, toLabel);
    var copy = (fitNoteEntry && fitNoteEntry.copy) || CONFIG.cardCopy[labelKey];

    /*
     * `shortNote` is the one-line version, e.g. "1 size up, moderate yet noticeable". It is what
     * the replacement picker shows under each recommended row, and it comes from the client's own
     * middle column in the sizing sheet.
     *
     * It is picked by DIRECTION as well as kind (client, 2026-08-26: "when they choose Too Big it
     * still says 1 size up, but it's too big, why size up?"). It used to key off `labelKey` alone,
     * so a shopper sent one size DOWN was told the size was one up.
     *
     * A per-pair override (from the same fitNoteEntryFor() lookup as `copy`, via its own `short`
     * field) is checked FIRST — this is how a fit-CHANGE card gets bottoms-correct wording at all,
     * since "Switch to a Zeus for more definition" vs "...for a roomier cut" depends on the exact
     * pair, not just direction. Everything else falls through to the generic settings, now also
     * split by product type (2026-08-28): "1 size up, moderate yet noticeable" is chest/shoulder
     * language that read as nonsense on a waist-sized Trouser/Short (client), so
     * cardNoteKeyFor() picks the bottoms-specific settings for those instead.
     *
     * `copy` is the full paragraph, which the cards used to expand into and nothing renders today.
     * It is deliberately left keyed on `labelKey`: it has no "down" wording of its own, and adding
     * settings for prose nothing displays would be four more fields for the merchant to maintain
     * against a view that may never return. If it is ever rendered again it needs the same
     * direction split this note just got.
     */
    var bottoms = isBottomsType(product.type);
    var pairShort = fitNoteEntry && fitNoteEntry.short;
    var note = pairShort || (CONFIG.cardNotes || {})[cardNoteKeyFor(kind, options.direction, bottoms)] || '';

    return {
      kind: kind,
      label: cardLabelFor(kind, options.magnitude, options.direction),
      copy: fillTemplate(copy, vars),
      shortNote: fillTemplate(note, vars),
      name: build + '\n' + token,
      build: build,
      token: token,
      title: product.title,
      productId: product.id,
      handle: product.handle,
      url: product.url,
      type: product.type,
      variantId: variant.id,
      price: variant.price,
      colour: variantColour(product, variant),
      image: variant.image || product.featuredImage || null
    };
  }

  /*
   * "Maximum two recommendations, not three." (Client meeting, section H.)
   *
   * This is a REAL cap now, not the old safety ceiling: several rows generate a third candidate
   * and it is deliberately dropped. The reason the client gave is about distance rather than
   * layout — the third row for Zeus L too big would have been Apollo M, and Zeus L to Apollo M was
   * called "too substantial" a jump. So the cap belongs to the engine, next to the priority order
   * it truncates, rather than to whatever happens to be rendering the rows this month.
   *
   * What it costs, on the record so it can be reversed knowingly: Poseidon S, Poseidon M and
   * Zeus L "too small" each lose their Different Fit row, because two same-build size steps come
   * first in the client's own priority order.
   */
  var MAX_CARDS = 2;

  /*
   * recommend(item, direction) -> { cards: [...], exhausted: bool }
   *
   * At most TWO cards (see MAX_CARDS), generated in the client's own priority order: one size up
   * (or down), two sizes up ("too small" only), then the adjacent build at the same size. Each is
   * dropped when the catalogue cannot supply it, which is why a row can legitimately come back
   * with one card or none.
   *
   * When the shopper's own build has no size left in the direction they need, the first card
   * becomes the adjacent build one letter the OTHER way (compensatedBuildStep), and the
   * same-letter build card follows it. That is the client's "drop a fit and step up a size".
   *
   * This mirrors the client's "Too Small" / "Too Big" matrix exactly, including its gaps. Poseidon
   * L has no two-sizes-up row because Poseidon tops stop at XL; Apollo S has no build row because
   * Zeus, the next build up, does not carry an S. We never pad: a card the matrix does not contain
   * is worse than a shorter row.
   */
  function recommend(item, direction) {
    var steps = direction === 'larger' ? -1 : 1;
    var product = resolveProduct(item);
    var cards = [];
    var exhausted = false;

    if (!product) return { cards: cards, exhausted: false };

    var seenVariants = {};

    function push(card) {
      if (!card) return;
      if (seenVariants[card.variantId]) return;
      seenVariants[card.variantId] = true;
      cards.push(card);
    }

    /*
     * THE TABLE WINS WHERE IT SPEAKS. Everything below this block is the derivation, which now
     * only runs for origins the client's sheet does not list — so an unlisted product behaves
     * exactly as it did before this existed.
     */
    var listed = matrixFor(item, direction);
    if (listed) {
      listed.forEach(function (target) {
        var targetProduct = target.build === item.build && product._builds.indexOf(target.build) !== -1
          ? product
          : productsForBuildAndType(target.build, product.type)[0];
        if (!targetProduct) return;

        var variant = findVariant(targetProduct, target.token, item.colour);
        if (!variant) return;

        /*
         * The card's KIND drives its wording, so it is derived from what the move actually is
         * rather than stored in the table: same build means a size step, and its magnitude is the
         * distance along that product's own size list; a different build is a fit change.
         */
        var magnitude = 0;
        if (target.build === item.build) {
          var ladder = targetProduct._sizeTokens || [];
          var from = ladder.indexOf(upper(item.sizeToken));
          var to = ladder.indexOf(upper(target.token));
          if (from !== -1 && to !== -1) magnitude = Math.abs(to - from);
        }

        var kind = magnitude === 1 ? 'one_up' : magnitude === 2 ? 'two_up' : magnitude > 2 ? 'n_up' : 'different';
        push(makeCard(kind, targetProduct, variant, target.token, item, {
          magnitude: magnitude,
          direction: direction
        }));
      });

      /*
       * An empty row is the sheet's own "No larger option" / "No smaller option". It has to mark
       * the journey exhausted rather than fall through, or the dead end the client asked for turns
       * back into a derived guess.
       */
      return { cards: cards.slice(0, MAX_CARDS), exhausted: cards.length === 0 };
    }

    /* --- Cards within the shopper's own product, 1..4 steps --- */
    function stepCard(magnitude) {
      var kind = magnitude === 1 ? 'one_up' : magnitude === 2 ? 'two_up' : 'n_up';
      var opts = { magnitude: magnitude, direction: direction };
      var token = stepWithinProduct(product, item.sizeToken, steps * magnitude);

      if (token) {
        push(makeCard(kind, product, findVariant(product, token, item.colour), token, item, opts));
        return;
      }

      /*
       * The ladder ran out. BOTH directions have a cross-build escape hatch now: the client's
       * step 2 ("if no smaller size exists in that fit, drop a fit and step up a size") is stated
       * for "too big", and the mirror answers "too small". This used to bail out for "too big" on
       * the reasoning that a leaner build is a different garment rather than a smaller size, which
       * left Zeus L too big answering with Apollo L alone — the client asked for Apollo XL ahead
       * of it.
       */
      var mapped = applyStepUpMap(item.build, item.sizeToken);
      if (mapped && mapped.exhausted) { exhausted = true; return; }

      /*
       * The compensated build step answers "there is no smaller/larger size in this FIT", which is
       * a first-step question. Asking it again for two steps out would invent a row the client's
       * table does not contain, so magnitude 2 only ever follows an explicit merchant rule.
       */
      if (!mapped) {
        if (magnitude > 1) return;
        mapped = compensatedBuildStep(item.build, item.sizeToken, direction);
        if (!mapped) { exhausted = true; return; }
      }

      var targets = productsForBuildAndType(mapped.build, product.type);
      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        var token2 = mapped.token;
        if (magnitude > 1) {
          var stepped = stepWithinProduct(target, mapped.token, magnitude - 1);
          if (!stepped) continue;
          token2 = stepped;
        }
        var variant = findVariant(target, token2, item.colour);
        if (variant) {
          push(makeCard(kind, target, variant, token2, item, opts));
          break;
        }
      }
    }

    stepCard(1);

    /*
     * Two sizes is a "too small" answer only. Every priority-2 row in the client's Too Big table
     * is a build switch, never a second size down: a garment that is already too big is corrected
     * by one size or by a leaner cut, and offering two sizes down invites the opposite complaint
     * on the replacement. The Too Small table is where the bigger jump earns its place.
     */
    if (direction === 'smaller') stepCard(2);

    /*
     * --- Card C: the ADJACENT build at the same size ---
     *
     * One card, not a fan-out. BUILD_ORDER runs leanest to broadest, so "too small" takes the
     * next build up and "too big" the next build down, and nothing beyond that.
     *
     * This is the client's sizing matrix (the "Too Small" / "Too Big" sheet, 2026-08-13) rather
     * than a derivation of our own, and it differs from what shipped before in three ways worth
     * spelling out, because each one used to produce a card the matrix does not contain:
     *
     *   1. ONE adjacent build, not every broader build. A Poseidon M shopper gets Apollo M and
     *      stops. The matrix never offers Hercules or Zeus to a Poseidon shopper; those are
     *      reached by stepping through Apollo first, on a later request.
     *   2. The SAME letter only. `firstCardForBuild` no longer substitutes the nearest size a
     *      build happens to stock, so Apollo S "too small" yields nothing rather than jumping to
     *      Zeus L, which the matrix agrees with: Zeus has no S or M, and a build that does not
     *      carry the shopper's size is not a fit recommendation.
     *   3. No deeper-step backfill. Three and four sizes out were padding a 6-card carousel with
     *      options the matrix never lists.
     *
     * The merchant's sibling map still wins where it is set, so a rule the matrix cannot express
     * stays possible without code. Its answer is only used when it points the right way along
     * BUILD_ORDER, since the map itself records no direction.
     */
    var fromIdx = BUILD_ORDER.indexOf(item.build);
    var wantBroader = direction === 'smaller';

    /*
     * An empty right-hand side in the step-up map means the catalogue genuinely stops at this
     * build and size, in BOTH directions. The matrix leaves Zeus XXL with no broader answer and
     * Hercules XXL with no leaner one, even though Hercules XXL and Zeus XXL both exist, so the
     * marker has to block the cross-build card as well as the size step that set it.
     */
    var blocked = applyStepUpMap(item.build, item.sizeToken);
    if (blocked && blocked.exhausted) {
      exhausted = true;
    } else {
      var target = null;

      var sibling = applySiblingMap(item.build, item.sizeToken);
      if (sibling) {
        var toIdx = BUILD_ORDER.indexOf(sibling.build);
        var pointsRightWay =
          fromIdx === -1 || toIdx === -1
            ? true // unknown builds: trust the merchant's explicit rule
            : wantBroader
              ? toIdx > fromIdx
              : toIdx < fromIdx;

        if (pointsRightWay) target = sibling;
      }

      if (!target && fromIdx !== -1) {
        var nextIdx = wantBroader ? fromIdx + 1 : fromIdx - 1;
        if (nextIdx >= 0 && nextIdx < BUILD_ORDER.length) {
          target = { build: BUILD_ORDER[nextIdx], token: item.sizeToken };
        }
      }

      if (target) push(firstCardForBuild(target.build, target.token, product, item, direction));
    }

    return { cards: cards.slice(0, MAX_CARDS), exhausted: exhausted && cards.length === 0 };
  }

  /*
   * The adjacent build at the SAME size, or nothing.
   *
   * There used to be a `nearestTokenInProduct` fallback here: when a build did not stock the
   * shopper's letter it offered the closest one it did carry, so that Hercules and Zeus, which
   * start at L, still produced a card for a Poseidon M shopper. The client's sizing matrix does
   * not work that way, and the fallback is what generated the rows it disagrees with. Apollo S
   * "too small" has no Zeus answer in the matrix precisely because Zeus has no S: a build that
   * does not carry the shopper's size is a different garment, not a different size.
   *
   * `direction` no longer selects the BUILD — that is applied one level up, when the adjacent
   * build is chosen — but it is read again as of 2026-08-26 to pick the card's wording. A
   * different-fit card on a "too big" journey is a leaner cut, and describing it with the broader
   * copy was the same defect as telling a Too Big shopper "1 size up".
   */
  function firstCardForBuild(build, token, fromProduct, item, direction) {
    var targets = productsForBuildAndType(build, fromProduct.type);
    for (var i = 0; i < targets.length; i++) {
      var target = targets[i];
      var wanted = applyLinenOffset(token, fromProduct._isLinen, target._isLinen);
      if (!wanted) continue;

      var variant = findVariant(target, wanted, item.colour);
      if (variant) return makeCard('different', target, variant, wanted, item, { direction: direction });
    }
    return null;
  }

  /* ------------------------------------------------------------------ Instant Dispatch match */

  /*
   * Port of the cross-sell rule in snippets/cart-upsell.liquid: same build, DIFFERENT category,
   * equivalent size, in stock — plus colour fan-out, the shopper's own colour first, and the
   * two-phase ordering (one card per distinct product first, then the remaining colours).
   *
   * >>> Keep in sync with snippets/cart-upsell.liquid. <<<
   */
  function matchFitCards(items, excludeVariantIds) {
    var exclude = {};
    (excludeVariantIds || []).forEach(function (id) { exclude[id] = true; });

    var cards = [];
    var seenVariants = {};

    for (var phase = 1; phase <= 2; phase++) {
      var usedProducts = {};

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item.build || !item.sizeToken) continue;

        var itemIsLinen = isLinenShirt(item.type);

        for (var p = 0; p < PRODUCTS.length; p++) {
          var product = PRODUCTS[p];

          if (product._builds.indexOf(item.build) === -1) continue;
          if (product.type === item.type) continue;
          if (usedProducts[product.id]) continue;

          var targetToken = applyLinenOffset(item.sizeToken, itemIsLinen, product._isLinen);
          if (!targetToken) continue;

          var colours = orderedColours(product, targetToken, item.colour);
          if (!colours.length) continue;

          for (var c = 0; c < colours.length; c++) {
            if (phase === 1 && c > 0) break;
            if (phase === 2 && c === 0) continue;

            var variant = findVariant(product, targetToken, colours[c]);
            if (!variant) continue;
            if (exclude[variant.id] || seenVariants[variant.id]) continue;

            seenVariants[variant.id] = true;
            cards.push({
              variantId: variant.id,
              productId: product.id,
              title: product.title,
              url: product.url + '?variant=' + variant.id,
              colour: variantColour(product, variant),
              token: targetToken,
              price: variant.price,
              image: variant.image || product.featuredImage || null
            });
          }

          usedProducts[product.id] = true;
        }
      }
    }

    return cards;
  }

  function orderedColours(product, targetToken, preferredColour) {
    var seen = {};
    var ordered = [];
    var wanted = String(preferredColour || '').toLowerCase();

    if (wanted) {
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (!v.available) continue;
        if (variantSizeToken(product, v) !== targetToken) continue;
        if (variantColour(product, v).toLowerCase() === wanted) {
          ordered.push(variantColour(product, v));
          seen[wanted] = true;
          break;
        }
      }
    }

    for (var j = 0; j < product.variants.length; j++) {
      var variant = product.variants[j];
      if (!variant.available) continue;
      if (variantSizeToken(product, variant) !== targetToken) continue;
      var colour = variantColour(product, variant);
      var key = colour.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      ordered.push(colour);
    }

    return ordered;
  }

  /* Builds "Poseidon Medium", or "Poseidon Medium and Apollo Large", or with bottoms appended
     after a comma using a closing quote. Mirrors the heading joiner in cart-upsell.liquid. */
  function describeSelection(items) {
    var tops = [];
    var bottoms = [];
    var seen = {};

    items.forEach(function (item) {
      var key = item.build + '|' + item.sizeToken;
      if (seen[key]) return;
      seen[key] = true;

      if (SIZE_LADDER.indexOf(upper(item.sizeToken)) !== -1) {
        tops.push(item.build + ' ' + spellSize(item.sizeToken));
      } else {
        bottoms.push(item.sizeToken.replace(/["”]/g, '') + '”');
      }
    });

    var phrase = joinNaturally(tops);
    if (bottoms.length) {
      phrase = phrase ? phrase + ', ' + joinNaturally(bottoms) : joinNaturally(bottoms);
    }
    return phrase;
  }

  function joinNaturally(list) {
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  /* ------------------------------------------------------------------ state */

  function blankState() {
    return {
      v: STATE_VERSION,
      path: null,
      step: 'entry',
      ref: null,
      // Short-lived signed token from /identify. Everything after identification is gated on it,
      // so losing it means starting over — which is why it lives in state, not a variable.
      token: null,
      order: { id: null, name: '', postcode: '', email: '', createdAt: null, source: 'manual' },
      orders: [],
      orderItems: [],
      items: [],
      fit: {},
      picks: {},
      extras: [],
      dispatchIntroPlayed: false,
      truckDriven: false,
      submissionComplete: false,
      returnId: null,
      returnName: null,
      returnMode: null,
      pendingPaymentCents: 0,
      paymentReady: false,
      paymentError: '',
      // Priced by the API for this shopper's currency/country; the theme only ever returns a key.
      // Empty is the shipped default — no rate table configured means no upgrade UI at all.
      shippingOptions: [],
      shippingKey: '',
      paymentUrl: '',
      note: '',
      reason: {},
      // Sizing-advice photo. `photoUrl` is the API's signed link, which is what reaches the team
      // in the email body; `photoName` is only ever shown back to the shopper as confirmation.
      photoUrl: '',
      photoName: ''
    };
  }

  var state = Core.createState(STATE_VERSION);
  var saveTimer = null;

  function load() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      state = Core.restoreState(parsed, STATE_VERSION);
    } catch (err) {
      /* Safari private mode and quota errors both land here. A lost draft is not worth an
         exception on page load. */
      state = Core.createState(STATE_VERSION);
    }
  }

  function save() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (err) { /* see load() */ }
    }, 200);
  }

  function clearState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
    state = Core.createState(STATE_VERSION);
  }

  /*
   * "Start over" is guarded by a second press on the button itself. See the [data-rp-restart]
   * branch of the click handler for why this cannot be `window.confirm()`.
   *
   * The armed label replaces the button's own text rather than adding a message beside it: the
   * client's standing direction on this portal is to remove boxes, backgrounds and explainer copy,
   * so the confirmation is plain text in the control the shopper already pressed.
   */
  var RESTART_ARM_MS = 6000;
  var RESTART_ARMED_LABEL = 'Press again to start over';
  var armedRestart = null;
  var armedRestartTimer = 0;

  function armRestart(button) {
    disarmRestart();
    armedRestart = button;
    button.dataset.rpRestartArmed = 'true';
    button.dataset.rpRestartLabel = button.textContent;
    button.textContent = RESTART_ARMED_LABEL;
    announce('Press again to start over. Your selections will be cleared.');
    armedRestartTimer = window.setTimeout(disarmRestart, RESTART_ARM_MS);
  }

  function disarmRestart() {
    if (armedRestartTimer) {
      window.clearTimeout(armedRestartTimer);
      armedRestartTimer = 0;
    }
    if (!armedRestart) return;
    if (armedRestart.dataset.rpRestartLabel !== undefined) {
      armedRestart.textContent = armedRestart.dataset.rpRestartLabel;
      delete armedRestart.dataset.rpRestartLabel;
    }
    delete armedRestart.dataset.rpRestartArmed;
    armedRestart = null;
  }

  function makeRef() {
    var alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var out = '';
    var bytes = new Uint8Array(5);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    for (var i = 0; i < bytes.length; i++) out += alphabet[bytes[i] % alphabet.length];
    return 'RX-' + out;
  }

  /* ------------------------------------------------------------------ routing */

  var STEPS = {
    exchange: ['identify', 'orders', 'items', 'replace', 'dispatch', 'review'],
    return: ['identify', 'orders', 'items', 'reason', 'review'],
    advice: ['identify', 'advice-detail']
  };

  /*
   * Steps that don't apply to this render drop out, so one step list covers every combination:
   * a merchant who turned Instant Dispatch off, and the fallback flow where there is no order to
   * pick because we never looked one up.
   *
   * "Exactly one order" used to always skip this step — right for a selectable order, wrong for
   * one outside the 14-day window or otherwise blocked: the shopper would sail straight past the
   * only screen that explains why, into an items list they were never meant to reach (client,
   * 2026-08-28: the window "restriction" wasn't being enforced at all for the common single-order
   * case, only for shoppers with two or more). The step now also stays when the sole order fails
   * orderSelectable(), so it renders exactly like a multi-order list of one — greyed out, with the
   * reason, reusing the exact same card the 2+ case already shows rather than inventing a second
   * "you can't do this" screen.
   */
  function stepsFor(path) {
    return (STEPS[path] || []).filter(function (step) {
      if (!stepEl(step, path)) return false;
      if (step === 'orders') {
        if (!LIVE) return false;
        var multiple = state.orders.length >= 2;
        var soleBlocked = state.orders.length === 1 && !orderSelectable(state.orders[0]);
        if (!multiple && !soleBlocked) return false;
      }
      return true;
    });
  }

  function stepEl(step, path) {
    var nodes = root.querySelectorAll('[data-rp-step="' + step + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var paths = nodes[i].dataset.rpPaths;
      if (!paths || !path || paths.split(/\s+/).indexOf(path) !== -1) return nodes[i];
    }
    return null;
  }

  var REQUIRES = {
    orders: function (s) { return s.orders.length > 0; },
    items: function (s) {
      if (s.order.source === 'api') return !!s.order.id;
      return isEmail(s.order.email);
    },
    replace: function (s) { return s.items.length > 0; },
    reason: function (s) { return s.items.length > 0; },
    'advice-detail': function (s) { return !!s.order.email; },
    dispatch: function (s) { return Object.keys(s.picks).length > 0; },
    review: function (s) {
      if (s.path === 'return') return s.items.length > 0;
      return Object.keys(s.picks).length > 0;
    }
  };

  function guard(step) {
    // Shopify has already accepted this return. No earlier wizard step is valid any more: opening
    // one from browser history would expose a stale Submit button and allow a duplicate request.
    if (state.submissionComplete) return 'done';
    if (step === 'entry' || step === 'done') return step;
    if (!state.path) return 'entry';

    var list = stepsFor(state.path);
    var wanted = list.indexOf(step);
    if (wanted === -1) return 'entry';

    for (var i = 0; i <= wanted; i++) {
      var check = REQUIRES[list[i]];
      if (check && !check(state)) return i === 0 ? 'entry' : list[Math.max(0, i - 1)];
    }
    return step;
  }

  function render(step, options) {
    options = options || {};
    var target = guard(step);
    state.step = target;

    if (!SHOW_ALL) {
      var all = root.querySelectorAll('[data-rp-step]');
      for (var i = 0; i < all.length; i++) all[i].hidden = true;

      var el = stepEl(target, state.path);
      if (el) el.hidden = false;
    }

    var currentHistory = history.state && history.state.rpPortal ? history.state : null;
    var currentDepth = currentHistory ? Number(currentHistory.rpDepth) || 0 : 0;
    var historyMode = options.history === undefined ? 'push' : options.history;

    if (historyMode === 'replace') {
      try {
        history.replaceState(
          Core.historyState(target, options.historyDepth === undefined ? currentDepth : options.historyDepth),
          '',
          '#' + target
        );
      } catch (err) { /* pushState can throw in odd embedding contexts */ }
    } else if (historyMode === 'push') {
      try {
        history.pushState(Core.historyState(target, currentDepth + 1), '', '#' + target);
      } catch (err) { /* pushState can throw in odd embedding contexts */ }
    }

    renderStep(target);
    restoreSharedControls();
    updateSticky();
    save();

    if (options.focus !== false) {
      var heading = stepEl(target, state.path);
      var title = heading && heading.querySelector('.rp__step-title');
      if (title) title.focus({ preventScroll: true });

      /*
       * Only correct the scroll when the portal has actually left the top of the viewport — after
       * a long step, say, where advancing would otherwise drop the shopper into the middle of the
       * new screen. If the portal is already in view, leave the page exactly where it is.
       *
       * This used to pull the STEP to the top of the window whenever it sat below 40% of the
       * viewport, which made sense when every step rendered its own visible heading. Since the
       * redesign that heading is clipped and mirrored into the burgundy masthead, so scrolling the
       * step flush to the top scrolled the masthead — the only place the title is actually
       * readable — off screen. Moving from the entry screen to "Let's find your order" jumped the
       * page down for no reason. Scroll the PORTAL, masthead included, or scroll nothing.
       */
      if (root.getBoundingClientRect().top < 0) {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  function go(step) { render(step, { history: 'push' }); }

  function restoreSharedControls() {
    Core.syncValues(root.querySelectorAll('[data-rp-note]'), state.note || '');
    Core.syncValues(
      root.querySelectorAll('[data-rp-email], [data-rp-advice-email]'),
      state.order.email || ''
    );
    Core.syncValues(root.querySelectorAll('[data-rp-postcode]'), state.order.postcode || '');
  }

  /*
   * Advancing is async at two points: leaving `identify` looks the order up, and leaving `orders`
   * loads that order's returnable items. Both show a busy state on the button and surface a
   * failure in place rather than moving the shopper to a broken screen.
   */
  function next() {
    var list = stepsFor(state.path);
    var idx = list.indexOf(state.step);
    if (idx === -1) { go(list[0]); return; }

    /*
     * Walks forward one step, stepping OVER Instant Dispatch when it has nothing to offer.
     *
     * That step is pure upsell: with no matching products it is a dead screen the shopper has to
     * click Next past. It happens on an ordinary journey — a linen shirt at size S maps to a
     * tee/polo XS, which the brand does not make, so the match is legitimately empty.
     *
     * Deliberately NOT done by filtering `dispatch` out of stepsFor(). guard() returns 'entry' for
     * any step not in the list, and the count CAN legitimately fall to zero while the shopper is
     * standing on the step — extras are passed to matchFitCards as `exclude`, so adding every
     * offered card empties it. Filtering there would throw that shopper back to the entry screen
     * mid-journey. Skipping only on forward navigation leaves the step valid and reachable.
     *
     * back() needs no matching change: it is history.back() whenever there is depth, and a step
     * that was never rendered never entered the history stack.
     */
    var advance = function () {
      var updated = stepsFor(state.path);
      var here = updated.indexOf(state.step);
      var target = here + 1;

      while (
        updated[target] === 'dispatch' &&
        !dispatchCards().cards.length &&
        !state.extras.length
      ) target++;

      if (target < updated.length) go(updated[target]);
    };

    if (state.step === 'identify' && state.path !== 'advice' && LIVE) {
      runBusy(function () {
        return identifyOrder({ email: state.order.email, postcode: state.order.postcode })
          .then(function () {
            hideIdentifyError();
            /*
             * With a single SELECTABLE order, identifyOrder() already called selectOrder() for it,
             * so pull its items straight away and skip a pointless "choose your order" screen. A
             * blocked sole order was deliberately left unselected — state.order.id is still null,
             * loadOrderItems() would no-op on that anyway, and stepsFor() now keeps 'orders' in the
             * list for exactly this case, so advance() below lands there instead of on 'items'.
             */
            if (state.orders.length === 1 && state.order.id) return loadOrderItems();
          })
          .then(advance)
          .catch(function (err) {
            /*
             * Two different failures land here and they need different messages. If we already
             * hold a token then /identify succeeded and it was loading the ITEMS that broke —
             * telling that shopper "we couldn't find your order" is simply untrue, and sends them
             * off to re-check details that were correct.
             */
            if (state.token) showLoadError(err);
            else showIdentifyError(err);
          });
      });
      return;
    }

    if (state.step === 'orders' && LIVE) {
      runBusy(function () { return loadOrderItems().then(advance).catch(showLoadError); });
      return;
    }

    advance();
  }

  /* Disables the current step's controls while a request is in flight. */
  function runBusy(work) {
    var el = stepEl(state.step, state.path);
    var buttons = el ? el.querySelectorAll('[data-rp-next], [data-rp-back]') : [];
    for (var i = 0; i < buttons.length; i++) buttons[i].setAttribute('aria-busy', 'true');

    var label = el && el.querySelector('[data-rp-next-label]');
    var original = label ? label.textContent : null;
    if (label) label.textContent = 'Finding your order…';

    work().then(function () {
      for (var j = 0; j < buttons.length; j++) buttons[j].removeAttribute('aria-busy');
      if (label && original !== null) label.textContent = original;
    });
  }

  function showIdentifyError(err) {
    console.error('[rp] identify failed', err && (err.body || err.message));
    var box = root.querySelector('[data-rp-identify-error]');
    if (box) {
      // Rate limiting is worth naming — "try again" is bad advice when the answer is "wait".
      var limited = err && err.body && err.body.error === 'rate_limited';
      if (limited && err.body.message) box.textContent = err.body.message;
      box.hidden = false;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    syncNextButtons();
  }

  function hideIdentifyError() {
    var box = root.querySelector('[data-rp-identify-error]');
    if (box) box.hidden = true;
  }

  /*
   * The order WAS found but we couldn't load its items. Say something true — "we found your order
   * but couldn't load it" — rather than reusing the not-found copy, which would send the shopper
   * off double-checking details that were right all along.
   */
  function showLoadError(err) {
    console.error('[rp] could not load order items', err && (err.body || err.message));
    var box = root.querySelector('[data-rp-identify-error]');
    if (box) {
      box.textContent =
        "We found your order, but couldn't load its items just now. Please try again in a moment, " +
        'or get in touch and we\'ll sort it out for you.';
      box.hidden = false;
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    syncNextButtons();
  }

  function back() {
    var current = history.state;
    if (current && current.rpPortal && Number(current.rpDepth) > 0) {
      history.back();
      return;
    }

    render(Core.previousStep(stepsFor(state.path), state.step), {
      history: 'replace',
      historyDepth: 0
    });
  }

  /* ------------------------------------------------------------------ the returns API */

  /*
   * XHR, not fetch. Fast Bundle monkey-patches window.fetch on this store and its wrapper throws
   * on the response — documented for cart mutations, and not worth risking on the one call that
   * stands between a shopper and their order. XHR is not wrapped.
   *
   * The timeout is a watchdog: a hung request must surface as an error the shopper can act on,
   * never as a button stuck on "Finding your order…".
   */
  function apiPost(path, payload) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', API_URL + path);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.responseType = 'json';
      xhr.timeout = 15000;
      xhr.onload = function () {
        var body = xhr.response;
        if (typeof body === 'string') {
          try { body = JSON.parse(body); } catch (err) { body = null; }
        }
        if (xhr.status >= 200 && xhr.status < 300 && body && body.ok) resolve(body);
        else reject(Object.assign(new Error(path + ' failed'), { status: xhr.status, body: body }));
      };
      xhr.onerror = function () { reject(new Error(path + ': network error')); };
      xhr.ontimeout = function () { reject(new Error(path + ': timed out')); };
      xhr.send(JSON.stringify(payload));
    });
  }

  /* ------------------------------------------------------------------ sizing-advice photo */

  /*
   * Shopify's contact form cannot carry a file, which is why this path historically ended with
   * "reply to our email and attach it" on the thank-you screen. The photo now goes to the returns
   * API's /advice-photo instead, which stores it privately and hands back a signed link; only the
   * link travels in the contact-form email.
   *
   * Uploaded on SELECTION, not on submit. The contact form submits natively and synchronously, so
   * uploading at submit time would mean intercepting it, going async, then re-submitting — and any
   * failure there lands after the shopper thinks they are finished. Doing it here means the link
   * is already in state by the time Submit is pressed, the shopper sees it worked, and a failure
   * is recoverable while they are still on the screen.
   *
   * Anything above 4MB is stopped HERE rather than at the API: Vercel rejects a request over its
   * own ~4.5MB limit before our handler runs, and answers with a plain-text platform error rather
   * than the JSON shape the rest of this file expects.
   */
  var PHOTO_MAX_BYTES = 4000000;
  var PHOTO_TYPES = { 'image/jpeg': 1, 'image/png': 1, 'image/webp': 1 };
  var PHOTO_STATUS_DEFAULT = 'A photo of the fit helps us size you accurately. JPG, PNG or WebP, up to 4MB.';

  function setPhotoStatus(message) {
    var status = root.querySelector('[data-rp-photo-status]');
    if (status) status.textContent = message;
  }

  // Remove is only ever offered against a photo that is actually attached — never mid-upload,
  // never against a failed attempt, so it can't be pressed on something that isn't there.
  function syncPhotoRemoveButton() {
    var removeBtn = root.querySelector('[data-rp-photo-remove]');
    if (removeBtn) removeBtn.hidden = !state.photoUrl;
  }

  /*
   * Clears the attachment only — never touches state.note, so a shopper who decides the photo
   * was a mistake keeps everything they've typed. The file input is reset too, or choosing the
   * exact same file again would not fire a change event and the shopper would be stuck.
   */
  function removeAdvicePhoto() {
    state.photoUrl = '';
    state.photoName = '';
    var input = root.querySelector('[data-rp-photo]');
    if (input) input.value = '';
    setPhotoStatus(PHOTO_STATUS_DEFAULT);
    syncPhotoRemoveButton();
    save();
  }

  function uploadAdvicePhoto(input) {
    var file = input.files && input.files[0];
    if (!file) return;

    // A new choice invalidates whatever was attached before, including a failed attempt.
    state.photoUrl = '';
    state.photoName = '';
    syncPhotoRemoveButton();

    if (!PHOTO_TYPES[file.type]) {
      input.value = '';
      setPhotoStatus('That file type is not supported. Please choose a JPG, PNG or WebP.');
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      input.value = '';
      setPhotoStatus('That photo is over 4MB. Please choose a smaller one.');
      return;
    }

    if (!state.ref) state.ref = makeRef();

    var form = new FormData();
    form.append('email', state.order.email || '');
    form.append('reference', state.ref);
    form.append('file', file);

    setPhotoUploading(true);
    setPhotoStatus('Uploading ' + file.name + '…');

    var xhr = new XMLHttpRequest();
    xhr.open('POST', API_URL + '/advice-photo');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.responseType = 'json';
    // Generous next to apiPost's 15s: this is an upload over a phone connection, not a lookup.
    xhr.timeout = 60000;

    xhr.onload = function () {
      var body = xhr.response;
      if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch (err) { body = null; }
      }
      setPhotoUploading(false);

      if (xhr.status >= 200 && xhr.status < 300 && body && body.ok && body.photoUrl) {
        state.photoUrl = body.photoUrl;
        state.photoName = file.name;
        setPhotoStatus('Photo attached: ' + file.name);
        syncPhotoRemoveButton();
        save();
        return;
      }

      /*
       * Never block submission on a failed photo. The note alone is a complete request, and the
       * thank-you screen still explains how to email a picture — so this degrades to exactly the
       * behaviour this path had before uploads existed.
       */
      input.value = '';
      setPhotoStatus(
        (body && body.error === 'photo_too_large')
          ? 'That photo is too large. Please choose one under 4MB.'
          : "We couldn't upload that photo. You can still submit, and reply to our email with it attached."
      );
    };

    xhr.onerror = function () {
      setPhotoUploading(false);
      input.value = '';
      setPhotoStatus("We couldn't upload that photo. You can still submit without it.");
    };
    xhr.ontimeout = function () {
      setPhotoUploading(false);
      input.value = '';
      setPhotoStatus('That upload timed out. You can still submit without it.');
    };

    xhr.send(form);
  }

  /*
   * Submit is blocked only while bytes are actually in flight, so a shopper cannot send the
   * request a moment before the link that is the whole point of it arrives.
   */
  function setPhotoUploading(active) {
    state.photoUploading = !!active;
    var choose = root.querySelector('.rp__photo-choose');
    if (choose) choose.setAttribute('aria-disabled', active ? 'true' : 'false');
    syncNextButtons();
  }

  /* ------------------------------------------------------------------ seam 1: identify */

  /*
   * SEAM. Turns whatever the shopper gave us into `state.order`.
   *
   * Live: POST /identify verifies email + postcode server-side and returns the matching orders
   * plus a short-lived signed token. Everything after this point is gated on that token, so this
   * is the only place ownership is proven.
   *
   * Fallback: record the email and move on. Nothing is verified, and the confirmation email says
   * so explicitly rather than implying we checked.
   */
  function identifyOrder(input) {
    state.order = Object.assign({}, state.order, input);

    if (!LIVE) {
      state.order.source = 'manual';
      save();
      return Promise.resolve({ orders: [] });
    }

    return apiPost('/identify', {
      email: state.order.email,
      postcode: state.order.postcode,
    }).then(function (result) {
      state.token = result.token;
      state.orders = result.orders || [];
      state.order.source = 'api';

      /*
       * Exactly one order is the common case — don't make them "choose" from a list of one.
       *
       * But only when it can actually be picked. Auto-selecting a blocked order (past the 14-day
       * window, or, for an exchange, undelivered/already fully returned) used to send the shopper
       * straight into the items list with no explanation at all — the window "restriction" was
       * real for a shopper with two-plus orders and simply absent for everyone else, since this
       * was the only path that skipped orderSelectable() entirely. Leaving it unselected here is
       * what makes stepsFor() keep the 'orders' step (see its own comment) instead of skipping to
       * 'items', so the shopper lands on the same greyed-out card a multi-order list would show.
       */
      if (state.orders.length === 1 && orderSelectable(state.orders[0])) selectOrder(state.orders[0]);

      save();
      return result;
    });
  }

  function selectOrder(order) {
    state.order.id = order.id;
    state.order.name = order.name;
    state.order.createdAt = order.createdAt;
    state.items = [];
    state.picks = {};
    state.fit = {};
    state.orderItems = [];
    save();
  }

  /*
   * Loads the genuinely returnable items for the chosen order. "Returnable" is Shopify's own
   * answer — it excludes anything undelivered or already returned — so a shopper can't request
   * something that would be rejected later.
   */
  /*
   * Shopify returns one entry per FULFILLMENT line, so a variant that shipped in two parcels — or
   * was added to the order as two separate lines — comes back as two rows the shopper cannot tell
   * apart ("why is this item listed twice?"). Merge to one row per variant and keep the individual
   * fulfillment lines in `sources`: returnCreate still needs a quantity against each
   * fulfillmentLineItemId, so the merge is presentational only and is undone at submit.
   */
  function mergeReturnableLines(lines) {
    var merged = [];
    var byVariant = {};

    (lines || []).forEach(function (li) {
      // Variant id is the real identity. Fall back to the visible attributes for a line whose
      // product was deleted, so those still collapse rather than showing as duplicates.
      var id = li.variantId || [li.title, li.size, li.colour].join('|');
      var existing = byVariant[id];

      if (!existing) {
        existing = Object.assign({}, li, { sources: [] });
        byVariant[id] = existing;
        merged.push(existing);
      } else {
        existing.quantity += li.quantity;
        /*
         * ANY unit in this row having spent its free exchange marks the whole row.
         *
         * Rows merge by variant, so an original unit and a replacement unit of the SAME variant
         * collapse into one row — and `Object.assign` above only ever copied the first line's
         * flag, which would silently hide the fact for every later line. Erring toward marking is
         * the right way round: the server decides the actual charge per fulfillment line at
         * submit, so a shopper warned here and then not charged has lost nothing, whereas an
         * unmarked row that turns out chargeable is a surprise at the till.
         *
         * KNOWN LIMITATION, pre-dating this flag: `sources` hands submit the fulfillment lines in
         * array order, so returning 1 of 2 otherwise identical units picks whichever line comes
         * first rather than letting the shopper choose the replacement or the original. Whether
         * that unit is the chargeable one is therefore down to ordering. Worth revisiting if the
         * re-exchange fee ever goes live.
         */
        if (li.fromExchange) existing.fromExchange = true;
      }

      existing.sources.push({
        fulfillmentLineItemId: li.fulfillmentLineItemId,
        quantity: li.quantity
      });
    });

    return merged;
  }

  function loadOrderItems() {
    if (!LIVE || !state.order.id) return Promise.resolve([]);

    return apiPost('/order', { token: state.token, orderId: state.order.id }).then(function (result) {
      state.orderItems = mergeReturnableLines(result.items || []);
      save();
      return state.orderItems;
    });
  }

  /* ------------------------------------------------------------------ seam 2: submit */

  /*
   * SEAM. Sends the finished request.
   *
   * Live + exchange/return: POST /submit creates a REAL Return in Shopify, with exchange items
   * where the shopper picked a replacement. Returns a promise, and the caller must prevent the
   * native form submit so the page doesn't navigate away underneath it.
   *
   * Everything else (sizing advice, or no API configured): falls back to the contact form — a
   * native POST, which never touches window.fetch. Sizing advice has no return to create; it is
   * a question for a human, so it stays an email by design.
   */
  function submitViaApi() {
    if (!state.ref) state.ref = makeRef();

    /*
     * A row the shopper sees may stand for several fulfillment lines (see mergeReturnableLines),
     * so spread the requested quantity across them in order, filling each before moving on.
     * Shopify validates quantity per fulfillmentLineItemId, so sending the row total against a
     * single line would be rejected whenever the variant shipped in more than one parcel.
     */
    var items = [];

    state.items.forEach(function (item) {
      var sources = (item.sources && item.sources.length)
        ? item.sources
        : (item.fulfillmentLineItemId
          ? [{ fulfillmentLineItemId: item.fulfillmentLineItemId, quantity: item.maxQty || 1 }]
          : []);

      var remaining = item.qty || 1;
      var reason = state.reason[item.key] || state.fit[item.key] || 'other';

      sources.forEach(function (source) {
        if (remaining <= 0 || !source.fulfillmentLineItemId) return;

        var take = Math.min(remaining, source.quantity || 1);
        remaining -= take;

        items.push({
          fulfillmentLineItemId: source.fulfillmentLineItemId,
          quantity: take,
          reason: reason,
          note: state.note || '',
        });
      });
    });

    /*
     * Only the like-for-like part goes on the Return. Surplus units are a purchase and are put in
     * the cart instead (see the submit handler), so they are actually paid for.
     */
    var exchanges = groupUnits(splitReplacementUnits().exchange).map(function (entry) {
      return {
        variantId: entry.pick.variantGid || entry.pick.variantId,
        quantity: entry.quantity
      };
    });

    return apiPost('/submit', {
      token: state.token,
      orderId: state.order.id,
      items: items,
      exchanges: exchanges,
      note: state.note || '',
    });
  }

  /* ------------------------------------------------------------------ seam 3: payment */

  /*
   * SEAM. Turns whatever is owed into a payable Shopify checkout.
   *
   * A Return cannot collect money — `returnCreate` opens the reverse logistics and nothing else —
   * so surplus replacement units, Instant Dispatch extras and a paid delivery upgrade go onto a
   * DRAFT ORDER that /payment creates, tagged and attributed back to the original order and the
   * Return. `invoiceUrl` is Shopify's own hosted checkout for it.
   *
   * Runs on arrival at the confirmation and again whenever the delivery choice changes. Safe to
   * call repeatedly: the endpoint is idempotent per Return, returning the same invoice rather than
   * minting a second, and repricing the open draft when the delivery option has changed.
   *
   * FALLS BACK, NEVER FAILS SHUT. If the endpoint is unreachable, or the app has not yet been
   * re-authorised for draft orders, `paymentUrl` stays empty and the Pay button keeps pointing at
   * the storefront cart, which is where the interim build sent it. That link can still take the
   * money; it just carries no linkage. Losing the linkage is a reporting problem, and blocking a
   * shopper who wants to pay would be a worse one.
   */
  /*
   * NO LONGER CALLED, AS OF 2026-09-01 — and deliberately kept rather than deleted.
   *
   * A draft order cannot run Shopify's discount functions, so paying a balance through one charged
   * full list price and silently skipped Mix & Match (£116.00 against the £94.00 an ordinary cart
   * charges for the same two garments). The balance now goes through the real cart instead; see
   * the long note on the submit path.
   *
   * It stays here because `/payment` itself is live, re-authorised (Obaid did the draft_orders
   * OAuth by hand) and still the only mechanism that can charge a SHIPPING line — which is what
   * the delivery-upgrade feature needs if it is ever switched on. Deleting this would mean
   * unpicking working, hard-won infrastructure to save a few lines. If shipping upgrades do come
   * back, the goods must still go through the cart; only the shipping charge belongs here.
   */
  function preparePayment() {
    if (!LIVE || !state.submissionComplete || !state.returnId) return Promise.resolve();
    if (totalOwedCents() <= 0) {
      state.paymentUrl = '';
      state.paymentReady = true;
      return Promise.resolve();
    }

    var surplus = groupUnits(splitReplacementUnits().surplus).map(function (entry) {
      return { variantId: entry.pick.variantGid || entry.pick.variantId, quantity: entry.quantity };
    });
    var extras = state.extras.map(function (extra) {
      return { variantId: extra.variantId, quantity: extra.qty || 1 };
    });

    return apiPost('/payment', {
      token: state.token,
      orderId: state.order.id,
      returnId: state.returnId,
      items: surplus.concat(extras),
      // The confirmation no longer offers a delivery upgrade. An empty key leaves fulfilment on
      // the return API's standard/default route without proposing a browser-controlled price.
      shippingKey: ''
    })
      .then(function (result) {
        state.paymentUrl = (result && result.invoiceUrl) || '';
        // The paid-submit path hides checkout while this linked invoice is being minted. Restore
        // readiness before its caller decides whether to use the invoice or the cart fallback.
        state.paymentReady = true;
        save();
        renderSubmittedSummary();
      })
      .catch(function (err) {
        console.error('[rp] payment draft failed', err && (err.body || err.message));
        /*
         * Still `true`: a failed draft order must not block a shopper who wants to pay. The Pay
         * button falls back to the storefront cart (see renderSubmittedSummary) — losing the
         * order/Return linkage is a reporting problem, refusing the payment is a worse one.
         */
        state.paymentUrl = '';
        state.paymentReady = true;
        save();
        renderSubmittedSummary();
      });
  }

  /* Fills the hidden contact form and lets the browser POST it natively. */
  function submitRequest(form) {
    if (!state.ref) state.ref = makeRef();

    setField(form, 'path', pathLabel(state.path));
    setField(form, 'ref', state.ref);
    setField(form, 'order', state.order.name || '(not given)');
    setField(form, 'postcode', state.order.postcode || '(not given)');

    if (state.order.email) {
      var emailField = form.querySelector('[data-rp-field="email"]');
      if (emailField && !emailField.value) emailField.value = state.order.email;
    }

    var nameField = form.querySelector('[data-rp-field="name"]');
    if (nameField && !nameField.value) {
      nameField.value = state.order.email ? state.order.email.split('@')[0] : 'Returns portal';
    }

    setField(form, 'body', buildBody());
    save();
    return true;
  }

  /*
   * Posts the contact form without navigating, so the portal can render its own confirmation the
   * way the API path does. `new FormData(form)` carries the hidden fields Shopify's own {% form %}
   * tag injects (`form_type`, `utf8`) alongside our `contact[...]` ones, so the request Shopify
   * receives is byte-for-byte the one the native POST would have sent — the transport is all that
   * changed, not the payload, so the email the team receives is unchanged.
   *
   * XHR, not fetch. Fast Bundle monkey-patches window.fetch on this store; the form snippet's own
   * doc comment cites "it never touches window.fetch" as the reason it was a native POST, and XHR
   * is how the rest of this file (uploadAdvicePhoto, cartXhr) keeps that property while still
   * going async.
   *
   * Shopify answers a successful contact POST with a redirect, which XHR follows on its own and
   * lands on an ordinary 200 — so any 2xx means accepted. Nothing parses the returned HTML: every
   * value the confirmation screen needs is already in `state`.
   */
  function submitViaContactForm(form) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', form.getAttribute('action') || (CONFIG.rootUrl || '/') + 'contact');
      xhr.timeout = 20000;
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('contact form returned ' + xhr.status));
      };
      xhr.onerror = function () { reject(new Error('network error')); };
      xhr.ontimeout = function () { reject(new Error('timed out')); };
      xhr.send(new FormData(form));
    });
  }

  function setField(form, key, value) {
    var el = form.querySelector('[data-rp-field="' + key + '"]');
    if (el) el.value = value;
  }

  function pathLabel(path) {
    if (path === 'exchange') return 'Exchange';
    if (path === 'advice') return 'Sizing advice';
    if (path === 'return') return 'Return';
    return 'Request';
  }

  function buildBody() {
    var lines = [];
    lines.push(pathLabel(state.path).toUpperCase() + ' REQUEST · ' + state.ref);

    var head = [];
    if (state.order.name) head.push('Order ' + state.order.name);
    if (state.order.createdAt) head.push('placed ' + state.order.createdAt);
    if (state.order.postcode) head.push(state.order.postcode);
    head.push(
      state.order.source === 'api'
        ? 'order verified by email + postcode'
        : 'not verified — shopper typed these'
    );
    lines.push(head.join(' · '));
    lines.push('');

    if (state.items.length) {
      lines.push(state.path === 'exchange' ? 'RETURNING (' + totalQty(state.items) + ')' : 'ITEMS (' + totalQty(state.items) + ')');
      state.items.forEach(function (item) {
        var row = '  ' + item.qty + ' x ' + item.title + ' — ' + [item.colour, item.sizeToken].filter(Boolean).join(', ');
        if (item.variantId) row += '  (variant ' + item.variantId + ')';
        // item.currency = what the order was charged in; the team's copy must agree with the
        // shopper's screen, so it takes the same override the review lines do.
        if (item.price != null) row += '  ' + money(item.price * item.qty, item.currency);
        lines.push(row);

        var reason = state.reason[item.key] || (state.fit[item.key] === 'larger' ? 'Too big' : state.fit[item.key] === 'smaller' ? 'Too small' : '');
        if (reason) lines.push('    Reason: ' + reason);
      });
      lines.push('');
    }

    var picks = allPicks();
    if (picks.length) {
      lines.push('RECEIVING (' + replacementUnits() + ')');
      picks.forEach(function (pick) {
        lines.push('  ' + (pick.qty || 1) + ' x ' + pick.title + ' — ' + [pick.colour, pick.token].filter(Boolean).join(', ') +
          '  [' + pick.label + ']  (variant ' + pick.variantId + ')  ' + money(pick.price));
      });
      lines.push('');
    }

    if (state.extras.length) {
      lines.push('ADDED TO CART (Instant Dispatch)');
      state.extras.forEach(function (extra) {
        lines.push('  1 x ' + extra.title + ' — ' + [extra.colour, extra.token].filter(Boolean).join(', ') +
          '  (variant ' + extra.variantId + ')');
      });
      lines.push('');
    }

    if (state.path === 'exchange') {
      lines.push('DIFFERENCE (indicative): ' + money(differenceCents()));
      lines.push('  Indicative only — ignores discounts on the original order, tax and shipping.');
      lines.push('');
    }

    /*
     * The whole point of the sizing-advice path. Above the note deliberately: the team opens
     * these to look at the garment on the shopper, so burying the link under a paragraph of free
     * text makes the one thing they need the last thing they find.
     */
    if (state.photoUrl) {
      lines.push('PHOTO');
      lines.push('  ' + state.photoUrl);
      lines.push('  Link expires 30 days after upload.');
      lines.push('');
    }

    if (state.note) {
      lines.push('CUSTOMER NOTE');
      lines.push('  "' + state.note.trim() + '"');
      lines.push('');
    }

    /* Machine-readable tail so a future app (or a script) can parse a request without
       reverse-engineering the prose above. Capped so support still gets a readable email. */
    var payload = {
      v: STATE_VERSION,
      ref: state.ref,
      path: state.path,
      order: state.order,
      items: state.items,
      picks: state.picks,
      extras: state.extras,
      fit: state.fit,
      reason: state.reason
    };
    var json = JSON.stringify(payload);
    if (json.length > 4000) json = json.slice(0, 4000) + '…(truncated)';
    lines.push('--- ORSERIO-RX-JSON ---');
    lines.push(json);

    return lines.join('\n');
  }

  function totalQty(items) {
    return items.reduce(function (sum, item) { return sum + (item.qty || 1); }, 0);
  }

  /* ---------------------------------------------------------------- replacement selections
   *
   * `state.picks[itemKey]` is a LIST. A shopper who isn't sure whether to go one or two sizes up
   * can take the item back in several sizes, which is the point of the "choose another" block —
   * so every reader goes through these rather than assuming a single object.
   */

  function picksFor(key) {
    return Core.pickList(state.picks, key);
  }

  function primaryPick(key) {
    return picksFor(key)[0] || null;
  }

  function setPicks(key, list) {
    if (list && list.length) state.picks[key] = list;
    else delete state.picks[key];
  }

  /* Replaces the first selection, preserving any additional sizes chosen beneath it. */
  function setPrimaryPick(key, pick) {
    var list = picksFor(key).slice();

    if (!pick) {
      list.shift();
      setPicks(key, list);
      return;
    }

    pick.qty = pick.qty || 1;
    if (list.length) list[0] = pick;
    else list.push(pick);

    setPicks(key, list);
  }

  function allPicks() {
    var out = [];
    Object.keys(state.picks).forEach(function (key) {
      picksFor(key).forEach(function (pick) { out.push(pick); });
    });
    return out;
  }

  function replacementUnits() {
    return allPicks().reduce(function (sum, pick) { return sum + (pick.qty || 1); }, 0);
  }

  /*
   * Splits the chosen replacements into the part that is a true exchange and the part that is a
   * purchase.
   *
   * One unit back for one unit returned is an exchange: it goes on the Return, and Shopify does
   * not collect money for it. Anything beyond that count is simply extra product, so it has to go
   * through the cart to actually be paid for — putting it on the Return instead would ship goods
   * nobody was charged for.
   */
  function splitReplacementUnits() {
    var allowance = totalQty(state.items);
    var units = [];

    allPicks().forEach(function (pick) {
      var qty = pick.qty || 1;
      for (var i = 0; i < qty; i++) units.push(pick);
    });

    return { exchange: units.slice(0, allowance), surplus: units.slice(allowance) };
  }

  /* Collapses repeated units of the same variant back into one line. */
  function groupUnits(units) {
    var order = [];
    var byVariant = {};

    units.forEach(function (unit) {
      var id = String(unit.variantGid || unit.variantId);
      if (!byVariant[id]) {
        byVariant[id] = { pick: unit, quantity: 0 };
        order.push(byVariant[id]);
      }
      byVariant[id].quantity += 1;
    });

    return order;
  }

  function differenceCents() {
    return Core.differenceCents(state.items, state.picks, state.extras, multiBuyContext());
  }

  /*
   * Multi-buy pricing for the ESTIMATE ONLY.
   *
   * The real money is Shopify's to decide — the balance is paid through the real cart, so the
   * discount function prices it there. This exists so the figure the portal shows beforehand is
   * the one the shopper is about to be charged, rather than full list price.
   *
   * `multiBuyPriceCents` comes from returns-portal-product-json.liquid, which derives it exactly
   * as layout/theme.liquid's own multi-buy block does, so the portal quotes the same number the
   * PDP and cart already show for that garment. A product with no offer resolves to null, and
   * Core then leaves its price alone.
   */
  function multiBuyContext() {
    return {
      threshold: 2,
      priceFor: function (entry) {
        if (!entry) return null;
        var product = productById(entry.productId);
        if (!product || !product.multiBuyEligible) return null;
        var cents = product.multiBuyPriceCents;
        return typeof cents === 'number' && cents > 0 ? cents : null;
      }
    };
  }

  /*
   * MIX & MATCH ON THE INSTANT DISPATCH OFFER (client, Kyle, 2026-09-02: "mirror the current PDP
   * pricing: original price in grey and crossed out, with the Mix & Match price clearly displayed
   * in red. It should be obvious that they already qualify.")
   *
   * The rule these two functions enforce is the one this file has been bitten by twice already:
   * NEVER SHOW A PRICE CHECKOUT WILL NOT HONOUR. Mix & Match is a Shopify Function discount that
   * needs `threshold` eligible units in the cart, and the cart is where the balance is actually
   * paid — so the offer is shown exactly when adding this unit would reach that threshold, and
   * list price is shown when it would not.
   *
   * Kyle also asked that an Instant Dispatch shopper qualify WITHOUT adding a second item. That is
   * a discount-configuration change in Shopify (the Function's own quantity condition), not a
   * theme change: dropping the threshold here alone would quote a price the checkout then refuses
   * to give, which is the "shown £94, charged £116" complaint from 2026-09-01 in reverse — and
   * worse, because it is the shopper who would be out of pocket. Once the discount is configured
   * to qualify these carts, `threshold` in multiBuyContext() is the single value to change and
   * both this display and the balance estimate follow it.
   */
  function eligibleChargeableUnits() {
    var context = multiBuyContext();
    var units = splitReplacementUnits().surplus.concat(state.extras || []);
    return units.filter(function (unit) {
      return context.priceFor(unit) != null;
    }).length;
  }

  /* What one more unit of this product would actually cost, and whether that is the offer price. */
  function priceQuote(product, variant) {
    var list = Number(variant && variant.price) || 0;
    var context = multiBuyContext();
    var offer = product ? context.priceFor({ productId: product.id }) : null;

    // An offer that is not below list is not an offer; same guard Core.applyMultiBuy carries.
    if (offer == null || offer >= list || !list) return { price: list, multiBuy: false };
    if (eligibleChargeableUnits() + 1 < (context.threshold || 2)) return { price: list, multiBuy: false };

    return { price: offer, was: list, multiBuy: true };
  }

  /*
   * Paints a price element as either a plain figure or the multi-buy pair. Built as nodes rather
   * than innerHTML so the label and the two figures each keep their own class, which is what lets
   * the struck original stay grey while the price being charged carries the brand red.
   */
  function paintPrice(el, quote) {
    if (!el) return;
    el.textContent = '';
    el.dataset.rpMultiBuy = quote.multiBuy ? 'true' : 'false';

    if (quote.multiBuy) {
      var was = document.createElement('span');
      was.className = 'rp__price-was';
      was.textContent = money(quote.was);
      el.appendChild(was);
    }

    var now = document.createElement('span');
    now.className = 'rp__price-now';
    now.textContent = money(quote.price);
    el.appendChild(now);

    if (quote.multiBuy) {
      var tag = document.createElement('span');
      tag.className = 'rp__price-offer';
      tag.textContent = CONFIG.multiBuyLabel || 'Mix & Match';
      el.appendChild(tag);
    }
  }

  /*
   * The order/Return attribution that rides along with anything the portal puts in the cart.
   *
   * This is what replaces the draft order's `customAttributes` now that balances are paid through
   * the real cart (see the long note on the submit path for why they are). Line-item properties
   * survive the cart, the checkout and onto the resulting Order, so staff opening either the order
   * or the Return in Admin can still find the other — which was the whole point of `/payment`.
   *
   * Every key is `_`-prefixed, which is Shopify's own convention for a property hidden from the
   * customer-facing cart and checkout. The theme already relies on exactly this for `_orserio_gift`.
   *
   * Returned fresh each call rather than shared: `cart/add.js` takes one properties object per
   * line, and handing the same reference to several lines invites a later edit to mutate them all.
   */
  function returnLinkageProperties() {
    var props = {};
    if (state.returnName) props._orserio_return_name = String(state.returnName);
    if (state.returnId) props._orserio_return_id = String(state.returnId);
    if (state.order && state.order.name) props._orserio_order_name = String(state.order.name);
    if (state.ref) props._orserio_ref = String(state.ref);
    return props;
  }

  /* ------------------------------------------------------------------ cart (Instant Dispatch) */

  /*
   * Raw XHR, never fetch: Fast Bundle monkey-patches window.fetch on this store and its wrapper
   * throws on the response, leaving fetch-based cart mutations pending forever. The timeout is
   * a watchdog so the UI can never hang silently. Same helper as
   * snippets/linen-size-nudge-assets.liquid.
   */
  function cartXhr(path, payload) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(payload === undefined ? 'GET' : 'POST', (CONFIG.rootUrl || '/') + path);
      if (payload !== undefined) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.responseType = 'json';
      xhr.timeout = 10000;
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response);
        /*
         * Carry the status and Shopify's own body onto the error. Without them every cart failure
         * arrives at the UI as one indistinguishable string, so a retryable 429 and a genuine
         * "that size just sold out" 422 get the same shrug of a message — which is what the
         * client saw as "unreliable, it only starts working after a while" (Kyle, 2026-09-02).
         */
        var err = new Error(path + ' ' + xhr.status);
        err.status = xhr.status;
        err.body = xhr.response;
        err.retryAfter = Number(xhr.getResponseHeader('Retry-After')) || 0;
        reject(err);
      };
      xhr.onerror = function () {
        var err = new Error(path + ': network error');
        err.status = 0;
        reject(err);
      };
      xhr.ontimeout = function () {
        var err = new Error(path + ': timed out');
        err.status = 0;
        err.timedOut = true;
        reject(err);
      };
      xhr.send(payload === undefined ? null : JSON.stringify(payload));
    });
  }

  /*
   * Shopify's cart endpoints answer 429 when they are being hit faster than the shop's own limit,
   * and this store runs Fast Bundle and CartBite, which mutate the cart on their own schedule
   * alongside anything the portal does. A shopper tapping "+" during one of those windows got a
   * flat refusal and a "please try again" — and trying again a moment later worked, which is
   * precisely the report.
   *
   * So transient failures are retried here rather than handed to the shopper: 429, 5xx, a dropped
   * connection and a timeout. A 4xx that is not 429 is Shopify's considered answer (sold out,
   * quantity ceiling) and is passed straight through — retrying it would just delay the truth.
   */
  var CART_RETRY_BACKOFF_MS = [400, 1200];

  function cartRetryable(err) {
    var status = err && err.status;
    return status === 0 || status === 429 || (status >= 500 && status < 600);
  }

  function cartXhrRetrying(path, payload, attempt) {
    var tries = attempt || 0;
    return cartXhr(path, payload).catch(function (err) {
      if (!cartRetryable(err) || tries >= CART_RETRY_BACKOFF_MS.length) throw err;
      // Honour Retry-After when Shopify sends one; it knows better than a fixed backoff.
      var wait = err.retryAfter ? err.retryAfter * 1000 : CART_RETRY_BACKOFF_MS[tries];
      return new Promise(function (resolve) {
        window.setTimeout(resolve, wait);
      }).then(function () {
        return cartXhrRetrying(path, payload, tries + 1);
      });
    });
  }

  /*
   * Shopify's own words for why a cart add was refused ("All 1 of Apollo Polo are in your cart"),
   * which is far more use to a shopper than a generic failure. Falls back to the generic line
   * when there is nothing quotable.
   */
  function cartErrorMessage(err, fallback) {
    var body = err && err.body;
    var described = body && (body.description || body.message);
    return typeof described === 'string' && described.trim() ? described.trim() : fallback;
  }

  /*
   * REMOVING an Instant Dispatch extra from the REAL cart.
   *
   * The extra went into the cart the moment "+" was pressed, so taking it out of `state.extras`
   * alone left it sitting in the cart: the confirmation screen showed the shopper's final
   * selection while checkout still listed everything they had ever added (client, Kyle,
   * 2026-09-02 — eight items at checkout against three on screen).
   *
   * Identity is the cart LINE KEY captured from the add response, never the variant id. A shopper
   * may be buying the same garment on their own account; that line carries no `_orserio_ref`
   * property, so Shopify keeps it as a separate line and the key can never point at it.
   *
   * The quantity sent is how many extras with that key REMAIN in state, not zero: Shopify merges
   * repeat adds of the same variant+properties into one line, so a blind removal would take out
   * an extra the shopper kept.
   */
  function removeExtraFromCart(extra) {
    if (!extra) return Promise.resolve();

    var remaining = state.extras.filter(function (other) {
      return String(other.variantId) === String(extra.variantId);
    }).length;

    var byKey = extra.cartKey
      ? Promise.resolve(extra.cartKey)
      // Sessions that predate cartKey (and any add whose response shape surprised us) fall back to
      // finding the line by variant + this request's own reference.
      : cartXhr('cart.js').then(function (cart) {
        var match = ((cart && cart.items) || []).filter(function (line) {
          var props = line.properties || {};
          return String(line.variant_id) === String(extra.variantId) &&
            (!state.ref || String(props._orserio_ref || '') === String(state.ref));
        })[0];
        return match ? match.key : null;
      });

    return byKey.then(function (key) {
      if (!key) throw new Error('cart line not found for extra');
      return cartXhrRetrying('cart/change.js', { id: key, quantity: remaining });
    });
  }

  /*
   * "Start over" discards the journey, so the extras it put in the cart go with it — otherwise the
   * shopper is left holding items they abandoned, which is the same complaint as removing one
   * from the basket and finding it at checkout, just reached by a different button.
   *
   * Sequential rather than parallel: these are the same cart, and firing several mutations at once
   * is what earns a 429. Best effort by design — a failure is logged and the reset still happens,
   * because trapping someone in a wizard they asked to leave is the worse outcome.
   *
   * NOT called from "Continue shopping" after a submission: those extras are part of a request the
   * shopper actually made and may still owe money on.
   */
  function discardExtrasFromCart(extras) {
    var list = (extras || []).slice();
    if (!list.length) return Promise.resolve();

    return list.reduce(function (chain, extra) {
      return chain.then(function () {
        // remaining is read off state.extras, which the caller has already emptied, so each line
        // resolves to quantity 0.
        return removeExtraFromCart(extra).catch(function (err) {
          console.error('[rp] could not discard extra from cart', err && err.status, err);
        });
      });
    }, Promise.resolve()).then(function () {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    });
  }

  var quickShop = {
    product: null,
    colour: '',
    size: '',
    variant: null,
    trigger: null
  };

  function announce(message) {
    var region = root.querySelector('[data-rp-announcer]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 10);
  }

  function openQuickShop(button) {
    var dialog = root.querySelector('[data-rp-quick-shop]');
    var product = productById(button.dataset.rpProductId);
    if (!dialog || !product) return;

    var preferredId = button.dataset.variantId;
    var preferred = (product.variants || []).filter(function (variant) {
      return String(variant.id) === String(preferredId);
    })[0] || product.variants[0];
    if (!preferred) return;

    quickShop.product = product;
    quickShop.colour = product.colorIndex >= 0 ? preferred.options[product.colorIndex] : '';
    quickShop.size = preferred.options[product.sizeIndex] || '';
    quickShop.variant = preferred;
    quickShop.trigger = button;

    dialog.hidden = false;
    document.documentElement.classList.add('rp-quick-shop-open');
    renderQuickShop();

    var close = dialog.querySelector('.rp__quick-shop-close');
    if (close) close.focus();
  }

  function closeQuickShop() {
    var dialog = root.querySelector('[data-rp-quick-shop]');
    if (!dialog || dialog.hidden) return;

    dialog.hidden = true;
    document.documentElement.classList.remove('rp-quick-shop-open');
    if (quickShop.trigger) quickShop.trigger.focus();
  }

  /*
   * THE QUICK VIEW'S IMAGE RESOLUTION, and what was actually wrong with it.
   *
   * Reported as "image resolution is low", with the guess that "the image is being scaled down to
   * render in the small window, so quality auto-drops". Nothing is scaled down, and no browser
   * degrades an image for being in a small box. The real cause is one line of Liquid:
   * snippets/returns-portal-product-json.liquid serialises every image in the catalogue payload at
   * `image_url: width: 400`. That is the right size for the ~200 carousel and list thumbnails the
   * payload mostly feeds, and it is the file the quick view was then stretching across a 403px
   * desktop column (806 device pixels at 2x) or a full-width 390px phone sheet (1170 at 3x). Two
   * to three times upscaled — from a tag whose width/height attributes claimed 800x1000.
   *
   * Raising the payload width would have re-sized every thumbnail on every step to fix one image,
   * so the width is rewritten at the single render site that needs it. Shopify's CDN sizes off the
   * `width` query parameter, so swapping it is enough; the asset itself is unchanged.
   */
  function cdnImageWidth(src, width) {
    var url = String(src || '');
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /*
   * Deliberately NOT setImage(): every other call site in this file paints a thumbnail, where 400px
   * is correct and a srcset would be waste. Only the quick view's hero needs the rungs.
   */
  function setQuickShopImage(el, src, alt) {
    if (!el) return;
    if (!src) {
      el.removeAttribute('src');
      el.removeAttribute('srcset');
      el.hidden = true;
      return;
    }
    el.src = cdnImageWidth(src, 900);
    el.srcset = [
      cdnImageWidth(src, 600) + ' 600w',
      cdnImageWidth(src, 900) + ' 900w',
      cdnImageWidth(src, 1200) + ' 1200w',
      cdnImageWidth(src, 1600) + ' 1600w'
    ].join(', ');
    // Desktop gives it a ~26rem column; a phone gives it the full width of the sheet.
    el.sizes = '(min-width: 700px) 26rem, 100vw';
    el.alt = alt || '';
    el.hidden = false;
  }

  /*
   * Extra shots for the chosen colour. Used to render as a click-to-swap thumbnail strip beneath
   * the hero image; the client asked for the hero itself to be swipeable instead, with the strip
   * gone. The hero <img> (data-rp-quick-shop-image) stays in the DOM as the first slide — only
   * the additional shots are appended/removed here — so setQuickShopImage() keeps painting it
   * exactly as before. A product with no extra photography ends up with one slide, which simply
   * doesn't scroll.
   */
  function renderQuickShopGallery(dialog, heroSrc, variant, product) {
    var gallery = dialog.querySelector('[data-rp-quick-shop-gallery]');
    if (!gallery) return;

    var extra = ((variant && variant.images) || []).filter(function (src) {
      return src && src !== heroSrc;
    });

    var slides = gallery.querySelectorAll('.rp__quick-shop-image:not([data-rp-quick-shop-image])');
    for (var i = 0; i < slides.length; i++) slides[i].remove();

    extra.forEach(function (src, index) {
      var img = document.createElement('img');
      img.className = 'rp__quick-shop-image';
      img.decoding = 'async';
      img.loading = 'lazy';
      img.alt = product.title + ' view ' + (index + 2);
      img.src = cdnImageWidth(src, 900);
      img.srcset = [
        cdnImageWidth(src, 600) + ' 600w',
        cdnImageWidth(src, 900) + ' 900w',
        cdnImageWidth(src, 1200) + ' 1200w',
        cdnImageWidth(src, 1600) + ' 1600w'
      ].join(', ');
      img.sizes = '(min-width: 700px) 26rem, 100vw';
      gallery.appendChild(img);
    });

    // A previous product/colour may have left the gallery scrolled mid-way; every fresh render
    // (open, or a colour swap) should start back on the hero slide.
    gallery.scrollLeft = 0;

    // The scroll listener only needs binding once per dialog; re-render just re-syncs it.
    if (!gallery.dataset.rpGalleryNavBound) {
      gallery.dataset.rpGalleryNavBound = 'true';
      gallery.addEventListener('scroll', function () {
        syncQuickShopGalleryNav(dialog);
      }, { passive: true });
    }
    syncQuickShopGalleryNav(dialog);
  }

  /*
   * Swiping alone gave no visible cue the hero image could scroll, so it grew prev/next arrows.
   * Hidden entirely for a single-shot variant (nothing to scroll to), and each end disables itself
   * once the gallery is already scrolled there, rather than wrapping around.
   */
  function syncQuickShopGalleryNav(dialog) {
    var gallery = dialog.querySelector('[data-rp-quick-shop-gallery]');
    var prev = dialog.querySelector('[data-rp-quick-shop-prev]');
    var next = dialog.querySelector('[data-rp-quick-shop-next]');
    if (!gallery || !prev || !next) return;

    var multiple = gallery.querySelectorAll('.rp__quick-shop-image').length > 1;
    prev.hidden = next.hidden = !multiple;
    if (!multiple) return;

    var max = gallery.scrollWidth - gallery.clientWidth;
    prev.disabled = gallery.scrollLeft <= 1;
    next.disabled = gallery.scrollLeft >= max - 1;
  }

  function scrollQuickShopGallery(gallery, direction) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollBy({ left: gallery.clientWidth * direction, behavior: reduced ? 'auto' : 'smooth' });
  }

  function renderQuickShop() {
    var dialog = root.querySelector('[data-rp-quick-shop]');
    var product = quickShop.product;
    if (!dialog || !product) return;

    quickShop.variant = Core.variantForOptions(product, quickShop.colour, quickShop.size);

    var image = dialog.querySelector('[data-rp-quick-shop-image]');
    var title = dialog.querySelector('[data-rp-quick-shop-title]');
    var price = dialog.querySelector('[data-rp-quick-shop-price]');
    var was = dialog.querySelector('[data-rp-quick-shop-was]');
    var copy = dialog.querySelector('[data-rp-quick-shop-copy]');
    var add = dialog.querySelector('[data-rp-quick-shop-add]');
    var activeVariant = quickShop.variant || product.variants[0];
    var heroSrc = activeVariant && (activeVariant.image || product.featuredImage);

    setQuickShopImage(image, heroSrc, product.title);
    renderQuickShopGallery(dialog, heroSrc, activeVariant, product);
    if (title) title.textContent = product.title;

    /*
     * MIX & MATCH, where adding this unit would earn it: the price the shopper would otherwise
     * pay goes grey and struck through, the offer price carries the brand red, and a small label
     * names the offer so it is obvious they already qualify (client, Kyle, 2026-09-02).
     *
     * `quote.multiBuy` false means the offer genuinely would not apply at checkout, so the dialog
     * shows the plain price rather than a discount the shopper would not receive.
     */
    var quote = priceQuote(product, activeVariant);
    if (price) {
      if (activeVariant) paintPrice(price, quote);
      else price.textContent = '';
    }
    if (copy) {
      copy.textContent = product.description || '';
      copy.hidden = !product.description;
    }

    /*
     * The struck-through original. Orserio's sale is a CATALOG PRICE CUT — compare-at holds the old
     * price, price holds the sale price, and Shopify creates no discount application — so there is
     * nothing to derive by comparing an original line price against a final one. It is the
     * variant's own `compareAtPrice` or it is nothing, and it shows only when strictly above what
     * is actually being charged, so a stale compare-at can never print a fake saving.
     */
    if (was) {
      var wasPrice = Number(activeVariant && activeVariant.compareAtPrice) || 0;
      /*
       * Suppressed while the multi-buy pair is on screen: paintPrice() already draws a struck
       * original there, and two struck figures beside one price is unreadable. The offer is the
       * better story anyway — it is the number the shopper is about to be charged.
       */
      var onSale = !!activeVariant && wasPrice > Number(activeVariant.price) && !quote.multiBuy;
      was.textContent = onSale ? money(wasPrice) : '';
      was.hidden = !onSale;
    }

    if (add) add.disabled = !quickShop.variant;

    var colourWrap = dialog.querySelector('[data-rp-quick-shop-colours]');
    if (colourWrap) colourWrap.hidden = product.colorIndex < 0;

    /*
     * The colour NAME as text, beside the swatches. A swatch alone cannot say whether this is
     * Ionian Sky or Harbour Mist. Deliberately NOT run through Core.sizeToken(), which parses a
     * leading token and would render "Ionian Sky" as "Ionian".
     */
    var colourName = dialog.querySelector('[data-rp-quick-shop-colour-name]');
    if (colourName) {
      var chosenColour = quickShop.colour ||
        (activeVariant ? variantColour(product, activeVariant) : '');
      colourName.textContent = chosenColour || '';
      colourName.hidden = product.colorIndex < 0 || !chosenColour;
    }

    renderQuickShopOptions(
      dialog.querySelector('[data-rp-quick-shop-colour-list]'),
      Core.availableOptionValues(product, product.colorIndex),
      'colour'
    );
    renderQuickShopOptions(
      dialog.querySelector('[data-rp-quick-shop-size-list]'),
      Core.availableOptionValues(product, product.sizeIndex),
      'size'
    );
  }

  /*
   * A CSS colour for a colour option name. Orserio's palette is small and its names are brand
   * words rather than CSS colours ("Ionian Sky", "Mariner Heather"), so the known ones are mapped
   * explicitly and anything else falls back to the browser's own parsing. Returns '' when neither
   * works, and the caller then shows the name as text — better an honest word than a wrong colour.
   */
  function normaliseSwatchName(value) {
    return String(value || '').trim().toLowerCase();
  }

  function legacySwatchMap(raw) {
    var map = {};
    String(raw || '').split(/\r?\n/).forEach(function (line) {
      var split = line.indexOf(':');
      if (split < 1) return;
      map[normaliseSwatchName(line.slice(0, split))] = line.slice(split + 1).trim();
    });
    return map;
  }

  var STOREFRONT_SWATCHES = legacySwatchMap(CONFIG.colorSwatchConfig);

  function swatchColour(value) {
    var key = normaliseSwatchName(value);
    var optionSwatches = (quickShop.product && quickShop.product.colorSwatches) || [];
    for (var i = 0; i < optionSwatches.length; i++) {
      if (normaliseSwatchName(optionSwatches[i].value) !== key) continue;
      if (optionSwatches[i].imageSrc) {
        return {
          fill: 'url("' + optionSwatches[i].imageSrc + '")',
          ring: optionSwatches[i].color || STOREFRONT_SWATCHES[key] || 'var(--rp-line)'
        };
      }
      if (optionSwatches[i].color) {
        return { fill: optionSwatches[i].color, ring: optionSwatches[i].color };
      }
    }

    if (STOREFRONT_SWATCHES[key]) {
      var configured = STOREFRONT_SWATCHES[key];
      return {
        fill: configured,
        ring: /^url\(/i.test(configured) ? 'var(--rp-line)' : configured
      };
    }

    // Let the browser decide whether the raw name is a colour it knows.
    var probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = String(value || '').trim().toLowerCase();
    return probe.style.color ? { fill: probe.style.color, ring: probe.style.color } : null;
  }

  function renderQuickShopOptions(list, values, type) {
    if (!list) return;
    list.innerHTML = '';

    values.forEach(function (value) {
      var button = document.createElement('button');
      var isColour = type === 'colour';
      var index = isColour ? quickShop.product.colorIndex : quickShop.product.sizeIndex;
      var selected = (isColour ? quickShop.colour : quickShop.size) === value;

      button.type = 'button';
      button.className = 'rp__quick-shop-option';
      button.dataset.rpQuickOption = type;
      button.dataset.rpValue = value;

      /*
       * Colours read as swatches, not words: "Ionian Sky" and "Mariner Heather" tell a shopper
       * nothing on their own. The name stays as the accessible label so it is still announced and
       * still searchable, and any colour we can't resolve falls back to plain text rather than
       * showing an empty chip.
       */
      if (isColour) {
        var swatch = swatchColour(value);
        if (swatch) {
          button.classList.add('rp__quick-shop-option--swatch');
          button.style.setProperty('--rp-swatch', swatch.fill);
          button.style.setProperty('--rp-swatch-ring', swatch.ring);
          button.setAttribute('aria-label', value);
          button.title = value;
        } else {
          button.textContent = value;
        }
      } else {
        /*
         * Sizes show the PARSED token, the same as every other screen in the portal. The raw
         * option value is a sentence — `S (Best Up to 5'9", Size Up by Build, In Stock)` — and a
         * 45-character chip fills a whole row on a phone, so four sizes became four stacked rows
         * and pushed the rest of the sheet below the fold. The full text stays as the accessible
         * label and the tooltip, so the height guidance is not lost.
         */
        var token = Core.sizeToken(value);
        button.textContent = token || value;
        if (token && token !== value) {
          button.setAttribute('aria-label', value);
          button.title = value;
        }
      }
      button.disabled = !Core.optionIsAvailable(
        quickShop.product,
        index,
        value,
        -1,
        ''
      );
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      list.appendChild(button);
    });
  }

  function showQuickShopError(message) {
    var error = root.querySelector('[data-rp-quick-shop-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  function hideQuickShopError() {
    var error = root.querySelector('[data-rp-quick-shop-error]');
    if (error) error.hidden = true;
  }

  function submitQuickShop(button) {
    if (!quickShop.variant || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    hideQuickShopError();

    /*
     * A plain native add-to-cart, exactly like the storefront's own quick buy. Instant Dispatch
     * extras are ordinary product the shopper is buying, so they belong in the real cart where
     * Shopify's own discount functions price them — Mix & Match included. Nothing here computes
     * or overrides a price.
     */
    cartXhrRetrying('cart/add.js', {
      items: [{
        id: quickShop.variant.id,
        quantity: 1,
        properties: returnLinkageProperties()
      }]
    })
      .then(function (added) {
        var extra = Core.extraFromSelection(
          quickShop.product,
          quickShop.variant,
          quickShop.colour,
          quickShop.size
        );
        /*
         * The cart line this add produced, kept so removing the extra later can take the RIGHT
         * line back out — see removeExtraFromCart. `cart/add.js` answers with the added lines;
         * a missing key is not fatal, removal falls back to a lookup.
         */
        var line = added && added.items && added.items[0];
        if (line && line.key) extra.cartKey = line.key;
        state.extras.push(extra);
        save();
        updateSticky();
        renderDispatch();
        driveTruck();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        announce('Added to your order');
        button.removeAttribute('aria-busy');
        closeQuickShop();
      })
      .catch(function (err) {
        console.error('[rp] cart add failed', err && err.status, err && err.body, err);
        // Shopify's own explanation where it gave one — "All 1 of X are in your cart" tells the
        // shopper something actionable; the generic line tells them nothing.
        showQuickShopError(cartErrorMessage(err, 'We could not add that item. Please try again.'));
        button.removeAttribute('aria-busy');
      });
  }

  /*
   * Hands the journey off from the truck to the sticky bar (client, 2026-08-27: "when the truck
   * reaches the endpoint it will float and move to the truck icon below and then it lights up? The
   * animation is like when adding an item to cart, just reuse that").
   *
   * It clones the truck glyph, animates the clone, then removes it. What the shopper follows is
   * therefore the same mark landing in the status icon that lights up — one object moving, not
   * two that happen to change at once.
   *
   * It waits for the 1240ms drive to finish. The parcel handoff is now a separate first-arrival
   * animation, so the flight has no reason to wait through that earlier stage. If the drive is
   * retuned, this constant must move with it rather than becoming a disconnected magic delay.
   */
  var TRUCK_DRIVE_TOTAL_MS = 1240;
  // Kept in step with the rp-truck-receive keyframes' own duration (returns-portal.css) — sped up
  // from 1100ms 2026-08-27 alongside the keyframe redesign that removed its mid-flight stall.
  var TRUCK_RECEIVE_TOTAL_MS = 850;

  function playDispatchIntro() {
    var truck = root.querySelector('[data-rp-dispatch] .rp__truck');
    if (!truck || !Core.claimDispatchIntro(state)) return;

    save();
    truck.classList.remove('is-receiving');
    void truck.offsetWidth;
    truck.classList.add('is-receiving');

    window.setTimeout(function () {
      truck.classList.remove('is-receiving');
    }, TRUCK_RECEIVE_TOTAL_MS);
  }

  function flyTruckToStatus(truck) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    var vehicle = truck.querySelector('.rp__truck-vehicle');
    var target = root.querySelector('[data-rp-dispatch-status]');
    if (!vehicle || !target || target.hidden) return false;

    window.setTimeout(function () {
      var from = vehicle.getBoundingClientRect();
      var to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;

      /*
       * The original hides as the clone takes over, so what the shopper sees is the truck ITSELF
       * leaving rather than a copy peeling off a truck that stays put. It comes back at the start
       * of the lane once the clone has landed, ready for the next add.
       */
      vehicle.style.opacity = '0';

      var clone = vehicle.cloneNode(true);
      clone.removeAttribute('class');
      clone.removeAttribute('style');
      clone.classList.add('rp__truck-fly');
      clone.style.left = from.left + 'px';
      clone.style.top = from.top + 'px';
      clone.style.width = from.width + 'px';
      clone.style.height = from.height + 'px';
      document.body.appendChild(clone);

      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);

      var motion = clone.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          {
            transform: 'translate(' + dx * 0.5 + 'px, ' + dy * 0.35 + 'px) scale(0.8)',
            opacity: 0.95,
            offset: 0.55
          },
          { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.35)', opacity: 0 }
        ],
        { duration: 640, easing: 'cubic-bezier(0.35, 0, 0.3, 1)' }
      );

      /*
       * The icon lights up as the clone lands, not when the flight starts — the arrival is what
       * the shopper is being shown, so the two have to coincide or the state change reads as
       * unrelated to the motion.
       */
      /*
       * THE TRUCK STAYS PARKED AT THE FAR END (client, 2026-08-27: "the truck should stay at the
       * endpoint once the truck animation is triggered, it should not reset or go back to the
       * starting position"). `.is-driving` therefore survives, and `both` fill-mode holds the
       * end frame.
       *
       * Only the OPACITY is restored — the vehicle was hidden so the flying clone could read as
       * the truck itself leaving, and without this it would stay invisible at its parking spot.
       */
      var restoreTruck = function () {
        vehicle.style.opacity = '';
      };

      var land = function () {
        clone.remove();
        restoreTruck();
        target.classList.remove('is-landing');
        void target.offsetWidth;
        target.classList.add('is-landing');
        window.setTimeout(function () { target.classList.remove('is-landing'); }, 520);
      };

      motion.onfinish = land;
      // An interrupted flight must not strand the clone or leave the truck permanently hidden.
      motion.oncancel = function () { clone.remove(); restoreTruck(); };
    }, TRUCK_DRIVE_TOTAL_MS);

    return true;
  }

  /*
   * Fires the drive, and only ever from an ADD. Kept next to the flight it hands off to, because
   * the two are one sequence: the truck runs the lane, then its glyph flies to the status icon.
   *
   * Only the FIRST add of a session runs it (client, 2026-08-26: repeating it on every add read as
   * the animation "redoing itself"). Core.claimTruckDrive mirrors claimDispatchIntro's one-time
   * claim, so every add after the first is a silent no-op and the truck simply stays parked at the
   * status icon where the first flight left it.
   */
  function driveTruck() {
    var truck = root.querySelector('[data-rp-dispatch] .rp__truck');
    if (!truck || !Core.claimTruckDrive(state)) return;

    save();

    // An unusually quick Add ends the parcel handoff before the truck starts its journey.
    truck.classList.remove('is-receiving');
    // Re-adding a class that is already present does not restart a CSS animation; a real restart
    // needs remove -> forced reflow -> add.
    truck.classList.remove('is-driving');
    void truck.offsetWidth;
    truck.classList.add('is-driving');

    /*
     * `.is-driving` is deliberately never removed. `both` fill-mode holds the end frame, so the
     * truck stays parked where it arrived — including when the flight cannot run at all (reduced
     * motion, or the status icon off screen), which is why there is no fallback reset here.
     */
    flyTruckToStatus(truck);
  }

  /* ------------------------------------------------------------------ rendering */

  function tpl(name) {
    var node = root.querySelector('[data-rp-tpl="' + name + '"]');
    return node ? node.content : null;
  }

  function slot(scope, name) { return scope.querySelector('[data-rp-slot="' + name + '"]'); }

  function setImage(el, src, alt) {
    if (!el) return;
    if (src) {
      el.src = src;
      el.alt = alt || '';
      el.hidden = false;
    } else {
      el.removeAttribute('src');
      el.hidden = true;
    }
  }

  function itemLabel(item) {
    return [item.colour, item.sizeToken].filter(Boolean).join(' / ');
  }

  function renderStep(step) {
    if (step === 'orders') renderOrders();
    if (step === 'items') renderItems();
    if (step === 'replace') renderReplace();
    if (step === 'reason') renderReason();
    if (step === 'dispatch') {
      renderDispatch();
      playDispatchIntro();
    }
    if (step === 'review') renderReview();
    if (step === 'advice-detail') renderAdviceDetail();
    if (step === 'done') renderThanks();
    // After the step has painted, so titles the step sets itself (the items heading swaps between
    // "exchange" and "return") reach the masthead in their final form.
    paintMasthead(step);
    paintStepper(step);
    syncNextButtons();
  }

  /* --- masthead + progress bar --- */

  /*
   * Six screens, three markers. The wizard's own step names are unchanged — this is purely how
   * the progress is PRESENTED, because six dots made a five-minute job look like a form.
   */
  var STEP_GROUP = {
    identify: 'order',
    orders: 'order',
    items: 'items',
    replace: 'items',
    reason: 'items',
    dispatch: 'items',
    'advice-detail': 'items',
    review: 'review',
    done: 'review'
  };
  var GROUP_ORDER = ['order', 'items', 'review'];

  /* The first step of each stage that actually exists in the current path, for the stepper's
     backwards jumps. guard() still has the final say, so an illegal jump lands somewhere sane. */
  function firstStepOfGroup(group) {
    var list = stepsFor(state.path);
    for (var i = 0; i < list.length; i++) {
      if (STEP_GROUP[list[i]] === group) return list[i];
    }
    return null;
  }

  /*
   * Each step keeps its own <h2> for focus and for the accessibility tree; the burgundy band
   * shows a copy of it. Nothing is duplicated in the markup — this reads the live text — so
   * there is only ever one place to edit a step's title.
   */
  function paintMasthead(step) {
    var titleEl = root.querySelector('[data-rp-masthead-title]');
    if (!titleEl) return;

    if (MASTHEAD_DEFAULT_TITLE === null) MASTHEAD_DEFAULT_TITLE = titleEl.textContent;

    /*
     * The entry screen keeps its branded burgundy band; every wizard step after it is the small
     * plain black label (client, 2026-08-26, after reviewing the live pages: the earlier "kill the
     * hero" fix had removed the entry screen's own colour too, since both share this one element —
     * the client's "the first part should stay the same" meant literally this, not merely that the
     * TEXT was unaffected). See `.rp__masthead[data-rp-masthead-entry]` in returns-portal.css.
     */
    var masthead = titleEl.closest('.rp__masthead');
    if (masthead) masthead.dataset.rpMastheadEntry = step === 'entry' ? 'true' : 'false';

    var panel = stepEl(step, state.path);
    var stepTitle = panel && panel.querySelector('.rp__step-title');
    var stepSub = panel && panel.querySelector('.rp__step-sub');

    titleEl.textContent = stepTitle && stepTitle.textContent.trim()
      ? stepTitle.textContent.trim()
      : MASTHEAD_DEFAULT_TITLE;

    var subEl = root.querySelector('[data-rp-masthead-sub]');
    var defaultEl = root.querySelector('[data-rp-masthead-default]');
    var subText = stepSub ? stepSub.textContent.trim() : '';

    if (subEl) {
      subEl.textContent = subText;
      subEl.hidden = !subText;
    }
    /*
     * The settings intro belongs to the ENTRY screen only. Carrying it into the wizard put
     * "Finding the perfect Orserio fit is nothing to compromise on" under "Let's find your
     * order", which is exactly the kind of stacked copy the client asked us to cut — a step
     * either has its own one-line subtitle or it has none.
     */
    if (defaultEl) defaultEl.hidden = step !== 'entry' || !defaultEl.textContent.trim();
  }

  function paintStepper(step) {
    var bar = root.querySelector('[data-rp-stepper]');
    if (!bar) return;

    // Nothing to track on the landing screen, and on the advice path the three stages don't apply.
    var hidden = step === 'entry' || state.path === 'advice';
    bar.hidden = hidden;
    if (hidden) return;

    var currentIndex = GROUP_ORDER.indexOf(STEP_GROUP[step] || 'order');
    var done = step === 'done';
    var items = bar.querySelectorAll('[data-rp-stepper-item]');

    for (var i = 0; i < items.length; i++) {
      var index = GROUP_ORDER.indexOf(items[i].dataset.rpGroup);
      var stateName = 'todo';
      if (done || index < currentIndex) stateName = 'done';
      else if (index === currentIndex) stateName = 'current';

      items[i].dataset.state = stateName;
      var button = items[i].querySelector('[data-rp-stepper-go]');
      if (button) {
        // Only backwards. Jumping ahead would skip the guards that make each step answerable.
        button.disabled = stateName !== 'done' || done;
        if (stateName === 'current') button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      }
    }
  }

  /* --- items --- */

  function renderItems() {
    var list = root.querySelector('[data-rp-items-list]');
    if (!list) return;

    /*
     * Two to three words, plain (client, 2026-08-25). This assignment is why shortening the
     * Liquid alone would have changed nothing: it overwrites the markup's heading on every entry
     * to the step. The exchange/return split stays, because it is the only place this screen says
     * which path the shopper is on.
     */
    var title = root.querySelector('[data-rp-items-title]');
    if (title) {
      title.textContent = state.path === 'return'
        ? 'Item to return'
        : 'Item to exchange';
    }

    var picker = root.querySelector('[data-rp-manual-picker]');
    var empty = root.querySelector('[data-rp-nothing-returnable]');
    var useApi = state.order.source === 'api';

    if (picker) picker.hidden = useApi;
    if (empty) empty.hidden = !(useApi && state.orderItems.length === 0);

    /*
     * "Not this order?" and the window reassurance only make sense against a real order, and only
     * while there is something listed — on the nothing-returnable panel the reassurance would
     * contradict the message right above it.
     */
    var foot = root.querySelector('[data-rp-items-foot]');
    if (foot) foot.hidden = !(useApi && state.orderItems.length > 0);

    var windowNote = root.querySelector('[data-rp-items-window-note]');
    if (windowNote) {
      var days = Number(CONFIG.returnsWindowDays) || 14;
      if (!windowNote.dataset.rpTemplate) windowNote.dataset.rpTemplate = windowNote.textContent;
      windowNote.textContent = windowNote.dataset.rpTemplate.replace('{days}', days);
    }

    if (useApi) renderOrderItems(list);
    else renderManualItems(list);
  }

  function renderOrderItems(list) {
    var content = tpl('order-item');
    if (!content) return;

    list.innerHTML = '';

    state.orderItems.forEach(function (li) {
      var node = content.cloneNode(true);
      var row = node.querySelector('.rp__item');
      var key = li.fulfillmentLineItemId;
      var existing = state.items.filter(function (i) { return i.key === key; })[0];

      setImage(slot(node, 'image'), li.image, li.title);
      slot(node, 'title').textContent = li.title;
      slot(node, 'variant').textContent = [li.colour, li.size].filter(Boolean).join(' / ');
      slot(node, 'qty').textContent = existing ? existing.qty : 1;

      var check = node.querySelector('[data-rp-item-check]');
      check.checked = !!existing;
      row.dataset.rpKey = key;
      row.dataset.rpLine = JSON.stringify(lineToItem(li));

      var label = node.querySelector('[data-rp-check-label]');
      if (label) label.textContent = existing ? 'Selected' : 'Select';

      /*
       * "Free exchange already used" on a unit that itself arrived as an exchange replacement.
       * Purely informational: the item stays fully selectable and fully exchangeable, which is the
       * client's own rule ("they are allowed to exchange it but it is not free anymore").
       */
      /*
       * ONE EXCHANGE PER ITEM, FULL STOP (client, Obaid, 2026-09-04: "Once an item is exchanged
       * once / Greyed out / Cant exchange it again / You don't need to worry about pricing").
       *
       * This REPLACES the earlier rule, where a second exchange was allowed but chargeable. The
       * charge is gone with it — nothing to price if nothing is permitted.
       *
       * ONLY ON THE EXCHANGE PATH. A shopper may still send the item back for a refund: what the
       * client blocked is swapping it a second time, not returning it. Disabling it everywhere
       * would quietly strip a right nobody asked to remove, and would push those people into
       * emailing support, which is the process this portal replaced.
       */
      var spent = li.fromExchange && state.path === 'exchange';

      var flag = slot(node, 'exchange-flag');
      if (flag) {
        var flagText = spent ? (root.dataset.rpExchangeUsed || '') : '';
        if (flagText) {
          flag.textContent = flagText;
          flag.hidden = false;
        } else {
          flag.hidden = true;
        }
      }

      if (spent) {
        row.classList.add('rp__item--spent');
        check.disabled = true;
        check.checked = false;
        // The whole row is a <label>; without this the disabled checkbox still gets toggled by a
        // click anywhere on the card in some browsers.
        row.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = root.dataset.rpExchangeUsedShort || 'Unavailable';
        var qty = node.querySelector('[data-rp-qty]');
        if (qty) qty.hidden = true;
      }

      list.appendChild(node);
    });
  }

  /*
   * Normalises an API line item into the shape every later step expects. `fulfillmentLineItemId`
   * is the field that actually creates the return, and `price` here is what the shopper really
   * paid after discounts — something the theme alone could only ever guess at.
   */
  function lineToItem(li) {
    return {
      key: li.fulfillmentLineItemId,
      fulfillmentLineItemId: li.fulfillmentLineItemId,
      // Every fulfillment line this merged row stands for, so submit can split the requested
      // quantity back across them. See mergeReturnableLines().
      sources: li.sources || [{ fulfillmentLineItemId: li.fulfillmentLineItemId, quantity: li.quantity }],
      variantGid: li.variantId,
      productHandle: li.productHandle,
      build: buildOf(li.tags),
      type: li.type,
      sizeToken: sizeToken(li.size),
      sizeRaw: li.size,
      colour: li.colour,
      title: li.title,
      qty: 1,
      maxQty: li.quantity,
      price: li.price,
      // The currency this line was actually CHARGED in, carried through so the review and
      // confirmation screens can print it as the shopper paid it rather than in whatever
      // currency they happen to be browsing today. See money().
      currency: li.currency || null,
      // True = this unit is itself an earlier exchange's replacement, so its free exchange is
      // spent. Carried into item state (not just the picker) so later steps can say so without
      // re-reading the order. The API is the authority on this; the theme never computes it.
      fromExchange: !!li.fromExchange,
      image: li.image
    };
  }

  /*
   * Whether an order can still be exchanged, and why not when it can't.
   *
   * The window runs from DELIVERY. Shopify only knows the delivery date when the carrier reported
   * it, so `deliveredAt` arrives flagged `deliveryEstimated` whenever we had to infer it from the
   * dispatch date. Where we are guessing we are deliberately LENIENT — a shopper must never be
   * refused because of a date we invented — so an estimated date also has the assumed transit
   * time added before the clock starts.
   *
   * With no dispatch date at all (unfulfilled, or the API predates these fields) the order stays
   * selectable: silently blocking someone on missing data is the worse failure.
   */
  function orderEligibility(order) {
    var windowDays = Number(CONFIG.returnsWindowDays) || 14;
    var basis = order.deliveredAt || order.shippedAt;
    if (!basis) return { eligible: true };

    var start = new Date(basis);
    if (isNaN(start.getTime())) return { eligible: true };

    if (order.deliveryEstimated) {
      var uk = String(order.countryCode || '').toUpperCase() === 'GB';
      var transit = Number(uk ? CONFIG.assumedTransitUk : CONFIG.assumedTransitIntl);
      start.setDate(start.getDate() + (isNaN(transit) ? (uk ? 3 : 10) : transit));
    }

    var deadline = new Date(start.getTime());
    deadline.setDate(deadline.getDate() + windowDays);

    if (Date.now() <= deadline.getTime()) return { eligible: true };

    var note = (CONFIG.orderExpiredNote || "Past the {days}-day window")
      .replace('{days}', windowDays);

    return { eligible: false, reason: note };
  }

  /*
   * Has this parcel actually ARRIVED? "Exchange only once status is Delivered" (client), and the
   * matching report that "two returns and one delivered order all appeared" when only the
   * delivered one should have been selectable.
   *
   * The honest signal is `deliveredAt`. The API sets it from a real DELIVERED fulfillment event,
   * and failing that from a DELIVERED fulfillment displayStatus dated off dispatch — so non-null
   * means Shopify said the parcel landed, either exactly or approximately.
   *
   * THE TRAP, and why this is not simply `return !!order.deliveredAt`: the carrier does not push
   * delivery events to this store at all, so on the live shop `deliveredAt` is routinely null on
   * orders that plainly arrived weeks ago. Gating on it alone would make every order unselectable
   * and break the portal outright. Where Shopify says nothing we therefore fall back to the SAME
   * assumption the returns window itself already runs on: dispatch plus the assumed transit for
   * the destination. Past that, it has arrived; before it, it has not.
   *
   * An order with no dispatch date has certainly not been delivered, which is what finally closes
   * the "no date at all stays selectable" hole in orderEligibility().
   */
  function orderDelivered(order) {
    if (!order) return false;
    if (order.deliveredAt) return true;

    var shipped = order.shippedAt ? new Date(order.shippedAt) : null;
    if (!shipped || isNaN(shipped.getTime())) return false;

    var uk = String(order.countryCode || '').toUpperCase() === 'GB';
    var transit = Number(uk ? CONFIG.assumedTransitUk : CONFIG.assumedTransitIntl);
    if (isNaN(transit)) transit = uk ? 3 : 10;

    var arrival = new Date(shipped.getTime());
    arrival.setDate(arrival.getDate() + transit);

    return Date.now() >= arrival.getTime();
  }

  /*
   * The single "can this order actually be picked" gate, shared by every place that offers an
   * order: `renderOrders()`'s multi-order card grid AND `identifyOrder()`'s single-order
   * auto-select. Two implementations of the same rule is exactly how the replacement picker's
   * minus button and its row-click toggle drifted apart (2026-08-28) — this exists so the 14-day
   * window and the exchange-only gates can't do the same thing here.
   *
   * The exchange-only gates (undelivered, already fully returned) are unchanged from
   * `renderOrders()`'s own long-standing logic; only the date window was ever missing from the
   * single-order path.
   */
  function orderSelectable(order) {
    if (!orderEligibility(order).eligible) return false;
    if (state.path !== 'exchange') return true;
    if (typeof order.itemCount === 'number' && order.itemCount === 0) return false;
    return orderDelivered(order);
  }

  function renderOrders() {
    var list = root.querySelector('[data-rp-orders-list]');
    var content = tpl('order-card');
    if (!list || !content) return;

    list.innerHTML = '';

    state.orders.forEach(function (order) {
      var node = content.cloneNode(true);
      var radio = node.querySelector('[data-rp-order-radio]');

      var eligibility = orderEligibility(order);

      /*
       * Two extra gates, and BOTH are exchange-only. The return path keeps its existing leniency
       * because the client's note was specifically about exchanges, and because a refund on an
       * undelivered parcel is a legitimate request.
       *
       * `itemCount` is built from `currentQuantity`, i.e. ordered minus anything already returned,
       * so zero means there is nothing left to send back. The `typeof` check is deliberate: an
       * older API response with the field absent must stay selectable rather than be blocked on
       * missing data.
       *
       * `blocked` (the STRING) still needs computing here for its own sake — it picks which
       * specific reason to show on the card, which orderSelectable() below deliberately doesn't
       * know about (it only answers yes/no). `selectable` itself, though, now comes from that
       * shared function, not from re-deriving the same boolean a second time.
       */
      var exchanging = state.path === 'exchange';
      var delivered = orderDelivered(order);
      var emptied = typeof order.itemCount === 'number' && order.itemCount === 0;

      var blocked = '';
      if (exchanging && emptied) blocked = CONFIG.orderReturnedNote || '';
      else if (exchanging && !delivered) blocked = CONFIG.orderUndeliveredNote || '';

      var selectable = orderSelectable(order);

      radio.value = order.id;
      radio.checked = selectable && state.order.id === order.id;
      radio.disabled = !selectable;
      radio.dataset.rpOrder = JSON.stringify(order);

      // Greyed out AND unselectable — Shopify itself won't accept a return past the window, so
      // letting someone pick it only produces a failure three steps later.
      var label = node.querySelector('.rp__order');
      if (label && !selectable) {
        label.classList.add('rp__order--expired');
        label.setAttribute('aria-disabled', 'true');
      }

      slot(node, 'name').textContent = order.name;
      slot(node, 'date').textContent = formatDate(order.createdAt);

      /*
       * Only four thumbnails fit the card, but a silent truncation is what made a six-item order
       * look like a four-item one. Show the overflow as a "+N" chip and state the real count.
       */
      var thumbs = slot(node, 'thumbs');
      var allThumbs = order.thumbnails || [];

      allThumbs.slice(0, 4).forEach(function (src) {
        var img = document.createElement('img');
        img.className = 'rp__order-thumb';
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        thumbs.appendChild(img);
      });

      if (allThumbs.length > 4) {
        var more = document.createElement('span');
        more.className = 'rp__order-thumb rp__order-thumb--more';
        more.textContent = '+' + (allThumbs.length - 4);
        thumbs.appendChild(more);
      }

      // Fall back to the thumbnail count on older API responses that predate `itemCount`.
      var count = typeof order.itemCount === 'number' ? order.itemCount : allThumbs.length;
      slot(node, 'count').textContent = count
        ? count + (count === 1 ? ' item' : ' items')
        : '';

      /*
       * An order that cannot be picked explains itself instead of showing its fulfilment status,
       * which is not the useful fact at that point. Expiry wins over the delivery gate, because
       * "outside the window" is the harder no of the two: a parcel that arrives tomorrow is still
       * out of time.
       *
       * The reason is plain text in the existing meta line, not a notice, a tint or a badge. The
       * client's standing rule for a "no" is black text with no background: a coloured block here
       * reads as a system error rather than as a fact about the order.
       */
      var status;
      if (!eligibility.eligible) status = eligibility.reason;
      else if (blocked) status = blocked;
      else status = order.fulfillmentStatus
        ? String(order.fulfillmentStatus).toLowerCase().replace(/_/g, ' ')
        : '';
      slot(node, 'meta').textContent = status && count ? ' · ' + status : status;

      list.appendChild(node);
    });
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(document.documentElement.lang || 'en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch (err) {
      return String(iso).slice(0, 10);
    }
  }

  function renderManualItems(list) {
    var content = tpl('manual-item');
    if (!content) return;

    list.innerHTML = '';

    state.items.forEach(function (item) {
      var node = content.cloneNode(true);
      var row = node.querySelector('.rp__item');
      setImage(slot(node, 'image'), item.image, item.title);
      slot(node, 'title').textContent = item.title;
      slot(node, 'variant').textContent = itemLabel(item);
      slot(node, 'qty').textContent = item.qty;
      row.dataset.rpKey = item.key;
      list.appendChild(node);
    });
  }

  /* --- manual picker (guest) --- */

  var picker = { build: '', type: '', size: '', colour: '' };

  function renderPickerRow(which, values, active) {
    var wraps = root.querySelectorAll('[data-rp-pick="' + which + '"]');
    for (var w = 0; w < wraps.length; w++) {
      var wrap = wraps[w];
      wrap.innerHTML = '';
      values.forEach(function (value) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rp__chip';

        /*
         * Builds get a plain-language description beside the name. Poseidon/Apollo/Hercules/Zeus
         * are product names, and asking someone to pick which one they *are* reads as labelling
         * their body by a product. The name stays so they can still recognise what they own.
         */
        var described = which === 'build' ? BUILD_DESCRIPTIONS[upper(value)] : '';
        if (described) {
          btn.innerHTML = '';
          btn.appendChild(document.createTextNode(value));
          var hint = document.createElement('span');
          hint.className = 'rp__chip-hint';
          hint.textContent = described;
          btn.appendChild(hint);
        } else {
          btn.textContent = value;
        }

        btn.dataset.rpChip = which;
        btn.dataset.rpValue = value;
        btn.setAttribute('aria-pressed', value === active ? 'true' : 'false');
        wrap.appendChild(btn);
      });
      var row = wrap.closest('[data-rp-picker-row]');
      if (row) row.hidden = values.length === 0;
    }
  }

  function refreshPicker() {
    var builds = [];
    PRODUCTS.forEach(function (p) {
      p._builds.forEach(function (b) { if (builds.indexOf(b) === -1) builds.push(b); });
    });
    builds.sort(function (a, b) { return BUILD_ORDER.indexOf(a) - BUILD_ORDER.indexOf(b); });
    renderPickerRow('build', builds, picker.build);

    var types = [];
    if (picker.build) {
      PRODUCTS.forEach(function (p) {
        if (p._builds.indexOf(picker.build) !== -1 && types.indexOf(p.type) === -1) types.push(p.type);
      });
    }
    renderPickerRow('type', types, picker.type);

    var sizes = [];
    var chosen = pickerProduct();
    if (chosen) {
      chosen._sizeTokens.forEach(function (t, idx) {
        if (sizes.indexOf(t) !== -1) return;
        if (!findVariant(chosen, t, '')) return;
        sizes.push(t);
      });
    }
    renderPickerRow('size', sizes, picker.size);

    var colours = [];
    if (chosen && picker.size) colours = orderedColours(chosen, picker.size, '');
    renderPickerRow('colour', colours, picker.colour);

    var addBtn = root.querySelector('[data-rp-manual-add]');
    if (addBtn) addBtn.disabled = !(picker.build && picker.type && picker.size);
  }

  function pickerProduct() {
    if (!picker.build || !picker.type) return null;
    return productsForBuildAndType(picker.build, picker.type)[0] || null;
  }

  function addManualItem() {
    var product = pickerProduct();
    if (!product || !picker.size) return;

    var variant = findVariant(product, picker.size, picker.colour);
    var key = 'manual-' + product.id + '-' + picker.size + '-' + (picker.colour || 'any');

    if (state.items.some(function (i) { return i.key === key; })) return;

    state.items.push({
      key: key,
      variantId: variant ? variant.id : null,
      productHandle: product.handle,
      build: picker.build,
      type: product.type,
      sizeToken: picker.size,
      sizeRaw: picker.size,
      colour: picker.colour || (variant ? variantColour(product, variant) : ''),
      title: product.title,
      qty: 1,
      maxQty: 10,
      price: variant ? variant.price : 0,
      image: variant && variant.image ? variant.image : product.featuredImage
    });

    picker.size = '';
    picker.colour = '';
    refreshPicker();
    renderItems();
    syncNextButtons();
    updateSticky();
    save();
  }

  /* --- replace --- */

  function renderReplace() {
    var list = root.querySelector('[data-rp-replace-list]');
    var panelTpl = tpl('replace-panel');
    if (!list || !panelTpl) return;

    list.innerHTML = '';

    state.items.forEach(function (item, index) {
      var node = panelTpl.cloneNode(true);
      var panel = node.querySelector('[data-rp-replace-panel]');
      panel.dataset.rpKey = item.key;

      /*
       * Only when there is more than one garment in play. With a single item this read
       * "For Item 1", which names something the shopper can already see and pushed the first
       * real control further down the step. Empty collapses via :empty in the stylesheet.
       */
      slot(node, 'index').textContent = state.items.length > 1
        ? 'Item ' + (index + 1) + ' of ' + state.items.length
        : '';
      setImage(slot(node, 'image'), item.image, item.title);
      slot(node, 'title').textContent = item.title;
      /*
       * Size and colour only. This line used to lead with the build, so it read
       * "Poseidon M, White" directly beneath a product title that already starts with the
       * word Poseidon. The title carries the build now and this carries the variant.
       */
      slot(node, 'current').textContent = item.sizeToken + (item.colour ? ' · ' + item.colour : '');

      var direction = state.fit[item.key];
      var toggles = node.querySelectorAll('[data-rp-fit]');
      for (var t = 0; t < toggles.length; t++) {
        toggles[t].setAttribute('aria-pressed', toggles[t].dataset.rpFit === direction ? 'true' : 'false');
      }

      list.appendChild(node);

      if (direction) paintFitCards(panel, item, direction);
    });
  }

  /*
   * Recommendations first, then the rest of the range, one entry per variant. Cards carry every
   * field a choice does, so they drop straight into the dropdown.
   */
  function mergeChoices(cards, choices) {
    var seen = {};
    var merged = [];

    (cards || []).concat(choices || []).forEach(function (entry) {
      if (!entry || !entry.variantId) return;
      var id = String(entry.variantId);
      if (seen[id]) return;
      seen[id] = true;
      merged.push(entry);
    });

    return merged;
  }

  /*
   * Runs the engine and pours the result into the single replacement picker.
   *
   * The name is a leftover: this used to paint a row of large product cards, which the client cut
   * as clutter (2026-08-13). The ENGINE is untouched — the recommendations are identical, they
   * are simply the first rows of the picker now instead of a separate visual feed above it.
   * `panel._rpCards` therefore still means "what the engine answered", and is what marks those
   * rows as recommendations and orders them ahead of the rest.
   */
  /*
   * The dead end. Reached when the catalogue has nothing to offer in the direction the shopper
   * asked for: Poseidon S "too big" is the one structural case (leanest build, smallest size, no
   * leaner build to fall into), and everything else here is stock.
   *
   * Three things the replace snippet cannot do on its own, because ONE container has to serve two
   * opposite outcomes, all from the client meeting (section I):
   *
   *   - "Replace the greyed state with a proper message." The copy depends on the direction.
   *     `no_recommendation_message` is the "nothing larger" wording and is already in the markup;
   *     `no_smaller_message` is the new "nothing smaller" one and rides in on the section root.
   *   - "CTA on the dead end routes to submit a return." It used to route to sizing advice, which
   *     cannot help someone the catalogue has no smaller garment for: the advice path ends in a
   *     size suggestion, and there is no size to suggest.
   *   - "Use the same visual treatment as the out-of-14-days screen." That treatment is black text
   *     with no box, and it lives in assets/returns-portal.css next to .rp__order--expired.
   *
   * The original copy is stashed on the node the first time through rather than re-read from a
   * setting, so switching Too Big to Too Small and back restores it without a re-render.
   */
  function paintDeadEnd(panel, direction) {
    var noRecos = panel.querySelector('[data-rp-no-recos]');
    if (!noRecos) return;

    var body = noRecos.querySelector('.rp__no-recos-body');
    if (body) {
      if (body.dataset.rpDefaultCopy === undefined) body.dataset.rpDefaultCopy = body.innerHTML;
      var nothingSmaller = root.dataset.rpNoSmaller || '';
      body.innerHTML = direction === 'larger' && nothingSmaller
        ? nothingSmaller
        : body.dataset.rpDefaultCopy;
    }

    var cta = noRecos.querySelector('[data-rp-path]');
    if (cta) {
      cta.dataset.rpPath = 'return';
      if (root.dataset.rpReturnCta) cta.textContent = root.dataset.rpReturnCta;
    }

    noRecos.hidden = false;
  }

  function paintFitCards(panel, item, direction) {
    var noRecos = panel.querySelector('[data-rp-no-recos]');

    var result = recommend(item, direction);
    panel._rpCards = result.cards;

    /*
     * Every fit, so the recommendation can always be overridden — but filtered to the direction
     * the shopper asked for, so "too small" never offers something smaller. The recommendations
     * are then merged back in AHEAD of that list.
     *
     * They have to be, because the direction filter compares size letters on one ladder and
     * letters are only comparable WITHIN a build. The step up from Apollo XL is Hercules L — a
     * broader cut at a lower letter — so the filter scored the engine's own answer as "smaller"
     * and dropped it. The dropdown then held no option matching the selected pick, and a <select>
     * handed a value it has no option for silently blanks, which is why it read as unset while the
     * card above it was clearly chosen. It also meant the guided answer could not be re-selected
     * once the shopper touched the dropdown.
     *
     * The engine owns cross-build steps (`build_step_up_map`, `fit_sibling_map`); the ladder
     * filter only guards same-ladder mistakes. So anything the engine proposed always belongs in
     * the list, and the filter keeps its job for everything else.
     */
    panel._rpChoices = mergeChoices(
      result.cards,
      Core.replacementChoices(PRODUCTS, item, direction, SIZE_LADDER)
    );

    /*
     * There was a line here announcing "You chose <b>Too Small</b>, here are the recommendations:".
     * Removed with its markup (2026-08-14): it restated the button the shopper had just pressed,
     * which is directly above it and visibly pressed, and then narrated a list that appears on its
     * own. The picker's selected row carries the part that was actually information — the reason
     * the size was chosen.
     */

    if (!result.cards.length) {
      paintDeadEnd(panel, direction);
      delete state.picks[item.key];
      renderReplacementControl(panel, item, []);
      save();
      syncNextButtons();
      return;
    }

    if (noRecos) noRecos.hidden = true;

    var current = primaryPick(item.key);
    var chosen = Core.cardForVariant(panel._rpChoices, current && current.variantId) ||
      Core.chooseDefaultReplacement(result.cards, null);

    // Keep the quantity the shopper already set rather than resetting it on every re-render.
    if (chosen && current && String(chosen.variantId) === String(current.variantId)) {
      chosen.qty = current.qty || 1;
    }

    setPrimaryPick(item.key, chosen);

    renderReplacementControl(panel, item, panel._rpChoices);
    renderChosen(panel, item);

    updateSticky();
    syncNextButtons();
    save();
  }

  /* ------------------------------------------------------------------ the replacement picker */

  /*
   * A custom picker rather than a <select>, because an <option> cannot hold an image or a quantity
   * stepper and a closed <select> collapses to one plain string. The popup is a non-modal dialog
   * containing a normal list, with one product toggle and one sibling stepper per row. Everything
   * a native select gave us for free is therefore implemented below: keyboard movement, focus
   * return, outside dismissal, and announcement of the current value.
   */

  var comboSeq = 0;

  function comboOptionLabel(choice) {
    return [choice.title, choice.token].filter(Boolean).join(' - ');
  }

  /*
   * The one-line reason a row is being offered, e.g. "1 size up, moderate yet noticeable". Only
   * the engine's own answers have one; the rest of the catalogue is offered without commentary,
   * which is what keeps the guided rows distinguishable inside a single list.
   */
  function comboOptionNote(choice) {
    return choice && choice.shortNote ? choice.shortNote : '';
  }

  function renderReplacementControl(panel, item, choices) {
    var combo = panel.querySelector('[data-rp-combo]');
    var list = combo && combo.querySelector('[data-rp-combo-list]');
    var button = combo && combo.querySelector('[data-rp-combo-button]');
    var optionTpl = tpl('combo-option');
    if (!combo || !list || !button || !optionTpl) return;

    if (!combo.id) combo.id = 'rp-combo-' + (++comboSeq);
    var popup = combo.querySelector('[data-rp-combo-popup]');
    if (popup) {
      if (!popup.id) popup.id = combo.id + '-popup';
      button.setAttribute('aria-controls', popup.id);
    }

    var recommended = {};
    (panel._rpCards || []).forEach(function (card, idx) {
      // Only the engine's FIRST answer is "the recommendation"; the others are alternatives it
      // also stands behind, which is why they keep their note but not the marker.
      recommended[String(card.variantId)] = idx === 0 ? 'primary' : 'secondary';
    });

    list.innerHTML = '';
    closeCombo(combo, { focusButton: false });

    /*
     * TWO SECTIONS, not one ordered list (client, 2026-08-25): "Recommended" and "Choose from all
     * sizes", with clear visual separation, and the second one hidden until the shopper asks for
     * it. The engine is untouched — its answers are still simply the rows that come first — so the
     * recommendation stays automated rather than becoming a hardcoded pair, which the client
     * specifically noted as worth preserving.
     */
    var groupTpl = tpl('combo-group');
    var revealTpl = tpl('combo-reveal');
    var restStarted = false;

    /*
     * With no recommendations there is nothing to separate the range FROM, so the split is dropped
     * and every row shows plainly. Hiding the whole list behind "show all" would leave a shopper
     * at a catalogue dead end staring at an empty picker.
     */
    var hasRecommendations = false;
    for (var rk in recommended) {
      if (Object.prototype.hasOwnProperty.call(recommended, rk)) { hasRecommendations = true; break; }
    }

    choices.forEach(function (choice, idx) {
      var node = optionTpl.cloneNode(true);
      var option = node.querySelector('[data-rp-combo-option]');
      var optionRow = node.querySelector('[data-rp-combo-row]');
      var optionQty = node.querySelector('[data-rp-option-qty]');
      var rank = recommended[String(choice.variantId)];

      /*
       * Out of stock is shown greyed out, not removed (client, 2026-08-26). The engine never
       * proposes an unavailable variant as a recommendation, so this only ever marks rows in the
       * manual override range below it — see the `available` flag Core.replacementChoices() now
       * carries instead of silently dropping the variant.
       */
      var unavailable = choice.available === false;

      option.id = combo.id + '-option-' + idx;
      option.dataset.rpKey = item.key;
      option.dataset.rpVariantId = String(choice.variantId);
      option.dataset.rpChoice = JSON.stringify(choice);
      if (optionQty) {
        optionQty.setAttribute('aria-label', 'Quantity for ' + comboOptionLabel(choice));
      }
      if (unavailable) {
        option.dataset.rpComboUnavailable = 'true';
        option.setAttribute('aria-disabled', 'true');
        if (optionRow) optionRow.dataset.rpComboUnavailable = 'true';
        var unavailableQtyButtons = optionQty && optionQty.querySelectorAll('button');
        for (var uq = 0; unavailableQtyButtons && uq < unavailableQtyButtons.length; uq++) {
          unavailableQtyButtons[uq].disabled = true;
        }
      }

      if (rank && idx === 0 && groupTpl) {
        var head = groupTpl.cloneNode(true);
        slot(head, 'title').textContent = CONFIG.recommendedLabel || 'Recommended';
        list.appendChild(head);
      }

      /*
       * Everything the engine did not propose sits behind the reveal, and stays hidden until it is
       * pressed. `data-rp-combo-rest` is what both the toggle and the keyboard walk read, so a
       * collapsed row is never focusable.
       */
      if (!rank && hasRecommendations) {
        if (!restStarted && revealTpl) {
          restStarted = true;
          var reveal = revealTpl.cloneNode(true);
          slot(reveal, 'label').textContent = CONFIG.revealLabel || 'Show all sizes';
          list.appendChild(reveal);
        }
        if (optionRow) {
          optionRow.dataset.rpComboRest = 'true';
          optionRow.hidden = true;
        }
      }

      if (rank === 'primary') option.dataset.rpComboRecommended = 'true';

      setImage(node.querySelector('[data-rp-slot="image"]'), choice.image, choice.title);
      slot(node, 'title').textContent = comboOptionLabel(choice);

      var noteEl = slot(node, 'note');
      // "Out of stock" always wins over a fit-reason note, which in practice never collides:
      // the engine only recommends available variants, so an unavailable row is never one of them.
      var note = unavailable ? (CONFIG.outOfStockLabel || 'Out of stock') : comboOptionNote(choice);
      if (noteEl) {
        noteEl.textContent = note;
        noteEl.hidden = !note;
      }

      var priceEl = slot(node, 'price');
      if (priceEl) priceEl.textContent = money(choice.price);

      list.appendChild(node);
    });

    button.disabled = !choices.length;
    syncReplacementControl(panel, item.key);
  }

  /*
   * Reflects the current pick onto the closed button and the ticked row. Called after any change
   * of selection, wherever it came from.
   *
   * A native <select> handed a value it has no option for silently blanks, which is the bug the
   * `mergeChoices` call in paintFitCards exists to prevent. The picker inherits the same hazard
   * in a different shape: with nothing matching, the button would fall back to the placeholder
   * while a pick was plainly set. So a pick that is not in the list is still SHOWN on the button,
   * rather than being silently dropped.
   */
  function syncReplacementControl(panel, itemKey) {
    var combo = panel.querySelector('[data-rp-combo]');
    if (!combo) return;

    var current = primaryPick(itemKey);
    syncPrimaryQty(panel, itemKey, current);
    var currentId = current ? String(current.variantId) : '';
    var options = combo.querySelectorAll('[data-rp-combo-option]');
    var matched = null;

    /*
     * EVERY chosen size is ticked, not just the first one — the picker is multi-select now, so a
     * single tick would claim the shopper had picked one thing when they had picked several. The
     * closed button still leads with the first, because it has one line to work with.
     */
    var picks = picksFor(itemKey);
    for (var i = 0; i < options.length; i++) {
      var optionId = options[i].dataset.rpVariantId;
      var optionIndex = -1;
      var optionPick = null;
      for (var p = 0; p < picks.length; p++) {
        if (String(picks[p].variantId) === optionId) {
          optionIndex = p;
          optionPick = picks[p];
          break;
        }
      }
      var isTaken = optionIndex >= 0;

      options[i].setAttribute('aria-pressed', isTaken ? 'true' : 'false');
      options[i].classList.toggle('is-selected', isTaken);
      var optionRow = options[i].closest('[data-rp-combo-row]');
      if (optionRow) optionRow.classList.toggle('is-selected', isTaken);

      var optionQtyEl = optionRow && optionRow.querySelector('[data-rp-option-qty]');
      var optionQty = optionPick ? (optionPick.qty || 1) : 0;
      var optionQtyValue = optionQtyEl && optionQtyEl.querySelector('[data-rp-slot="option-qty"]');
      if (optionQtyValue) optionQtyValue.textContent = optionQty;

      var optionDown = optionQtyEl && optionQtyEl.querySelector('[data-rp-option-qty-step="down"]');
      var optionUp = optionQtyEl && optionQtyEl.querySelector('[data-rp-option-qty-step="up"]');
      var optionUnavailable = options[i].dataset.rpComboUnavailable === 'true';
      if (optionDown) {
        /*
         * Same rule as Core.adjustReplacementQuantity, and it has to be — this is the DOM-level
         * gate on the exact button that logic decides for. The first fix (2026-08-28) only
         * touched the logic; this render-time `disabled` check carried the identical mistake
         * (`optionIndex === 0` instead of "is this the only pick left") and a disabled button
         * never fires a click at all, so the corrected logic never got the chance to run. Reported
         * back as "still the same bug" — it was, just one layer further out.
         */
        optionDown.disabled = optionUnavailable || optionQty === 0 ||
          (picks.length < 2 && optionQty <= 1);
      }
      if (optionUp) optionUp.disabled = optionUnavailable;
      // Superseded by the tick now that every taken row carries one; kept off so the two cannot
      // both describe the same row in different words.
      options[i].classList.toggle('is-taken', false);
      if (currentId !== '' && optionId === currentId) matched = options[i];
    }

    var valueEl = combo.querySelector('[data-rp-slot="value"]');
    var noteEl = combo.querySelector('[data-rp-combo-button] [data-rp-slot="note"]');
    var figure = combo.querySelector('[data-rp-combo-figure]');
    var img = figure && figure.querySelector('img');

    if (!current) {
      if (valueEl) valueEl.textContent = 'Choose your replacement';
      if (noteEl) { noteEl.textContent = ''; noteEl.hidden = true; }
      if (figure) figure.hidden = true;
      return;
    }

    // Prefer the row's own data so the button and the list can never disagree; fall back to the
    // pick itself for the not-in-list case described above.
    var source = matched ? JSON.parse(matched.dataset.rpChoice || '{}') : current;

    // The open picker and sticky basket already show the full selection. The old "+2 more"
    // suffix was ambiguous, so the closed control names only its lead replacement.
    if (valueEl) valueEl.textContent = comboOptionLabel(source);
    if (noteEl) {
      var note = comboOptionNote(source);
      noteEl.textContent = note;
      noteEl.hidden = !note;
    }
    if (figure && img) {
      setImage(img, source.image, source.title);
      figure.hidden = !source.image;
    }
  }

  /*
   * The merged quantity control beside the combo button (client, 2026-08-25). It exists only for
   * the PRIMARY pick — every step that changes that pick (a fresh recommendation, picking a row,
   * bumping the same size again in "add" mode, a +/- click, removing an extra which promotes
   * nothing here since the primary is index 0 and never removable) runs through
   * syncReplacementControl, which is the one place `current` is already computed.
   */
  function syncPrimaryQty(panel, itemKey, current) {
    var qtyEl = panel.querySelector('[data-rp-primary-qty]');
    if (!qtyEl) return;

    // Carries the key on itself, not on a [data-rp-chosen-row] ancestor — there is no row here.
    qtyEl.dataset.rpKey = itemKey;
    qtyEl.hidden = !current;
    if (!current) return;

    var valueEl = qtyEl.querySelector('[data-rp-slot="qty"]');
    if (valueEl) valueEl.textContent = current.qty || 1;

    var down = qtyEl.querySelector('[data-rp-pick-qty="down"]');
    if (down) down.disabled = (current.qty || 1) <= 1;
  }

  function comboIsOpen(combo) {
    var button = combo.querySelector('[data-rp-combo-button]');
    return !!button && button.getAttribute('aria-expanded') === 'true';
  }

  function openCombo(combo) {
    var button = combo.querySelector('[data-rp-combo-button]');
    var popup = combo.querySelector('[data-rp-combo-popup]');
    if (!button || !popup || button.disabled) return;

    // Only one open at a time: with several returned items there is a panel each.
    closeAllCombos(combo);

    button.setAttribute('aria-expanded', 'true');
    popup.hidden = false;
    combo.classList.add('is-open');

    /* The stack around the picker owns the border since the extra sizes were merged into it, so
       the open-state highlight has to go there rather than on the button's own (now absent) edge. */
    var openStack = combo.closest('.rp__replacement-stack');
    if (openStack) openStack.classList.add('is-open');

    /*
     * The all-sizes section opens collapsed, as the client asked, EXCEPT when the shopper's own
     * current pick lives inside it. Hiding the row that carries the tick would leave the button
     * naming a size the open list appears not to contain.
     */
    var selected = combo.querySelector('[data-rp-combo-option].is-selected');
    var selectedRow = selected && selected.closest('[data-rp-combo-row]');
    setComboRestExpanded(combo, !!(selectedRow && selectedRow.dataset.rpComboRest));

    var focusTarget = (selected && !selected.hidden) ? selected : visibleComboOptions(combo)[0];
    if (focusTarget) focusTarget.focus();
  }

  function closeCombo(combo, options) {
    var button = combo.querySelector('[data-rp-combo-button]');
    var popup = combo.querySelector('[data-rp-combo-popup]');
    if (!button || !popup) return;

    var wasOpen = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', 'false');
    popup.hidden = true;
    combo.classList.remove('is-open');

    var closeStack = combo.closest('.rp__replacement-stack');
    if (closeStack) closeStack.classList.remove('is-open');

    /* Hidden by default is the requirement, so the section resets rather than remembering. */
    setComboRestExpanded(combo, false);

    // Focus only comes back when the shopper closed it themselves. Returning focus on a re-render
    // would yank the page around while they were somewhere else entirely.
    if (wasOpen && options && options.focusButton) button.focus();
  }

  function closeAllCombos(except) {
    var combos = root.querySelectorAll('[data-rp-combo]');
    for (var i = 0; i < combos.length; i++) {
      if (combos[i] !== except) closeCombo(combos[i], { focusButton: false });
    }
  }

  /* Commits a row, from a click or from Enter/Space. */
  function selectComboOption(option) {
    var combo = option.closest('[data-rp-combo]');
    var panel = option.closest('[data-rp-replace-panel]');
    if (!combo || !panel) return;

    /*
     * Shown, not removed, but never selectable: an out-of-stock row still tells the shopper the
     * size exists, but a click on it must not silently do nothing either — it announces why,
     * the way a disabled control should.
     */
    if (option.dataset.rpComboUnavailable === 'true') {
      announce('That size is currently out of stock.');
      return;
    }

    var key = option.dataset.rpKey;
    var choice = JSON.parse(option.dataset.rpChoice || '{}');
    var label = [choice.title, choice.colour, choice.token].filter(Boolean).join(', ');

    /*
     * THE PICKER IS MULTI-SELECT, AND THAT IS THE UPSELL (client, 2026-08-27). A row TOGGLES its
     * size in and out of `state.picks[key]`, which has always been a list.
     *
     * It used to replace the single chosen size, and taking a second one meant leaving the picker,
     * pressing "choose another size", and reopening the same list in a hidden "add" mode. The
     * client's verdict on that: "that process isn't as natural as it was, choosing two directly
     * from the same dropdown... this is the upsell process, we want them to choose more." The
     * add-mode flag and its button are gone; there is one list and one behaviour.
     *
     * Quantities can also be edited on the sibling stepper in each picker row. Keeping that
     * stepper beside the product toggle, rather than nesting it inside, preserves valid interactive
     * semantics and lets either control be used independently.
     */
    var list = picksFor(key).slice();
    var at = -1;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].variantId) === String(choice.variantId)) at = i;
    }

    if (at >= 0) {
      /*
       * An exchange has to receive something, so the last remaining size cannot be toggled off.
       * Refusing silently would read as a broken control, so it says why.
       */
      if (list.length < 2) {
        announce('Keep at least one replacement. Choose a different size to swap it.');
        return;
      }
      list.splice(at, 1);
      setPicks(key, list);
      announce('Removed ' + label);
    } else {
      list.push(Object.assign({}, choice, { qty: 1 }));
      setPicks(key, list);
      announce('Added ' + label);
    }

    /*
     * The popup STAYS OPEN. Multi-select is the whole point of the change, and closing after every
     * toggle would put the shopper back to one reopen per size — the friction that was just
     * removed. Escape, an outside click or the button itself still close it.
     *
     * The recommendations are deliberately NOT regenerated here. They answer "you sent back
     * Poseidon M because it was too small, here is what to try", so they are a function of the
     * RETURNED item and the direction, not of whatever is currently selected. Re-anchoring them to
     * the new choice would walk the shopper further from the garment they returned on every change.
     */
    syncReplacementControl(panel, key);
    renderChosen(panel, { key: key }, at >= 0 ? -1 : list.length - 1);

    syncNextButtons();
    updateSticky();
    save();
  }

  function adjustComboOptionQuantity(button) {
    var row = button.closest('[data-rp-combo-row]');
    var option = row && row.querySelector('[data-rp-combo-option]');
    var panel = button.closest('[data-rp-replace-panel]');
    if (!row || !option || !panel || option.dataset.rpComboUnavailable === 'true') return;

    var key = option.dataset.rpKey;
    var choice = JSON.parse(option.dataset.rpChoice || '{}');
    var list = picksFor(key).slice();
    var beforeIndex = -1;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].variantId) === String(choice.variantId)) { beforeIndex = i; break; }
    }

    var step = button.dataset.rpOptionQtyStep === 'up' ? 1 : -1;
    var next = Core.adjustReplacementQuantity(list, choice, step);
    setPicks(key, next);

    syncReplacementControl(panel, key);
    renderChosen(panel, { key: key }, step > 0 && beforeIndex < 0 ? next.length - 1 : -1);

    var current = null;
    for (var n = 0; n < next.length; n++) {
      if (String(next[n].variantId) === String(choice.variantId)) { current = next[n]; break; }
    }
    announce(current
      ? comboOptionLabel(choice) + ' quantity ' + (current.qty || 1)
      : 'Removed ' + comboOptionLabel(choice));

    syncNextButtons();
    updateSticky();
    save();
  }

  /*
   * Only the rows a shopper can actually see. A collapsed "choose from all sizes" row must never
   * be reachable with an arrow key, or keyboard focus disappears into a hidden section while the
   * list on screen stops moving.
   */
  function visibleComboOptions(combo) {
    var all = combo.querySelectorAll('[data-rp-combo-option]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var row = all[i].closest('[data-rp-combo-row]');
      if (!row || !row.hidden) out.push(all[i]);
    }
    return out;
  }

  /*
   * Reveals or re-hides the "choose from all sizes" section. The label carries the state as words
   * as well as `aria-expanded`, because the client's direction is that the sections are named, not
   * merely separated.
   */
  function setComboRestExpanded(combo, expanded) {
    var toggle = combo.querySelector('[data-rp-combo-reveal]');
    if (!toggle) return;

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    var label = toggle.querySelector('[data-rp-slot="label"]');
    if (label) {
      label.textContent = expanded
        ? (CONFIG.allSizesLabel || 'Choose from all sizes')
        : (CONFIG.revealLabel || 'Show all sizes');
    }

    var rest = combo.querySelectorAll('[data-rp-combo-rest]');
    for (var i = 0; i < rest.length; i++) rest[i].hidden = !expanded;
  }

  function comboRestIsExpanded(combo) {
    var toggle = combo.querySelector('[data-rp-combo-reveal]');
    return !!toggle && toggle.getAttribute('aria-expanded') === 'true';
  }

  function moveComboFocus(combo, from, delta) {
    var options = visibleComboOptions(combo);
    if (!options.length) return;

    var index = -1;
    for (var i = 0; i < options.length; i++) {
      if (options[i] === from) { index = i; break; }
    }

    var next = delta === 'first' ? 0
      : delta === 'last' ? options.length - 1
        : Math.min(options.length - 1, Math.max(0, index + delta));

    options[next].focus();
  }

  /*
   * ONLY the EXTRA sizes added through "choose another" (client, 2026-08-25). The primary pick
   * (index 0) used to render here too, as a second row directly beneath the combo button that
   * already named the same product and size — "a bit repetitive", the client's own words, and
   * exactly what "delete the separate quantity selector block" asked to remove. Its quantity now
   * lives beside the combo button instead (syncPrimaryQty), and never through this function.
   *
   * `enterIndex` marks the one row that was just added, so only it animates in. It is still an
   * index into the FULL pick list (0 = primary), because that is what selectComboOption already
   * has on hand — index 0 here means "the primary's own quantity was bumped", which has no row of
   * its own to animate, so it is a silent no-op rather than a special case to route around.
   */
  function renderChosen(panel, item, enterIndex) {
    var host = panel.querySelector('[data-rp-chosen]');
    var rowTpl = tpl('chosen-row');
    if (!host || !rowTpl) return;

    var list = picksFor(item.key);
    var extras = list.slice(1);

    host.innerHTML = '';
    host.hidden = extras.length === 0;

    extras.forEach(function (pick, extraIndex) {
      var realIndex = extraIndex + 1;
      var node = rowTpl.cloneNode(true);
      var row = node.querySelector('[data-rp-chosen-row]');

      if (enterIndex === realIndex) row.classList.add('rp__chosen-row--enter');
      row.dataset.rpKey = item.key;
      row.dataset.rpIndex = String(realIndex);

      setImage(slot(node, 'image'), pick.image, pick.title);
      slot(node, 'title').textContent = pick.title;
      slot(node, 'meta').textContent = [pick.colour, pick.token].filter(Boolean).join(' / ');
      slot(node, 'qty').textContent = pick.qty || 1;

      var down = node.querySelector('[data-rp-pick-qty="down"]');
      if (down) down.disabled = (pick.qty || 1) <= 1;

      // Every row rendered here IS an extra, so remove is always available — unlike the primary,
      // an exchange losing one of its extra sizes still has something else coming back.
      host.appendChild(node);
    });
  }

  /*
   * Re-renders the chosen row behind a brief busy state.
   *
   * The work is instant, so this is NOT a progress indicator — it exists because the row sits
   * below the control that changed it and can otherwise swap contents with no perceptible event,
   * which reads as "did that actually do anything?". A short settle makes the swap legible.
   * Skipped under prefers-reduced-motion, where the content just updates.
   */
  function refreshChosen(panel, itemKey) {
    var host = panel.querySelector('[data-rp-chosen]');
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!host || reduced) {
      renderChosen(panel, { key: itemKey });
      return;
    }

    window.clearTimeout(panel._rpChosenTimer);
    host.classList.add('is-loading');

    panel._rpChosenTimer = window.setTimeout(function () {
      renderChosen(panel, { key: itemKey });
      var fresh = panel.querySelector('[data-rp-chosen]');
      if (fresh) fresh.classList.remove('is-loading');
    }, 260);
  }


  function calibrate(panel, callback) {
    var status = panel.querySelector('[data-rp-calibrating]');
    window.clearTimeout(panel._rpCalibrationTimer);
    if (status) status.hidden = false;
    panel.classList.add('is-calibrating');

    panel._rpCalibrationTimer = window.setTimeout(function () {
      panel.classList.remove('is-calibrating');
      if (status) status.hidden = true;
      callback();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450);
  }

  /* --- reason (return path) --- */

  function renderReason() {
    var list = root.querySelector('[data-rp-reason-list]');
    var panelTpl = tpl('reason-panel');
    if (!list || !panelTpl) return;

    list.innerHTML = '';

    state.items.forEach(function (item, index) {
      var node = panelTpl.cloneNode(true);
      var panel = node.querySelector('[data-rp-reason-panel]');
      panel.dataset.rpKey = item.key;

      /*
       * Only when there is more than one garment in play. With a single item this read
       * "For Item 1", which names something the shopper can already see and pushed the first
       * real control further down the step. Empty collapses via :empty in the stylesheet.
       */
      slot(node, 'index').textContent = state.items.length > 1
        ? 'Item ' + (index + 1) + ' of ' + state.items.length
        : '';
      setImage(slot(node, 'image'), item.image, item.title);
      slot(node, 'title').textContent = item.title;
      /*
       * Size and colour only. This line used to lead with the build, so it read
       * "Poseidon M, White" directly beneath a product title that already starts with the
       * word Poseidon. The title carries the build now and this carries the variant.
       */
      slot(node, 'current').textContent = item.sizeToken + (item.colour ? ' · ' + item.colour : '');

      var chosen = state.reason[item.key];
      var buttons = node.querySelectorAll('[data-rp-reason]');
      for (var b = 0; b < buttons.length; b++) {
        buttons[b].setAttribute('aria-pressed', buttons[b].dataset.rpReason === chosen ? 'true' : 'false');
      }

      list.appendChild(node);

      // A reason restored from the session has to bring its upsell back with it.
      syncReasonUpsell(panel);
    });

    syncReasonNote();
  }

  /*
   * "Too small" and "Too big" are the two answers an exchange can actually solve, and they are the
   * same two directions the exchange path's own [data-rp-fit] toggle sets. Mapping one onto the
   * other is what lets a shopper cross over without re-answering anything.
   */
  var REASON_DIRECTION = {
    'Too small': 'smaller',
    'Too big': 'larger'
  };

  /*
   * The free-text box is for "Other" and nothing else.
   *
   * It used to render on every reason, which asked someone who had just pressed "Too Small" to
   * explain it in prose. The client's framing was direct: "when they select too small, do we show
   * the box? Only show it for other reasons."
   *
   * Hidden, not removed. `state.note` and the shared [data-rp-note] sync are load-bearing on the
   * sizing-advice path, where the field IS the request, so the plumbing stays intact.
   */
  function syncReasonNote() {
    var field = root.querySelector('[data-rp-reason-note-field]');
    if (!field) return;

    var wanted = false;
    for (var i = 0; i < state.items.length; i++) {
      if (state.reason[state.items[i].key] === 'Other') wanted = true;
    }

    field.hidden = !wanted;
  }

  /*
   * The inline exchange upsell, per item.
   *
   * Deliberately not a screen and not a modal: a dedicated screen was floated by the client and
   * then judged "quite aggressive". It sits in the item's own panel, directly under the answer that
   * triggered it, and it is dismissible — "Proceed with paid return" marks the panel and the offer
   * does not come back while the shopper is on this step.
   */
  function syncReasonUpsell(panel) {
    if (!panel) return;
    var upsell = panel.querySelector('[data-rp-reason-upsell]');
    if (!upsell) return;

    var direction = REASON_DIRECTION[state.reason[panel.dataset.rpKey]];
    upsell.hidden = !direction || panel.dataset.rpUpsellDismissed === 'true';
  }

  /*
   * Cross to the exchange path without asking who they are again.
   *
   * `state.order`, `state.items` and `state.token` are exactly what the identify and items steps
   * already produced, and none of them is path-specific, so switching is a change of `state.path`
   * and nothing else. The reason is carried over as the exchange path's fit direction, which is
   * what `renderReplace()` reads to paint the recommendations — so the replacement step opens with
   * its answer already generated rather than with an unanswered "How did it fit?".
   */
  function switchReasonToExchange(key) {
    var direction = REASON_DIRECTION[state.reason[key]];
    if (!direction) return;

    state.path = 'exchange';
    if (!state.ref) state.ref = makeRef();
    state.fit[key] = direction;
    save();
    announce('Switched to a complimentary exchange.');
    go('replace');
  }

  /* --- dispatch --- */

  /*
   * What Instant Dispatch can actually offer right now, and the basis it was matched from.
   *
   * Extracted so renderDispatch() and next() cannot disagree about whether the step has anything
   * on it — next() skips the step when this comes back empty, and rendering it twice from two
   * copies of the basis maths is exactly the kind of drift this file has been bitten by before.
   */
  function dispatchCards() {
    var basis = allPicks().map(function (pick) {
      return {
        build: pick.build,
        type: pick.type,
        sizeToken: pick.token,
        colour: pick.colour
      };
    });

    if (!basis.length) basis = state.items;

    var exclude = state.extras.map(function (e) { return e.variantId; })
      .concat(allPicks().map(function (pick) { return pick.variantId; }));

    return { basis: basis, cards: matchFitCards(basis, exclude) };
  }

  function renderDispatch() {
    var mount = root.querySelector('[data-rp-upsell-mount]');
    var wrap = root.querySelector('[data-rp-upsell-wrap]');
    var cardTpl = tpl('upsell-card');
    var carouselTpl = tpl('upsell-carousel');
    if (!mount || !cardTpl || !carouselTpl) return;

    /*
     * Built off-DOM and mounted at the end. theme.js's <scroll-carousel> reads
     * getComputedStyle(this.cells[0]) as soon as it is connected and observed, so connecting an
     * empty one throws. Cloning from the template leaves it un-upgraded until we append it.
     */
    var carousel = carouselTpl.cloneNode(true).querySelector('scroll-carousel');
    if (!carousel) return;

    var matched = dispatchCards();
    var basis = matched.basis;
    var cards = matched.cards;

    /*
     * The truck goes green as an ENABLED state, not as decoration: Instant Dispatch is genuinely
     * active once the shopper has added something, because that is the parcel that ships the
     * moment they post theirs. Driven off `state.extras`, which is the same list the sticky basket
     * and the amount due read, so the three can never disagree.
     */
    var dispatchBlock = root.querySelector('[data-rp-dispatch]');
    if (dispatchBlock) {
      dispatchBlock.dataset.rpDispatchActive = state.extras.length > 0 ? 'true' : 'false';
    }

    /*
     * RENDERING DOES NOT TOUCH THE TRUCK, in either direction, and both halves of that matter.
     *
     * It must not START it: this function runs on arrival, on Back/Forward, when an extra is
     * removed, and again after state is restored from sessionStorage. Inferring "an add happened"
     * from a `state.extras.length` delta here fired on all of those — the client saw the truck
     * driving the moment they reached the step.
     *
     * It must not STOP it either, which is the subtler half. Rendering runs immediately before the
     * drive on both add paths, and can run again straight after; stripping `.is-driving` there
     * cancels the animation the add just started, which is how the truck went from animating when
     * it should not, to not animating at all. `driveTruck()` owns the class start to finish.
     */

    if (wrap) wrap.hidden = cards.length === 0;

    /*
     * With nothing to offer, the WHOLE offer goes — not just the carousel.
     *
     * Hiding only the wrap is what produced the client's "choose too big for the linen and it is
     * not showing anything" (2026-08-27): the heading and this lockup both sit outside the wrap,
     * so a shopper with no matches still got "Because you chose Apollo Small, these will fit you
     * too:" over an empty space, beside a truck advertising an upgrade with nothing to add to it.
     *
     * It is reachable on a perfectly ordinary journey. A linen shirt is one ladder rung smaller
     * than a tee/polo for the same body, so crossing out of linen shifts DOWN one — and from S
     * that lands on XS, which this store does not stock. Every candidate is skipped and the match
     * is legitimately empty. See tests/returns-portal-dispatch-empty.test.mjs.
     *
     * `state.extras.length` is the other half of the condition and is NOT optional. The count also
     * falls to zero once the shopper has added every card offered — extras go to matchFitCards as
     * `exclude` — and that shopper has Instant Dispatch genuinely ACTIVE. This lockup holds the
     * truck carrying `data-rp-dispatch-active`, the only signal on the step that it is live for
     * them, so hiding on the card count alone would delete their status the moment they took the
     * offer up in full.
     */
    var nothingToOffer = cards.length === 0 && state.extras.length === 0;
    if (dispatchBlock) dispatchBlock.hidden = nothingToOffer;

    var heading = root.querySelector('[data-rp-upsell-heading]');
    if (heading) {
      // The heading names a list. It goes whenever the list is empty, added-everything included.
      heading.hidden = cards.length === 0;

      var phrase = describeSelection(basis);
      heading.innerHTML = fillTemplate(CONFIG.dispatchHeading || '', {
        build: '<strong>' + (basis[0] ? basis[0].build : '') + '</strong>',
        size: '<strong>' + phrase + '</strong>'
      });
    }

    cards.forEach(function (card) {
      var node = cardTpl.cloneNode(true);
      setImage(slot(node, 'image'), card.image, card.title);
      slot(node, 'name').textContent = card.title + (card.colour ? ' - ' + card.colour : '') + ', Size ' + card.token;
      /*
       * The card quotes what this unit would actually cost if added now — the multi-buy pair when
       * adding it reaches the threshold, list price when it does not. See priceQuote().
       */
      paintPrice(slot(node, 'price'), priceQuote(productById(card.productId), { price: card.price }));

      var openers = node.querySelectorAll('[data-rp-open-quick-shop]');
      for (var o = 0; o < openers.length; o++) {
        openers[o].dataset.rpProductId = card.productId;
        openers[o].dataset.variantId = card.variantId;
        openers[o].setAttribute('aria-label', 'Choose options for ' + card.title);
      }

      carousel.appendChild(node);
    });

    // Swap wholesale rather than clearing and refilling, so the connected element always has cells.
    mount.innerHTML = '';
    if (cards.length) mount.appendChild(carousel);
  }

  /* --- review --- */

  function renderReview() {
    var returning = root.querySelector('[data-rp-review-returning]');
    var receiving = root.querySelector('[data-rp-review-receiving]');
    var receivingCol = root.querySelector('[data-rp-review-receiving-col]');
    var lineTpl = tpl('review-line');
    if (!returning || !lineTpl) return;

    returning.innerHTML = '';
    state.items.forEach(function (item) {
      returning.appendChild(reviewLine(
        lineTpl,
        item.title,
        itemLabel(item),
        item.image,
        item.qty,
        item.price,
        // What they paid, in the currency they paid it — never the browsing currency.
        item.currency
      ));
    });

    if (receivingCol) receivingCol.hidden = state.path === 'return';

    if (receiving) {
      receiving.innerHTML = '';
      allPicks().forEach(function (pick) {
        receiving.appendChild(reviewLine(
          lineTpl,
          pick.title,
          [pick.colour, pick.token].filter(Boolean).join(' / '),
          pick.image,
          pick.qty || 1,
          pick.price
        ));
      });
      state.extras.forEach(function (extra) {
        receiving.appendChild(reviewLine(
          lineTpl,
          extra.title,
          [extra.colour, extra.token].filter(Boolean).join(' / '),
          extra.image,
          extra.qty || 1,
          extra.price
        ));
      });
    }

    /*
     * The review step no longer carries a note field (client, 2026-08-13), so there is nothing to
     * seed here. The guard stays because `state.note` is still real: the return and sizing-advice
     * paths each have their own [data-rp-note], and restoring one into this step is a one-line
     * change if the exchange path ever wants free text back.
     */
    var noteInput = root.querySelector('[data-rp-review-note-input]');
    if (noteInput) noteInput.value = state.note || '';
  }

  function reviewLine(lineTpl, title, variant, image, qty, price, currency) {
    var node = lineTpl.cloneNode(true);
    setImage(slot(node, 'image'), image, title);
    slot(node, 'title').textContent = title;
    slot(node, 'variant').textContent = variant;
    slot(node, 'qty').textContent = 'Qty ' + (qty || 1);
    slot(node, 'price').textContent = money((price || 0) * (qty || 1), currency);
    return node;
  }

  /* --- advice --- */

  function renderAdviceDetail() {
    var basis = root.querySelector('[data-rp-advice-basis]');
    if (basis) {
      basis.hidden = !state.order.name;
      basis.textContent = state.order.name ? 'Based on your order ' + state.order.name : '';
    }

    var list = root.querySelector('[data-rp-advice-items]');
    if (list && state.order.source === 'api') renderOrderItems(list);

    /*
     * Runs on every visit to this step, not just at boot, so the control is correct after
     * "Start over" clears state.photoUrl and after Back/Next revisits with a photo still
     * attached — not only on the very first page load.
     */
    setPhotoStatus(
      state.photoUrl && state.photoName ? 'Photo attached: ' + state.photoName : PHOTO_STATUS_DEFAULT
    );
    syncPhotoRemoveButton();
  }

  /* --- thanks --- */

  function renderThanks() {
    /*
     * The heading is per path where the settings give one, falling back to the shared heading the
     * snippet already rendered. The return path's own wording is the client's, and the exact word
     * matters to them: "accepted", never "confirmed" or "received" (Kyle, 2026-09-02).
     *
     * Set BEFORE paintMasthead() runs (renderStep calls this first), so the burgundy band mirrors
     * the heading the shopper is actually reading rather than the server-rendered default.
     *
     * Assigned unconditionally, never only when a path heading exists: this screen is reused
     * across requests ("Start another request"), so a return followed by an exchange would
     * otherwise leave the return's heading standing over the exchange's copy.
     */
    var titleEl = root.querySelector('[data-rp-thanks-title]');
    if (titleEl) {
      titleEl.textContent =
        (CONFIG.thanksHeadings || {})[state.path] || CONFIG.thanksHeading || titleEl.textContent;
    }

    var body = root.querySelector('[data-rp-thanks-body]');
    if (body) body.innerHTML = (CONFIG.thanks || {})[state.path] || '';

    var refWrap = root.querySelector('[data-rp-thanks-ref]');
    if (refWrap) {
      var confirmationRef = state.returnName || state.ref;
      refWrap.hidden = !confirmationRef;
      var refSlot = slot(refWrap, 'ref');
      if (refSlot) refSlot.textContent = confirmationRef || '';
    }

    renderSubmittedSummary();

    /* Photos matter for the sizing-advice path and Shopify's contact form cannot take file
       uploads, so we point at email instead. */
    var photos = root.querySelector('[data-rp-thanks-photos]');
    if (photos) {
      photos.hidden = state.path !== 'advice';
      var link = photos.querySelector('[data-rp-photo-link]');
      if (link) {
        link.href = 'mailto:' + (CONFIG.supportEmail || '') +
          '?subject=' + encodeURIComponent('Photos for ' + (state.ref || 'my sizing request')) +
          '&body=' + encodeURIComponent('Attach your photos to this email and send.');
      }
    }
  }

  function renderSubmittedSummary() {
    var summary = root.querySelector('[data-rp-submission-summary]');
    var returning = root.querySelector('[data-rp-submission-returning]');
    var receiving = root.querySelector('[data-rp-submission-receiving]');
    var receivingCol = root.querySelector('[data-rp-submission-receiving-col]');
    var lineTpl = tpl('review-line');
    var showSummary = state.submissionComplete && state.path !== 'advice';

    if (summary) summary.hidden = !showSummary;
    if (showSummary && lineTpl && returning) {
      returning.innerHTML = '';
      state.items.forEach(function (item) {
        returning.appendChild(reviewLine(
          lineTpl, item.title, itemLabel(item), item.image, item.qty, item.price, item.currency
        ));
      });
    }

    if (showSummary && lineTpl && receiving) {
      receiving.innerHTML = '';
      allPicks().forEach(function (pick) {
        receiving.appendChild(reviewLine(
          lineTpl,
          pick.title,
          [pick.colour, pick.token].filter(Boolean).join(' / '),
          pick.image,
          pick.qty || 1,
          pick.price
        ));
      });
      state.extras.forEach(function (extra) {
        receiving.appendChild(reviewLine(
          lineTpl,
          extra.title,
          [extra.colour, extra.token].filter(Boolean).join(' / '),
          extra.image,
          extra.qty || 1,
          extra.price
        ));
      });
      if (receivingCol) receivingCol.hidden = receiving.children.length === 0;
    }

    var payment = root.querySelector('[data-rp-payment-action]');
    var amount = root.querySelector('[data-rp-payment-amount]');
    var link = root.querySelector('[data-rp-payment-link]');
    var paymentError = root.querySelector('[data-rp-payment-error]');

    // Shipping is no longer selected on this page, so this is only the accepted goods balance.
    var owed = totalOwedCents();
    var owes = state.submissionComplete && owed > 0;
    if (payment) payment.hidden = !owes;
    if (amount) amount.textContent = money(owed);
    if (link) {
      /*
       * Shopify's hosted invoice for the linked draft order, once /payment has minted one. The
       * storefront-cart URL is the FALLBACK and stays until the draft-order endpoint has passed
       * its acceptance scenarios on the dev theme — the rollout guard in the design doc. It can
       * take the money but carries no linkage back to the order or the Return, which is the whole
       * reason /payment exists.
       */
      link.href = state.paymentUrl || ((CONFIG.rootUrl || '/') + 'checkout');
      link.hidden = !state.paymentReady;
    }
    if (paymentError) {
      paymentError.textContent = state.paymentError || '';
      paymentError.hidden = !state.paymentError;
    }
  }

  function totalOwedCents() {
    return state.pendingPaymentCents || 0;
  }

  /* --- sticky bar --- */

  /*
   * Writes a value and, when it actually changed, flags it briefly so CSS can draw attention.
   * The numbers in the summary bar move as a side effect of something the shopper did further up
   * the page, so without this the change is easy to miss entirely.
   */
  function bumpValue(el, value) {
    if (!el) return;

    var next = String(value);
    if (el.textContent === next) return;

    el.textContent = next;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    el.classList.remove('is-bumped');
    void el.offsetWidth; // restart the animation when the value changes again quickly
    el.classList.add('is-bumped');
  }

  /*
   * THE "RECEIVE 2" BUG, at its root.
   *
   * Every reader of the receive side enumerates `Object.keys(state.picks)` and trusts it:
   * replacementUnits() for the sticky count, allPicks() for the review list, and
   * Core.differenceCents() for the amount due. Nothing ever reconciled that key set against
   * `state.items`. Deselection cleaned up by HAND, separately, at each of the three places that
   * mutate `state.items` — so any path that missed the hand cleanup, or wrote a pick after it,
   * left a replacement behind for an item that is no longer being returned. It then counted
   * forever, and could not be taken out by hand either: stickyLines() marks index 0 non-removable,
   * because the first pick against a returned item IS the exchange for it.
   *
   * Such a pick is genuinely reachable. calibrate() runs paintFitCards() behind a 450ms
   * setTimeout; paintFitCards() ends in setPrimaryPick(item.key, chosen) against an `item` held by
   * closure; and nothing cancels that timer when the item leaves state.items, because renderItems()
   * repaints the items list and never touches the replace step's panels. Deselect inside that
   * window and the pick is written back AFTER the delete.
   *
   * Reconciling here rather than adding a fourth hand cleanup is what makes the whole class
   * impossible: paintFitCards() calls updateSticky() on the line after it writes the pick, so an
   * orphan dies in the tick it is born, and every other mutation reaches updateSticky() through
   * syncNextButtons(). One reader of `state.picks` is now the gatekeeper for all of them.
   */
  function prunePicks() {
    var live = {};
    state.items.forEach(function (item) { live[item.key] = true; });

    Object.keys(state.picks).forEach(function (key) {
      if (!live[key]) delete state.picks[key];
    });

    // The fit direction is meaningless without the garment it describes, and a stale one would
    // re-run the engine for a deselected item the next time the replace step painted.
    Object.keys(state.fit).forEach(function (key) {
      if (!live[key]) delete state.fit[key];
    });
  }

  function updateSticky() {
    prunePicks();
    paintReviewDifference();

    /*
     * "Remove the 'nothing to pay' box when the amount is zero" (client, 2026-08-25). A £0.00 line
     * announcing that nothing is owed is the portal talking about itself, and it was doing it in
     * the boxed, coloured treatment the client keeps reading as a system error. Zero is the
     * condition rather than "no extras": a surplus unit and an Instant Dispatch add can cancel
     * nothing out, so the amount is the only honest test.
     */
    var differenceBlock = root.querySelector('[data-rp-difference-block]');
    if (differenceBlock) differenceBlock.hidden = differenceCents() === 0;

    var bar = root.querySelector('[data-rp-sticky]');
    if (!bar) return;

    var hidden = state.step === 'entry' || state.step === 'done' || state.path === 'advice';
    bar.hidden = hidden;
    if (hidden) return;

    var returnCount = totalQty(state.items);
    var receiveCount = replacementUnits() + totalQty(state.extras);

    var rc = slot(bar, 'return-count');
    var vc = slot(bar, 'receive-count');
    bumpValue(rc, returnCount);
    bumpValue(vc, receiveCount);

    var difference = differenceCents();
    var differenceAmount = slot(bar, 'difference-amount');
    var differenceLabel = slot(bar, 'difference-label');
    if (differenceAmount) bumpValue(differenceAmount, money(difference));
    if (differenceLabel) {
      /*
       * Never an "estimated credit": a like-for-like swap is zero by rule, so a negative can no
       * longer arise. Showing a credit during the sale was the artefact this replaced.
       */
      if (difference === 0) differenceLabel.textContent = 'No price difference.';
      else differenceLabel.textContent = differenceLabel.dataset.rpDefaultLabel || 'Difference to be paid at checkout.';
    }

    var cta = bar.querySelector('[data-rp-primary]');
    if (cta) {
      var list = stepsFor(state.path);
      var isLast = list.indexOf(state.step) === list.length - 1;
      cta.textContent = isLast ? submitLabel() : 'Next';
      cta.disabled = !canAdvance();
    }

    renderStickyPanel(bar);
    syncDispatchStatus(bar);
  }

  /*
   * The small status echo of the dispatch step's truck, in the persistent sticky bar (client,
   * 2026-08-26): a second confirmed source gave the exact tooltip wording — "Instant Dispatch:
   * Off" / "Instant Dispatch: On" — which is what CONFIG.dispatchStatus{Off,On}Tooltip default to.
   *
   * Only meaningful on the exchange path with the step turned on; hidden everywhere else, same
   * gating the truck itself already uses (state.extras.length > 0 for "active").
   *
   * Sets BOTH `data-tooltip` (the CSS-only visual bubble's text, via `content: attr(...)`) AND
   * `aria-label` (what a screen reader actually announces) — a CSS pseudo-element's content is
   * decorative and never reaches the accessibility tree, so the label has to be kept in sync
   * separately or a screen-reader user gets a button with no name.
   */
  function syncDispatchStatus(bar) {
    var statusIcon = bar.querySelector('[data-rp-dispatch-status]');
    if (!statusIcon) return;

    var relevant = state.path === 'exchange' && !!CONFIG.dispatchEnabled;
    statusIcon.hidden = !relevant;
    if (!relevant) return;

    var active = state.extras.length > 0;
    var label = active
      ? (CONFIG.dispatchStatusOnTooltip || 'Instant Dispatch: On')
      : (CONFIG.dispatchStatusOffTooltip || 'Instant Dispatch: Off');

    statusIcon.dataset.rpDispatchActive = active ? 'true' : 'false';
    statusIcon.setAttribute('data-tooltip', label);
    statusIcon.setAttribute('aria-label', label);
  }

  /* ---------------------------------------------------------------- summary basket
   *
   * Everything on the RECEIVE side, so an Instant Dispatch extra can be taken back out. The
   * dispatch carousel drops a card once it is in the basket and the review step lists extras
   * read-only, so before this there was no way to undo an add — it just changed the amount due.
   *
   * What can be removed here is only what the shopper ADDED. The first pick against a returned
   * item is the exchange for that item; removing it would leave the item with nothing coming back,
   * and it is changed on the replacement step instead. Additional sizes beyond that first one, and
   * extras, are both fair game.
   */
  function stickyLines() {
    var lines = [];

    Object.keys(state.picks).forEach(function (key) {
      picksFor(key).forEach(function (pick, index) {
        lines.push({
          kind: 'pick',
          key: key,
          index: index,
          removable: index > 0,
          title: pick.title,
          meta: [pick.colour, pick.token].filter(Boolean).join(' / '),
          image: pick.image,
          qty: pick.qty || 1,
          price: pick.price
        });
      });
    });

    state.extras.forEach(function (extra, index) {
      lines.push({
        kind: 'extra',
        index: index,
        removable: true,
        title: extra.title,
        meta: [extra.colour, extra.token].filter(Boolean).join(' / '),
        image: extra.image,
        qty: extra.qty || 1,
        price: extra.price
      });
    });

    return lines;
  }

  function renderStickyPanel(bar) {
    var panel = bar.querySelector('[data-rp-sticky-panel]');
    var list = bar.querySelector('[data-rp-sticky-lines]');
    var lineTpl = tpl('sticky-line');
    var toggle = bar.querySelector('[data-rp-sticky-toggle]');
    if (!panel || !list || !lineTpl || !toggle) return;

    var lines = stickyLines();

    // Nothing to look at yet: the toggle goes inert rather than opening an empty drawer.
    toggle.disabled = lines.length === 0;
    if (!lines.length) { closeStickyPanel(bar); return; }

    list.innerHTML = '';
    lines.forEach(function (line) {
      var node = lineTpl.cloneNode(true);
      setImage(slot(node, 'image'), line.image, line.title);
      slot(node, 'title').textContent = line.title;
      slot(node, 'meta').textContent = line.qty > 1
        ? line.meta + ' · ×' + line.qty
        : line.meta;
      slot(node, 'price').textContent = money((line.price || 0) * line.qty);

      var remove = node.querySelector('[data-rp-sticky-remove]');
      if (remove) {
        if (line.removable) {
          remove.dataset.rpKind = line.kind;
          remove.dataset.rpIndex = line.index;
          if (line.key) remove.dataset.rpKey = line.key;
          remove.setAttribute('aria-label', 'Remove ' + line.title + ' ' + line.meta);
        } else {
          /*
           * Kept in the layout rather than removed so every row lines up on the same grid. It has
           * to stay `visibility: hidden` and not `hidden`, which is `display: none` and would
           * collapse the cell, shifting the price column on those rows only.
           */
          remove.classList.add('is-placeholder');
          remove.disabled = true;
          remove.tabIndex = -1;
          remove.setAttribute('aria-hidden', 'true');
        }
      }

      list.appendChild(node);
    });
  }

  function closeStickyPanel(bar) {
    var host = bar || root.querySelector('[data-rp-sticky]');
    if (!host) return;
    var panel = host.querySelector('[data-rp-sticky-panel]');
    var toggle = host.querySelector('[data-rp-sticky-toggle]');
    if (panel) panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function removeStickyLine(button) {
    if (button.dataset.rpKind === 'extra') {
      var removed = state.extras.splice(Number(button.dataset.rpIndex), 1)[0];
      /*
       * An Instant Dispatch extra lives in TWO places — this state and the shopper's real cart —
       * because "+" adds it to the cart immediately. Dropping it from state alone is what let
       * removed items reappear at checkout (client, Kyle, 2026-09-02).
       *
       * The state half is applied straight away so the UI stays responsive, and the cart half
       * follows. If the cart call fails the extra is put BACK into state rather than left
       * silently out of step: the screen must never promise a cart we could not deliver, and the
       * shopper can try the removal again.
       */
      removeExtraFromCart(removed)
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function (err) {
          console.error('[rp] could not remove extra from cart', err && err.status, err);
          state.extras.push(removed);
          save();
          renderStep(state.step);
          updateSticky();
          announce('We could not remove that item. Please try again.');
        });
    } else {
      var key = button.dataset.rpKey;
      var picks = picksFor(key).slice();
      picks.splice(Number(button.dataset.rpIndex), 1);
      setPicks(key, picks);
    }

    save();
    // Re-render the current step too: a removed extra becomes available in the dispatch carousel
    // again, and the review lines have to drop it.
    renderStep(state.step);
    updateSticky();
  }

  /*
   * The confirmation step's own price band. Same number as the sticky pill, given room to be
   * read — it is the last thing anyone checks before submitting, and it was previously only
   * legible in a 12px pill at the bottom of the screen.
   */
  function paintReviewDifference() {
    var amountEl = root.querySelector('[data-rp-review-difference]');
    if (!amountEl) return;

    var difference = differenceCents();
    amountEl.textContent = money(difference);

    var noteEl = root.querySelector('[data-rp-review-difference-note]');
    if (!noteEl) return;
    noteEl.textContent = difference === 0
      ? 'Nothing to pay. Your exchange is free.'
      : 'Charged at checkout for the extras you added.';
  }

  /* --- next-button gating --- */

  function canAdvance() {
    switch (state.step) {
      case 'identify':
        if (state.path === 'advice') return isEmail(state.order.email);
        // Live: both halves of the identifying pair. Fallback: an email is all we can use.
        if (!isEmail(state.order.email)) return false;
        return LIVE ? String(state.order.postcode || '').trim().length > 0 : true;
      case 'orders':
        return !!state.order.id;
      case 'items':
        return state.items.length > 0;
      case 'replace':
        return state.items.length > 0 && state.items.every(function (item) {
          return !!state.fit[item.key] && picksFor(item.key).length > 0;
        });
      case 'reason':
        return state.items.every(function (item) { return !!state.reason[item.key]; });
      case 'advice-detail':
        // A photo mid-upload holds Submit; a photo that failed or was never chosen does not.
        return String(state.note || '').trim().length > 0 && !state.photoUploading;
      case 'dispatch':
      case 'review':
        return true;
      default:
        return true;
    }
  }

  /*
   * "Submit & Pay Difference (if applicable)" ON THE EXCHANGE PATH ONLY (client, Kyle, 2026-09-05).
   * Return and sizing-advice keep plain "Submit": a return only ever refunds, an advice request
   * never charges anything, so "pay difference" would be untrue on either. `[data-rp-submit]`
   * exists only inside each path's own terminal step, so any button this touches IS the button
   * about to submit that path -- no extra "is this the last step" check needed here.
   */
  function submitLabel() {
    if (state.path === 'exchange' && root.dataset.rpExchangeSubmitCta) return root.dataset.rpExchangeSubmitCta;
    return 'Submit';
  }

  function syncNextButtons() {
    var el = stepEl(state.step, state.path);
    if (!el) return;
    var ok = canAdvance();
    var buttons = el.querySelectorAll('[data-rp-next], [data-rp-submit]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].disabled = !ok;
      if (buttons[i].hasAttribute('data-rp-submit')) buttons[i].textContent = submitLabel();
    }
    updateSticky();
  }

  function isEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  /* ------------------------------------------------------------------ input normalisers */

  /*
   * FORMAT checks only. We cannot verify either value without the Admin API, and the copy in the
   * UI says so. The point is to catch a typo before it costs the shopper a support round-trip,
   * not to imply the order was found.
   */
  function normaliseOrderNumber(raw) {
    var value = String(raw || '').trim().replace(/^orserio/i, '').trim();
    if (!value) return { value: '', hint: '' };
    var digits = value.replace(/[^0-9]/g, '');
    if (!digits) return { value: value, hint: "That doesn't look like an order number." };
    return { value: '#' + digits, hint: '' };
  }

  function normalisePostcode(raw) {
    var value = String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!value) return { value: '', hint: '' };
    var uk = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/.exec(value);
    if (uk) return { value: uk[1] + ' ' + uk[2], hint: '' };
    return { value: String(raw || '').trim().toUpperCase(), hint: '' };
  }

  /* ------------------------------------------------------------------ events */

  root.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-rp-photo-remove]')) { removeAdvicePhoto(); return; }

    /* Backwards jump from the progress bar. Only "done" stages have an enabled button, and
       go() → guard() still refuses anything the state can't support. */
    var stickyRemove = target.closest('[data-rp-sticky-remove]');
    if (stickyRemove) { removeStickyLine(stickyRemove); return; }

    var stickyToggle = target.closest('[data-rp-sticky-toggle]');
    if (stickyToggle) {
      var stickyHost = stickyToggle.closest('[data-rp-sticky]');
      var stickyPanel = stickyHost && stickyHost.querySelector('[data-rp-sticky-panel]');
      if (stickyPanel) {
        var opening = stickyPanel.hidden;
        stickyPanel.hidden = !opening;
        stickyToggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      }
      return;
    }

    // Any click outside the bar closes the basket, so it never sits over the step being worked on.
    if (!target.closest('[data-rp-sticky]')) closeStickyPanel();

    // An armed "Start over" must never sit waiting to fire under a shopper who has moved on.
    if (!target.closest('[data-rp-restart]')) disarmRestart();

    var stepperBtn = target.closest('[data-rp-stepper-go]');
    if (stepperBtn) {
      var destination = firstStepOfGroup(stepperBtn.dataset.rpStepperGo);
      if (destination) go(destination);
      return;
    }

    var pathBtn = target.closest('[data-rp-path]');
    if (pathBtn) {
      state.path = pathBtn.dataset.rpPath;
      if (!state.ref) state.ref = makeRef();
      go(stepsFor(state.path)[0] || 'entry');
      return;
    }

    /*
     * Back to the very beginning, from any step. `back()` only walks one step at a time, which is
     * a long way out of a six-step wizard once someone realises they picked the wrong order.
     * Everything already chosen is discarded — a half-kept selection against a different order is
     * worse than starting clean — so a shopper with selections has to press twice.
     *
     * THE GUARD IS A SECOND PRESS, NOT `window.confirm()`. A sandboxed iframe without
     * `allow-modals` — the Shopify theme editor preview among them — makes `confirm()` return
     * FALSE with no dialog shown, so the old code returned early and the button did nothing at
     * all, in exactly the case the guard exists for (a shopper who has something to lose). That is
     * the reported "Start over doesn't take you back to the home screen". A native OS dialog is
     * also the least on-brand chrome in the flow.
     *
     * `history: 'replace'` at depth 0, not 'push': the abandoned wizard's entries must not stay
     * behind the entry screen, or the browser's own Back button walks straight back into a journey
     * whose state has just been cleared.
     */
    var restartBtn = target.closest('[data-rp-restart]');
    if (restartBtn) {
      var hasProgress = state.items.length > 0 || Object.keys(state.picks).length > 0;
      if (hasProgress && restartBtn.dataset.rpRestartArmed !== 'true') {
        armRestart(restartBtn);
        return;
      }

      disarmRestart();
      // Captured before clearState() wipes it; the cart lines these represent go too.
      var abandonedExtras = state.extras.slice();
      clearState();
      discardExtrasFromCart(abandonedExtras);
      render('entry', { history: 'replace', historyDepth: 0 });
      announce('Starting over.');
      return;
    }

    if (target.closest('[data-rp-complete]')) {
      clearState();
      return;
    }

    if (target.closest('[data-rp-back]')) { back(); return; }
    if (target.closest('[data-rp-next]')) { next(); return; }
    if (target.closest('[data-rp-primary]')) {
      var list = stepsFor(state.path);
      if (list.indexOf(state.step) === list.length - 1) {
        var submitBtn = stepEl(state.step, state.path);
        var actual = submitBtn && submitBtn.querySelector('[data-rp-submit]');
        if (actual) actual.click();
      } else {
        next();
      }
      return;
    }

    var goBtn = target.closest('[data-rp-go]');
    if (goBtn) { go(goBtn.dataset.rpGo); return; }

    var chip = target.closest('[data-rp-chip]');
    if (chip) {
      var which = chip.dataset.rpChip;
      picker[which] = picker[which] === chip.dataset.rpValue ? '' : chip.dataset.rpValue;
      if (which === 'build') { picker.type = ''; picker.size = ''; picker.colour = ''; }
      if (which === 'type') { picker.size = ''; picker.colour = ''; }
      if (which === 'size') { picker.colour = ''; }
      refreshPicker();
      return;
    }

    if (target.closest('[data-rp-manual-add]')) { addManualItem(); return; }

    var removeBtn = target.closest('[data-rp-item-remove]');
    if (removeBtn) {
      var row = removeBtn.closest('[data-rp-key]');
      if (row) {
        state.items = state.items.filter(function (i) { return i.key !== row.dataset.rpKey; });
        delete state.picks[row.dataset.rpKey];
        delete state.fit[row.dataset.rpKey];
        renderItems();
        syncNextButtons();
        save();
      }
      return;
    }

    var qtyBtn = target.closest('[data-rp-qty-up], [data-rp-qty-down]');
    if (qtyBtn) {
      event.preventDefault();
      adjustQty(qtyBtn);
      return;
    }

    var fitBtn = target.closest('[data-rp-fit]');
    if (fitBtn) {
      var panel = fitBtn.closest('[data-rp-replace-panel]');
      if (!panel) return;
      var key = panel.dataset.rpKey;
      state.fit[key] = fitBtn.dataset.rpFit;
      delete state.picks[key];

      var siblings = panel.querySelectorAll('[data-rp-fit]');
      for (var s = 0; s < siblings.length; s++) {
        siblings[s].setAttribute('aria-pressed', siblings[s] === fitBtn ? 'true' : 'false');
      }

      var item = state.items.filter(function (i) { return i.key === key; })[0];
      syncNextButtons();
      save();
      if (item) {
        calibrate(panel, function () {
          paintFitCards(panel, item, fitBtn.dataset.rpFit);
          var announced = primaryPick(key);
          if (announced) {
            announce('Recommended replacement selected: ' + [
              announced.title,
              announced.colour,
              announced.token
            ].filter(Boolean).join(', '));
          }
        });
      }
      return;
    }

    /* --- the replacement picker --- */

    var optionQtyBtn = target.closest('[data-rp-option-qty-step]');
    if (optionQtyBtn) {
      adjustComboOptionQuantity(optionQtyBtn);
      return;
    }

    /*
     * Must come BEFORE the option branch: the reveal is a row of the same list, and letting the
     * option branch see it first would try to select a separator as a replacement.
     */
    var revealBtn = target.closest('[data-rp-combo-reveal]');
    if (revealBtn) {
      var revealCombo = revealBtn.closest('[data-rp-combo]');
      if (revealCombo) setComboRestExpanded(revealCombo, !comboRestIsExpanded(revealCombo));
      return;
    }

    var comboOption = target.closest('[data-rp-combo-option]');
    if (comboOption) {
      selectComboOption(comboOption);
      return;
    }

    var comboButton = target.closest('[data-rp-combo-button]');
    if (comboButton) {
      var ownCombo = comboButton.closest('[data-rp-combo]');
      if (comboIsOpen(ownCombo)) closeCombo(ownCombo, { focusButton: true });
      else openCombo(ownCombo);
      return;
    }

    /*
     * A click anywhere else dismisses an open list, the way a native select would.
     *
     * On mobile `[data-rp-combo-popup]` becomes a fixed, full-screen backdrop behind the bottom
     * sheet (see the phone media query in returns-portal.css) — but it is still a DESCENDANT of
     * `[data-rp-combo]` in the markup, so a tap on that backdrop always passed the `.closest`
     * check below and never reached here (client, 2026-08-27: "when it opens it cannot be closed
     * when I click outside the screen"). The backdrop itself is the only element carrying the
     * `data-rp-combo-popup` attribute directly — `.rp__combo-sheet` and everything inside it does
     * not — so checking for that exact element (not just an ancestor match) tells a genuine
     * backdrop tap apart from a tap on the dialog surface itself.
     */
    var tappedComboBackdrop = target.hasAttribute('data-rp-combo-popup');
    if (tappedComboBackdrop || !target.closest('[data-rp-combo]')) closeAllCombos(null);

    /* --- quantity, remove, and "choose another" on a chosen replacement --- */

    var qtyBtn = target.closest('[data-rp-pick-qty]');
    if (qtyBtn) {
      var qtyPanel = qtyBtn.closest('[data-rp-replace-panel]');
      if (!qtyPanel) return;

      /*
       * Two shapes of quantity control now share this one handler: an EXTRA size's own
       * [data-rp-chosen-row] (index carried on the row), or the PRIMARY pick's merged control
       * beside the combo button (index 0, key carried on the control itself since there is no row
       * to carry it — see syncPrimaryQty).
       */
      var qtyRow = qtyBtn.closest('[data-rp-chosen-row]');
      var qtyPrimary = qtyBtn.closest('[data-rp-primary-qty]');

      var qtyKey, qtyIndex;
      if (qtyRow) {
        qtyKey = qtyRow.dataset.rpKey;
        qtyIndex = Number(qtyRow.dataset.rpIndex);
      } else if (qtyPrimary) {
        qtyKey = qtyPrimary.dataset.rpKey;
        qtyIndex = 0;
      } else {
        return;
      }

      var qtyList = picksFor(qtyKey).slice();
      var qtyPick = qtyList[qtyIndex];
      if (!qtyPick) return;

      var step = qtyBtn.dataset.rpPickQty === 'up' ? 1 : -1;
      qtyPick.qty = Math.max(1, (qtyPick.qty || 1) + step);

      setPicks(qtyKey, qtyList);
      renderChosen(qtyPanel, { key: qtyKey });
      syncPrimaryQty(qtyPanel, qtyKey, primaryPick(qtyKey));
      announce(qtyPick.title + ' quantity ' + qtyPick.qty);

      updateSticky();
      save();
      return;
    }

    var removeBtn = target.closest('[data-rp-pick-remove]');
    if (removeBtn) {
      var removeRow = removeBtn.closest('[data-rp-chosen-row]');
      var removePanel = removeBtn.closest('[data-rp-replace-panel]');
      if (!removeRow || !removePanel) return;

      var removeKey = removeRow.dataset.rpKey;
      var removeList = picksFor(removeKey).slice();
      var removed = removeList.splice(Number(removeRow.dataset.rpIndex), 1)[0];

      setPicks(removeKey, removeList);
      renderChosen(removePanel, { key: removeKey });
      syncReplacementControl(removePanel, removeKey);
      if (removed) announce('Removed ' + removed.title + ' ' + removed.token);

      syncNextButtons();
      updateSticky();
      save();
      return;
    }

    /*
     * The three inline buttons the sizing upsell offers. Checked before [data-rp-reason] purely
     * for readability — the two attribute names are distinct, so neither branch can catch the
     * other's clicks.
     */
    var upsellBtn = target.closest('[data-rp-reason-upsell-action]');
    if (upsellBtn) {
      var upsellPanel = upsellBtn.closest('[data-rp-reason-panel]');
      if (!upsellPanel) return;
      var upsellKey = upsellPanel.dataset.rpKey;
      var upsellAction = upsellBtn.dataset.rpReasonUpsellAction;

      if (upsellAction === 'exchange') {
        switchReasonToExchange(upsellKey);
        return;
      }

      if (upsellAction === 'back') {
        // Un-answer the question, so the pills read as unpressed and the shopper can choose again.
        delete state.reason[upsellKey];
        delete upsellPanel.dataset.rpUpsellDismissed;
        var backSiblings = upsellPanel.querySelectorAll('[data-rp-reason]');
        for (var u = 0; u < backSiblings.length; u++) backSiblings[u].setAttribute('aria-pressed', 'false');
      } else {
        // "Proceed with paid return": keep the answer, retire the offer for this visit to the step.
        upsellPanel.dataset.rpUpsellDismissed = 'true';
      }

      syncReasonUpsell(upsellPanel);
      syncReasonNote();
      syncNextButtons();
      save();
      return;
    }

    var reasonBtn = target.closest('[data-rp-reason]');
    if (reasonBtn) {
      var reasonPanel = reasonBtn.closest('[data-rp-reason-panel]');
      if (!reasonPanel) return;
      state.reason[reasonPanel.dataset.rpKey] = reasonBtn.dataset.rpReason;
      var reasonSiblings = reasonPanel.querySelectorAll('[data-rp-reason]');
      for (var r = 0; r < reasonSiblings.length; r++) {
        reasonSiblings[r].setAttribute('aria-pressed', reasonSiblings[r] === reasonBtn ? 'true' : 'false');
      }
      /*
       * Changing the answer re-offers the exchange, so a shopper who dismissed it on "Too Small"
       * and then pressed "Too Big" is asked again rather than silently skipped.
       */
      delete reasonPanel.dataset.rpUpsellDismissed;
      syncReasonUpsell(reasonPanel);
      syncReasonNote();
      syncNextButtons();
      save();
      return;
    }

    var quickShopOpen = target.closest('[data-rp-open-quick-shop]');
    if (quickShopOpen) {
      event.preventDefault();
      openQuickShop(quickShopOpen);
      return;
    }

    if (target.closest('[data-rp-close-quick-shop]')) {
      closeQuickShop();
      return;
    }

    var galleryNav = target.closest('[data-rp-quick-shop-prev], [data-rp-quick-shop-next]');
    if (galleryNav && !galleryNav.disabled) {
      var media = galleryNav.closest('.rp__quick-shop-media');
      var gallery = media && media.querySelector('[data-rp-quick-shop-gallery]');
      if (gallery) {
        scrollQuickShopGallery(gallery, galleryNav.hasAttribute('data-rp-quick-shop-prev') ? -1 : 1);
      }
      return;
    }

    var quickOption = target.closest('[data-rp-quick-option]');
    if (quickOption && !quickOption.disabled) {
      var optionType = quickOption.dataset.rpQuickOption;
      var optionIndex = optionType === 'colour'
        ? quickShop.product.colorIndex
        : quickShop.product.sizeIndex;
      var matchingVariant = Core.variantForOption(
        quickShop.product,
        optionIndex,
        quickOption.dataset.rpValue
      );

      if (optionType === 'colour') quickShop.colour = quickOption.dataset.rpValue;
      if (optionType === 'size') quickShop.size = quickOption.dataset.rpValue;

      if (matchingVariant && !Core.variantForOptions(quickShop.product, quickShop.colour, quickShop.size)) {
        quickShop.colour = quickShop.product.colorIndex >= 0
          ? matchingVariant.options[quickShop.product.colorIndex]
          : '';
        quickShop.size = matchingVariant.options[quickShop.product.sizeIndex] || '';
      }
      hideQuickShopError();
      renderQuickShop();
      return;
    }

    var quickAdd = target.closest('[data-rp-quick-shop-add]');
    if (quickAdd) {
      submitQuickShop(quickAdd);
      return;
    }
  });

  root.addEventListener('keydown', function (event) {
    var dialog = root.querySelector('[data-rp-quick-shop]');

    /*
     * The summary basket is a disclosure, not a modal — it traps nothing and the page stays
     * usable behind it — but Escape should still shut it. Checked before the quick shop so the
     * dialog keeps priority when both happen to be open.
     */
    if (event.key === 'Escape' && (!dialog || dialog.hidden)) {
      var stickyPanel = root.querySelector('[data-rp-sticky-panel]');
      if (stickyPanel && !stickyPanel.hidden) {
        event.preventDefault();
        closeStickyPanel();
        var stickyToggle = root.querySelector('[data-rp-sticky-toggle]');
        if (stickyToggle) stickyToggle.focus();
        return;
      }
    }

    if (!dialog || dialog.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeQuickShop();
      return;
    }

    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.slice.call(dialog.querySelectorAll(
      'button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function adjustQty(button) {
    var row = button.closest('[data-rp-key]');
    if (!row) return;
    var key = row.dataset.rpKey;
    var delta = button.hasAttribute('data-rp-qty-up') ? 1 : -1;
    var item = state.items.filter(function (i) { return i.key === key; })[0];
    if (!item) return;

    var max = item.maxQty || 10;
    item.qty = Math.min(max, Math.max(1, (item.qty || 1) + delta));

    var valueEl = row.querySelector('[data-rp-slot="qty"]');
    if (valueEl) valueEl.textContent = item.qty;
    updateSticky();
    save();
  }

  /*
   * Keyboard for the replacement picker. A native <select> gave all of this for free; the moment
   * the control became custom to carry thumbnails and per-row steppers, it became ours to provide.
   * Product buttons support arrow movement and selection; Escape returns from either control.
   */
  root.addEventListener('keydown', function (event) {
    var option = event.target.closest && event.target.closest('[data-rp-combo-option]');
    if (option) {
      var optionCombo = option.closest('[data-rp-combo]');
      switch (event.key) {
        case 'ArrowDown': event.preventDefault(); moveComboFocus(optionCombo, option, 1); return;
        case 'ArrowUp': event.preventDefault(); moveComboFocus(optionCombo, option, -1); return;
        case 'Home': event.preventDefault(); moveComboFocus(optionCombo, option, 'first'); return;
        case 'End': event.preventDefault(); moveComboFocus(optionCombo, option, 'last'); return;
        case 'Enter':
        case ' ':
        case 'Spacebar': event.preventDefault(); selectComboOption(option); return;
        case 'Escape': event.preventDefault(); closeCombo(optionCombo, { focusButton: true }); return;
        default: return;
      }
    }

    var optionQtyBtn = event.target.closest && event.target.closest('[data-rp-option-qty-step]');
    if (optionQtyBtn && event.key === 'Escape') {
      var qtyCombo = optionQtyBtn.closest('[data-rp-combo]');
      if (qtyCombo) {
        event.preventDefault();
        closeCombo(qtyCombo, { focusButton: true });
      }
      return;
    }

    var comboBtn = event.target.closest && event.target.closest('[data-rp-combo-button]');
    if (comboBtn) {
      var btnCombo = comboBtn.closest('[data-rp-combo]');
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openCombo(btnCombo);
      } else if (event.key === 'Escape' && comboIsOpen(btnCombo)) {
        event.preventDefault();
        closeCombo(btnCombo, { focusButton: true });
      }
    }
  });

  root.addEventListener('focusin', function (event) {
    if (!event.target.closest || !event.target.closest('[data-rp-combo]')) closeAllCombos(null);
  });

  root.addEventListener('change', function (event) {
    if (event.target.matches && event.target.matches('[data-rp-photo]')) {
      uploadAdvicePhoto(event.target);
      return;
    }

    var check = event.target.closest && event.target.closest('[data-rp-item-check]');
    if (check) {
      var row = check.closest('[data-rp-key]');
      if (!row) return;
      var key = row.dataset.rpKey;

      if (check.checked) {
        var line = JSON.parse(row.dataset.rpLine || '{}');
        if (!state.items.some(function (i) { return i.key === key; })) state.items.push(line);
      } else {
        state.items = state.items.filter(function (i) { return i.key !== key; });
        delete state.picks[key];
        delete state.fit[key];
      }

      var label = row.querySelector('[data-rp-check-label]');
      if (label) label.textContent = check.checked ? 'Selected' : 'Select';

      syncNextButtons();
      updateSticky();
      save();
      return;
    }

    var radio = event.target.closest && event.target.closest('[data-rp-order-radio]');
    if (radio) {
      try {
        selectOrder(JSON.parse(radio.dataset.rpOrder));
      } catch (err) {
        console.error('[rp] could not read order', err);
      }
      syncNextButtons();
    }
  });

  root.addEventListener('input', function (event) {
    var target = event.target;

    if (target.matches('[data-rp-email]') || target.matches('[data-rp-advice-email]')) {
      state.order.email = target.value.trim();
      hideIdentifyError();
      syncNextButtons();
      save();
      return;
    }

    if (target.matches('[data-rp-postcode]')) {
      state.order.postcode = target.value.trim();
      hideIdentifyError();
      syncNextButtons();
      save();
      return;
    }

    if (target.matches('[data-rp-order-number]')) {
      state.order.name = target.value.trim();
      syncNextButtons();
      save();
      return;
    }

    if (target.matches('[data-rp-note]')) {
      state.note = target.value;
      Core.syncValues(root.querySelectorAll('[data-rp-note]'), state.note);
      syncNextButtons();
      save();
    }
  });

  /* Normalise on blur rather than while typing, so the caret never jumps mid-entry. */
  root.addEventListener('blur', function (event) {
    var target = event.target;

    if (target.matches && target.matches('[data-rp-order-number]')) {
      var order = normaliseOrderNumber(target.value);
      target.value = order.value;
      state.order.name = order.value;
      setHint('[data-rp-order-hint]', order.hint);
      syncNextButtons();
      save();
    }

    if (target.matches && target.matches('[data-rp-postcode]')) {
      var post = normalisePostcode(target.value);
      target.value = post.value;
      state.order.postcode = post.value;
      setHint('[data-rp-postcode-hint]', post.hint);
      syncNextButtons();
      save();
    }
  }, true);

  function setHint(selector, message) {
    var el = root.querySelector(selector);
    if (!el) return;
    el.textContent = message || '';
    if (message) el.dataset.tone = 'warn';
    else delete el.dataset.tone;
  }

  function setSubmitBusy(busy) {
    var buttons = root.querySelectorAll('[data-rp-submit]');
    for (var i = 0; i < buttons.length; i++) buttons[i].toggleAttribute('aria-busy', busy);
  }

  var submissionInFlight = false;
  var form = document.getElementById('rp-contact-form');
  if (form) {
    form.addEventListener('submit', function (event) {
      // A Return is not repeatable. This can happen after browser Back restores a cached review
      // page, or when a rapid second activation lands after the first request resolved.
      if (state.submissionComplete) {
        event.preventDefault();
        setSubmitBusy(false);
        render('done', { history: 'replace' });
        return;
      }

      if (submissionInFlight) {
        event.preventDefault();
        return;
      }

      setSubmitBusy(true);

      /*
       * Exchanges and returns go to the API and create a real Return. Sizing advice has no Return
       * to create — it is a question for a human — so it still goes through the contact form, as
       * does everything when no API is configured.
       *
       * BOTH now cancel the native POST and submit in the background. The contact form used to be
       * left to navigate natively, and that is what made its confirmation screen unreachable: the
       * native POST lands on Shopify's own redirect back to the page, which drops any query
       * params, so a portal being previewed through `?view=returns` (the only way to reach it
       * until the merchant assigns the template to a real page) came back as the page's ORDINARY
       * content with no portal on it at all — reported as "it just takes me to the Delivery page".
       * Rendering 'done' ourselves, the way the API path already did, removes that dependency
       * entirely: the shopper never leaves the page, so there is no redirect to lose.
       */
      var viaApi = LIVE && state.path !== 'advice' && state.order.source === 'api';

      if (!viaApi) {
        submitRequest(form);
        event.preventDefault();
        submissionInFlight = true;

        submitViaContactForm(form)
          .then(function () {
            submissionInFlight = false;
            setSubmitBusy(false);
            // No Return object exists on this path, so there is no reference but our own `ref`.
            state.submissionComplete = true;
            save();
            render('done', { history: 'replace' });
          })
          .catch(function (err) {
            submissionInFlight = false;
            console.error('[rp] contact form submit failed', err && err.message);
            /*
             * Never fails shut. Falling back to the real native POST is exactly the behaviour this
             * path had before, so the worst case is the old one — the request still reaches the
             * team — rather than a shopper whose request silently went nowhere. `form.submit()`
             * does not re-fire this listener, so it cannot loop.
             */
            form.submit();
          });
        return;
      }

      event.preventDefault();
      submissionInFlight = true;

      submitViaApi()
        .then(function (result) {
          submissionInFlight = false;
          setSubmitBusy(false);
          state.returnId = result.returnId || null;
          state.returnName = result.returnName || null;
          state.returnMode = result.mode || null;
          state.submissionComplete = true;
          var surplus = groupUnits(splitReplacementUnits().surplus);
          var owed = differenceCents();
          state.pendingPaymentCents = owed;
          state.paymentError = '';

          /*
           * NOTHING OWED: unchanged from before this file existed. Surplus is checked rather than
           * `owed` because a free-priced surplus unit (price 0) still needs to physically ship even
           * though it adds nothing to the balance — same edge case the old code handled, kept as
           * is. The thank-you confirmation screen is still exactly right here: there is nothing to
           * pay, so there is nothing to redirect to.
           */
          if (owed <= 0) {
            function finishFree(paymentReady) {
              state.paymentReady = paymentReady;
              save();
              render('done', { history: 'replace' });
            }

            if (!surplus.length) return finishFree(true);

            return cartXhrRetrying('cart/add.js', {
              items: surplus.map(function (entry) {
                return {
                  id: entry.pick.variantId,
                  quantity: entry.quantity,
                  properties: returnLinkageProperties()
                };
              })
            })
              .then(function () { finishFree(true); })
              .catch(function (err) {
                console.error('[rp] surplus cart add failed', err && err.message);
                state.paymentError = 'Your return was submitted, but we could not prepare the payment. Please contact us before paying.';
                finishFree(false);
              });
          }

          /*
           * SOMETHING IS OWED: straight to checkout through the REAL CART, not a draft order.
           *
           * THIS IS WHY, AND IT IS A DELIBERATE REVERSAL OF THE 2026-08-27 DRAFT-ORDER DESIGN.
           *
           * Mix & Match is a Shopify FUNCTION discount, and Shopify evaluates function discounts
           * at cart/checkout time. `draftOrderCreate` is neither: it prices its lines straight
           * from the catalogue, so the discount never runs. Measured on the real store, same two
           * garments, same shopper:
           *
           *     ordinary cart   £48 + £68  ->  £38 + £56  =  £94.00  ("Mix and Match", -£22.00)
           *     draft order     £48 + £68  ->  £48 + £68  = £116.00  (no discount at all)
           *
           * So every returns-portal balance was being charged at full list price on goods the same
           * shopper would have got at multi-buy pricing in an ordinary cart. Reported by the client
           * as "it is still adding the original price", and confirmed against the live store.
           *
           * The alternative — replicating the discount rules server-side and attaching them to the
           * draft — was built and thrown away on purpose. It is a SECOND COPY of pricing that
           * drifts the first time a percentage is edited in the Functions Creator app, and the
           * rules are not uniform enough to guess at: the UK rules are flat `fixed` amounts, other
           * markets are percentages, Canada splits one category across two price bands, and
           * `round(list * 0.8)` gets the UK Polo wrong by £2. Going through the cart means Shopify
           * applies its own live rules and there is nothing to keep in step.
           *
           * LINKAGE IS NOT LOST, which was the entire reason the draft order existed. The order/
           * Return attribution rides along as hidden line-item properties instead — verified to
           * survive the cart and to leave the discount intact. `_`-prefixed properties are hidden
           * from the customer-facing cart and checkout, the same mechanism `_orserio_gift` already
           * uses in this theme.
           */
          state.paymentReady = false;
          save();

          var toCheckout = function () {
            window.location.assign((CONFIG.rootUrl || '/') + 'checkout');
          };

          // Nothing physical to buy (the balance is Instant Dispatch extras only, already added
          // to the cart when the shopper picked them) — go straight through.
          if (!surplus.length) { toCheckout(); return; }

          return cartXhrRetrying('cart/add.js', {
            items: surplus.map(function (entry) {
              return {
                id: entry.pick.variantId,
                quantity: entry.quantity,
                properties: returnLinkageProperties()
              };
            })
          })
            .then(toCheckout)
            .catch(function (err) {
              console.error('[rp] surplus cart add failed', err && err.message);
              /*
               * Genuinely nowhere left to send them automatically. The Return already exists in
               * Shopify — showing the confirmation screen here, as the final fallback, keeps
               * that from being lost, with the reason explained rather than a silent dead end.
               */
              state.paymentError = 'Your return was submitted, but we could not prepare the payment. Please contact us before paying.';
              state.paymentReady = false;
              save();
              render('done', { history: 'replace' });
            });
        })
        .catch(function (err) {
          submissionInFlight = false;
          console.error('[rp] submit failed', err && (err.body || err.message));
          setSubmitBusy(false);

          // The Return may already exist even if a later cart, redirect or render operation failed.
          // Never invite a duplicate submission once Shopify has accepted the first one.
          if (state.submissionComplete) {
            save();
            render('done', { history: 'replace' });
            return;
          }

          // An expired token is recoverable and specifically worth explaining, because "try
          // again" doesn't help — they need to re-identify.
          var expired = err && err.body && err.body.error === 'expired';
          showSubmitError(
            expired
              ? "Your session timed out. Please start again so we can re-check your order."
              : "We couldn't submit that just now. Please try again, or contact us and we'll sort it out."
          );
        });
    });
  }

  function showSubmitError(message) {
    var el = stepEl(state.step, state.path);
    if (!el) return;
    var box = el.querySelector('[data-rp-submit-error]');
    if (!box) {
      box = document.createElement('div');
      box.className = 'rp__notice rp__notice--warn';
      box.setAttribute('data-rp-submit-error', '');
      var actions = el.querySelector('.rp__actions');
      if (actions) el.insertBefore(box, actions);
      else el.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Checkout Back can restore this page from the browser's back-forward cache without rerunning
  // boot. Clear its frozen busy style and show the already-accepted confirmation, never Review.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    setSubmitBusy(false);
    if (state.submissionComplete) render('done', { history: 'replace', focus: false });
  });

  window.addEventListener('popstate', function (event) {
    var step = (event.state && event.state.rpStep) || stepFromHash() || 'entry';
    render(step, { history: 'replace' });
  });

  function stepFromHash() {
    var hash = (location.hash || '').replace(/^#/, '');
    return hash && root.querySelector('[data-rp-step="' + hash + '"]') ? hash : '';
  }

  /*
   * A PATH name in the hash (`#advice`, `#exchange`, `#return`) opens that journey directly rather
   * than naming a step. The footer's "Sizing Advice" link is why this exists: it pointed at
   * `#identify`, but `guard()` sends every step back to `entry` while `state.path` is unset, so
   * the link promised the sizing-advice screen and delivered the generic entry screen instead.
   *
   * Deliberately consulted only at boot, and only when no journey is already underway: a shopper
   * part-way through an exchange who clicks this keeps their selections instead of having them
   * silently discarded. Path names cannot collide with step names — no `[data-rp-step]` is called
   * `exchange`, `return` or `advice` — so `stepFromHash()` still wins for a real step hash.
   */
  function pathFromHash() {
    var hash = (location.hash || '').replace(/^#/, '');
    return Object.prototype.hasOwnProperty.call(STEPS, hash) ? hash : '';
  }

  /* ------------------------------------------------------------------ boot */

  load();

  /*
   * Prefill the email when Shopify happens to know who this is. Purely a convenience — it is not
   * authentication, and the postcode check is what actually proves ownership.
   */
  var emailField = root.querySelector('[data-rp-email]');
  if (emailField) {
    if (state.order.email) emailField.value = state.order.email;
    else if (emailField.value) state.order.email = emailField.value.trim();
  }

  refreshPicker();

  /*
   * Photo upload is revealed only when the returns API is configured, because that is where the
   * upload endpoint lives. Without it the shopper keeps the original "we'll show you how to send
   * them" line and the thank-you screen's mailto route, rather than being offered a control that
   * has nowhere to POST to.
   */
  if (API_URL) {
    var photoField = root.querySelector('[data-rp-photo-field]');
    var photoFallback = root.querySelector('[data-rp-photo-fallback]');
    if (photoField) photoField.hidden = false;
    if (photoFallback) photoFallback.hidden = true;
    // A restored session may already carry an attached photo — say so rather than looking empty.
    if (state.photoUrl && state.photoName) setPhotoStatus('Photo attached: ' + state.photoName);
    syncPhotoRemoveButton();
  }

  /* `[data-rp-posted]` only exists when Shopify actually accepted the POST, so it — not an
     optimistic client-side flag — is what tells us the request really went through. */
  var posted = root.querySelector('[data-rp-posted]');
  if (posted) {
    render('done', { history: 'replace' });
    clearState();
  } else {
    var hashPath = pathFromHash();
    if (hashPath && !state.path && !state.submissionComplete) {
      state.path = hashPath;
      if (!state.ref) state.ref = makeRef();
    }
    var bootStep = stepFromHash();
    if (!bootStep && hashPath) bootStep = stepsFor(hashPath)[0] || '';
    render(bootStep || state.step || 'entry', {
      history: 'replace',
      historyDepth: history.state && history.state.rpPortal ? history.state.rpDepth : 0
    });
  }

  /*
   * Test hook for the browser verification harness. The fit engine has enough branching (two
   * catalogue dead ends, a linen offset, two sizing systems) that it needs a table test.
   *
   * Gated on the THEME ROLE, not on the URL: `role` is 'main' only on the published theme, so
   * this exists on dev/unpublished themes and in the editor and can never appear on live.
   * Checking `preview_theme_id` in the query string does not work — Shopify consumes that
   * param, sets a cookie and redirects, so it is gone by the time this runs.
   */
  var themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
  if (window.Shopify && (window.Shopify.designMode || (themeRole && themeRole !== 'main'))) {
    window.__rpTest = {
      recommend: recommend,
      matchFitCards: matchFitCards,
      sizeToken: sizeToken,
      products: PRODUCTS,
      state: function () { return state; },
      config: CONFIG
    };
  }
})();
